import React from 'react';
import { PATH_NODES, WORLD_MAP_NODES, DECOR_PLACEMENTS, LEVEL_DECOR_PLACEMENTS } from '../data/gameData';

interface WorldMapArtworkProps {
  unlockedLevels: number[];
  selectedLevel: number;
  onSelect: (id: number) => void;
  isAdmin?: boolean;
}

const MAP_SANCTUM_NODES = [
  { id: 0, x: 250, y: 275, name: 'Taddart', number: '1', color: '#f59e0b', ringColor: '#fbbf24', textY: 16 },
  { id: 1, x: 145, y: 235, name: 'Azemmur', number: '2', color: '#84cc16', ringColor: '#a3e635', textY: 16 },
  { id: 2, x: 250, y: 195, name: 'Djurdjura', number: '3', color: '#38bdf8', ringColor: '#7dd3fc', textY: 16 },
  { id: 3, x: 190, y: 145, name: 'Iɣil', number: '4', color: '#c084fc', ringColor: '#d8b4fe', textY: -16 },
  { id: 4, x: 250, y: 90, name: 'Ijduden', number: 'ⵣ', color: '#10b981', ringColor: '#34d399', textY: -18, isSanctuary: true },
  { id: 5, x: 250, y: 35, name: 'Ayur', number: '🌙', color: '#fbbf24', ringColor: '#fef08a', textY: -18, isMoon: true }
];

const calculateWorldMapViewBox = (w?: number, h?: number): string => {
  let width = w;
  let height = h;
  if (!width || !height || width <= 0 || height <= 0) {
    if (typeof window !== 'undefined') {
      width = window.innerWidth;
      // Top screen container is 55% of window height minus top bar (~42px)
      height = Math.max(window.innerHeight * 0.55 - 42, 180);
    } else {
      width = 480;
      height = 320;
    }
  }

  const aspect = width / height;
  if (aspect >= 1.5) {
    // Desktop / widescreen: fixed vertical height 320, expand horizontally, centered on x=240
    const vbWidth = Math.round(320 * aspect);
    const minX = Math.round(240 - vbWidth / 2);
    return `${minX} 0 ${vbWidth} 320`;
  } else {
    // Portrait / narrow screens: fixed width 480, expand vertically, centered on y=160
    const vbHeight = Math.round(480 / aspect);
    const minY = Math.round(160 - vbHeight / 2);
    return `0 ${minY} 480 ${vbHeight}`;
  }
};

export const WorldMapArtwork: React.FC<WorldMapArtworkProps> = React.memo(({
  unlockedLevels,
  selectedLevel,
  onSelect,
  isAdmin = false
}) => {
  const isLevel5Restored = unlockedLevels.includes(5) || isAdmin;
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  // Synchronous initialization prevents the 1-frame aspect-ratio flash on mount
  const [viewBox, setViewBox] = React.useState<string>(() => calculateWorldMapViewBox());

  // useLayoutEffect guarantees any sub-pixel container adjustments happen before the browser paints
  React.useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let rafId: number | null = null;
    let lastW = 0;
    let lastH = 0;

    const updateViewBox = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        if (!el) return;
        const w = el.clientWidth || window.innerWidth;
        const h = el.clientHeight || Math.max(window.innerHeight * 0.55 - 42, 180);
        if (h <= 0 || w <= 0) return;
        if (Math.abs(w - lastW) < 2 && Math.abs(h - lastH) < 2) return;
        lastW = w;
        lastH = h;
        const nextVb = calculateWorldMapViewBox(w, h);
        setViewBox((prev) => (prev === nextVb ? prev : nextVb));
      });
    };

    updateViewBox();

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(updateViewBox);
      ro.observe(el);
    }
    window.addEventListener('resize', updateViewBox, { passive: true });

    return () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', updateViewBox);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="w-full h-full relative overflow-hidden bg-[#081528]"
      style={{ contain: 'paint', willChange: 'transform' }}
    >
      <svg
        className="w-full h-full select-none block"
        viewBox={viewBox}
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ imageRendering: 'auto' }}
      >
        <defs>
          <style>{`
            @keyframes auroraFlow1 {
              0%, 100% { opacity: 0.72; }
              50% { opacity: 0.95; }
            }
            @keyframes auroraFlow2 {
              0%, 100% { opacity: 0.60; }
              50% { opacity: 0.88; }
            }
            @keyframes questDashTrail {
              to { stroke-dashoffset: -20; }
            }
            .aurora-ribbon-1 {
              animation: auroraFlow1 4.5s ease-in-out infinite;
              will-change: opacity;
            }
            .aurora-ribbon-2 {
              animation: auroraFlow2 6s ease-in-out infinite;
              will-change: opacity;
            }
            .quest-path-active {
              animation: questDashTrail 2.5s linear infinite;
              will-change: stroke-dashoffset;
            }
          `}</style>
          <linearGradient id="worldSky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#08142c" />
            <stop offset="50%" stopColor="#050c1e" />
            <stop offset="100%" stopColor="#020617" />
          </linearGradient>
          <radialGradient id="greenSkyAura" cx="50%" cy="15%" r="65%">
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.35" />
            <stop offset="35%" stopColor="#34d399" stopOpacity="0.25" />
            <stop offset="70%" stopColor="#059669" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#020617" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="auroraRibbon1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#059669" stopOpacity="0" />
            <stop offset="15%" stopColor="#10b981" stopOpacity="0.4" />
            <stop offset="35%" stopColor="#34d399" stopOpacity="0.65" />
            <stop offset="50%" stopColor="#6ee7b7" stopOpacity="0.8" />
            <stop offset="65%" stopColor="#34d399" stopOpacity="0.65" />
            <stop offset="85%" stopColor="#10b981" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#059669" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="auroraRibbon2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#047857" stopOpacity="0" />
            <stop offset="20%" stopColor="#10b981" stopOpacity="0.4" />
            <stop offset="50%" stopColor="#38bdf8" stopOpacity="0.55" />
            <stop offset="80%" stopColor="#6ee7b7" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#065f46" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="worldPathGlow" x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor="#f59e0b" />
            <stop offset="50%" stopColor="#38bdf8" />
            <stop offset="100%" stopColor="#34d399" />
          </linearGradient>
        </defs>

        {/* 1. Deep Night Sky with Celestial Glow across wide horizon */}
        <rect x="-600" y="-120" width="1800" height="540" fill="url(#worldSky)" />
        <rect x="-600" y="-120" width="1800" height="380" fill="url(#greenSkyAura)" />

        {/* Luminous Celestial Aurora Ribbons flowing across the panorama */}
        <g>
          <path
            d="M -600 48 Q -200 15 100 40 T 400 25 T 700 45 T 1200 35 L 1200 95 Q 700 70 400 80 T 100 70 T -200 85 T -600 80 Z"
            fill="url(#auroraRibbon1)"
            opacity="0.85"
            className="aurora-ribbon-1"
          />
          <path
            d="M -600 75 Q -300 40 50 65 T 300 55 T 600 75 T 1200 60 L 1200 115 Q 600 95 300 100 T 50 95 T -300 115 T -600 105 Z"
            fill="url(#auroraRibbon2)"
            opacity="0.75"
            className="aurora-ribbon-2"
          />
          <ellipse cx="250" cy="42" rx="220" ry="26" fill="#38bdf8" opacity="0.22" />
        </g>

        {/* Twinkling Stars & Cross Sparkles (adopting Level 2 Djurdjura night sky design) */}
        <circle cx="-540" cy="30" r="1.2" fill="#fff" opacity="0.8" />
        <circle cx="-380" cy="55" r="1.1" fill="#bae6fd" opacity="0.85" />
        <circle cx="-190" cy="25" r="1.4" fill="#ffffff" opacity="0.9" />
        <circle cx="-80" cy="45" r="1.1" fill="#e2e8f0" opacity="0.85" />
        <circle cx="65" cy="28" r="1" fill="#fff" opacity="0.8" />
        <circle cx="130" cy="52" r="1.3" fill="#bae6fd" opacity="0.9" />
        <circle cx="210" cy="20" r="1.5" fill="#ffffff" opacity="0.95" />
        <circle cx="285" cy="38" r="1.2" fill="#bae6fd" opacity="0.85" />
        <circle cx="370" cy="18" r="1.4" fill="#ffffff" opacity="0.9" />
        <circle cx="430" cy="32" r="1.2" fill="#e2e8f0" opacity="0.8" />
        <circle cx="560" cy="22" r="1.3" fill="#fff" opacity="0.85" />
        <circle cx="720" cy="48" r="1.1" fill="#bae6fd" opacity="0.9" />
        <circle cx="890" cy="25" r="1.5" fill="#ffffff" opacity="0.9" />
        <circle cx="1060" cy="40" r="1.1" fill="#e2e8f0" opacity="0.8" />

        {/* Guiding Cross Sparkle Stars */}
        <g transform="translate(210, 20)">
          <line x1="-4" y1="0" x2="4" y2="0" stroke="#ffffff" strokeWidth="0.6" opacity="0.9" />
          <line x1="0" y1="-4" x2="0" y2="4" stroke="#ffffff" strokeWidth="0.6" opacity="0.9" />
        </g>
        <g transform="translate(370, 18)">
          <line x1="-3.5" y1="0" x2="3.5" y2="0" stroke="#ffffff" strokeWidth="0.5" opacity="0.85" />
          <line x1="0" y1="-3.5" x2="0" y2="3.5" stroke="#ffffff" strokeWidth="0.5" opacity="0.85" />
        </g>
        <g transform="translate(890, 25)">
          <line x1="-4" y1="0" x2="4" y2="0" stroke="#ffffff" strokeWidth="0.6" opacity="0.85" />
          <line x1="0" y1="-4" x2="0" y2="4" stroke="#ffffff" strokeWidth="0.6" opacity="0.85" />
        </g>

        {/* ========================================================================= */}
        {/* 2. LAYER 1: Distant Majestic Massifs (High altitude peaks, pure flat      */}
        {/* facets, strict collinear snow caps with left lit #f8fafc & shadow #94a3b8)*/}
        {/* ========================================================================= */}
        <g id="distant_massifs">
          {/* Peak 1 (Left Peak - Thaletat / Main du Juif - Summit at 70, 115) - Base y=280 */}
          <polygon points="-133,280 70,115 70,280" fill="#1e293b" />
          <polygon points="70,115 222,280 70,280" fill="#090d1f" />
          {/* Calotte glaciaire strictement superposée et collinéaire */}
          <polygon points="26.9,150 70,115 70,150" fill="#f8fafc" />
          <polygon points="70,115 102.3,150 70,150" fill="#94a3b8" />

          {/* Peak 2 (Center Sacred Peak - Ijduden / Le Sanctuaire Céleste - Summit at 250, 55) - Base y=280 */}
          {/* Teintes indigo et améthyste majestueuses, sans calotte de neige */}
          <polygon points="108,280 250,55 250,280" fill="#312e81" />
          <polygon points="250,55 392,280 250,280" fill="#1e1b4b" />

          {/* Peak 3 (Right Peak - Ras Timedouine - Summit at 410, 95) - Base y=280 */}
          <polygon points="262,280 410,95 410,280" fill="#1e293b" />
          <polygon points="410,95 558,280 410,280" fill="#090d1f" />
          {/* Calotte glaciaire strictement superposée et collinéaire */}
          <polygon points="378,135 410,95 410,135" fill="#f8fafc" />
          <polygon points="410,95 442,135 410,135" fill="#94a3b8" />
        </g>

        {/* ========================================================================= */}
        {/* 3. LAYER 2: Midground Mountain Ridges (The Djurdjura Massif Chain)        */}
        {/* Pure vector facet lighting, strict snow cap alignment, zero artifacts    */}
        {/* ========================================================================= */}
        <g id="midground_massifs">
          {/* Ridge Peak West-Center (90, 160) - Base y=280 */}
          <polygon points="-90,280 90,160 90,280" fill="#1e293b" />
          <polygon points="90,160 215,280 90,280" fill="#090d1f" />
          <polygon points="51.9,185 90,160 90,185" fill="#e2e8f0" />
          <polygon points="90,160 116.2,185 90,185" fill="#94a3b8" />

          {/* DJURDJURA MAIN SUMMIT (Step 3 / Node 2 - Sommet à 280, 142) - Base y=280 */}
          {/* Flanc rocheux gauche éclairé (#1e293b) */}
          <polygon points="173,280 280,142 280,280" fill="#1e293b" />
          {/* Flanc rocheux droit ombré (#090d1f) */}
          <polygon points="280,142 392,280 280,280" fill="#090d1f" />

          {/* Calotte glaciaire du Djurdjura - Exacte superposition géométrique aux arêtes */}
          <g id="djurdjura_snow_cap">
            {/* Neige immaculée éclairée (#f8fafc) */}
            <polygon points="256.8,172 280,142 280,172" fill="#f8fafc" />
            {/* Neige bleutée dans l'ombre (#94a3b8) */}
            <polygon points="280,142 304.4,172 280,172" fill="#94a3b8" />
          </g>

          {/* Ridge Peak East 1 (470, 160) - Base y=280 */}
          <polygon points="345,280 470,160 470,280" fill="#1e293b" />
          <polygon points="470,160 595,280 470,280" fill="#090d1f" />
          <polygon points="443.8,185 470,160 470,185" fill="#e2e8f0" />
          <polygon points="470,160 496.2,185 470,185" fill="#94a3b8" />
        </g>

        {/* ========================================================================= */}
        {/* 4. LAYER 3: Contreforts rocheux d'altitude (Premier plan rocheux)         */}
        {/* ========================================================================= */}
        <g id="lower_foothills">
          {/* Contrefort 1 (45, 205) - Base y=280 */}
          <polygon points="-15,280 45,205 45,280" fill="#0f172a" />
          <polygon points="45,205 105,280 45,280" fill="#020617" />
          <polygon points="33,220 45,205 45,220" fill="#e2e8f0" />
          <polygon points="45,205 57,220 45,220" fill="#94a3b8" />

          {/* Contrefort 2 (180, 200) - Base y=280 */}
          <polygon points="120,280 180,200 180,280" fill="#0f172a" />
          <polygon points="180,200 240,280 180,280" fill="#020617" />
          <polygon points="168.75,215 180,200 180,215" fill="#e2e8f0" />
          <polygon points="180,200 191.25,215 180,215" fill="#94a3b8" />

          {/* Contrefort 3 (315, 205) - Base y=280 */}
          <polygon points="255,280 315,205 315,280" fill="#0f172a" />
          <polygon points="315,205 375,280 315,280" fill="#020617" />
          <polygon points="303,220 315,205 315,220" fill="#e2e8f0" />
          <polygon points="315,205 327,220 315,220" fill="#94a3b8" />

          {/* Contrefort 4 (420, 208) - Base y=280 */}
          <polygon points="365,280 420,208 420,280" fill="#0f172a" />
          <polygon points="420,208 475,280 420,280" fill="#020617" />
          <polygon points="409.3,222 420,208 420,222" fill="#e2e8f0" />
          <polygon points="420,208 430.7,222 420,222" fill="#94a3b8" />
        </g>

        {/* ========================================================================= */}
        {/* 5. LAYER 4: Collines verdoyantes & Forêt de Kabylie (Azemmur & Taddart)   */}
        {/* ========================================================================= */}
        <g id="valley_hills">
          {/* Étage intermédiaire : Forêt de pins & d'oliviers (Azemmur) - vague en S inversée panoramique (sommet à 250, descente fluide, 0 trou) */}
          <path
            d="M -600 258 L -200 258 C 0 258, 120 242, 250 242 C 380 242, 500 258, 700 258 L 1200 258 L 1200 400 L -600 400 Z"
            fill="#065f46"
          />
          {/* Collines au premier plan : Vert émeraude nocturne (Taddart) - rigoureusement parallèle (+24px) */}
          <path
            d="M -600 282 L -200 282 C 0 282, 120 266, 250 266 C 380 266, 500 282, 700 282 L 1200 282 L 1200 400 L -600 400 Z"
            fill="#022c22"
          />
          {/* Liseré lumineux délicat soulignant la crête du premier plan - strictement superposé */}
          <path
            d="M -600 282 L -200 282 C 0 282, 120 266, 250 266 C 380 266, 500 282, 700 282 L 1200 282"
            fill="none"
            stroke="#047857"
            strokeWidth="1.5"
            opacity="0.6"
          />
        </g>

        {/* 6. Glowing Quest Path Trail */}
        <path
          d="M 250 275 C 200 275 165 260 145 235 C 125 210 200 200 250 195 C 290 190 240 150 190 145 C 145 140 180 95 250 90"
          fill="none"
          stroke="url(#worldPathGlow)"
          strokeWidth="3.5"
          strokeDasharray="6 4"
          className="quest-path-active"
        />
        {isLevel5Restored ? (
          <path
            d="M 250 90 L 250 35"
            fill="none"
            stroke="#fcd34d"
            strokeWidth="3"
            strokeDasharray="6 4"
            className="quest-path-active"
          />
        ) : (
          <path
            d="M 250 90 L 250 35"
            fill="none"
            stroke="#475569"
            strokeWidth="2"
            strokeDasharray="6 4"
          />
        )}

      {/* 6. Interactive Sanctuary Nodes */}
      {MAP_SANCTUM_NODES.map((node) => {
        const isUnlocked = unlockedLevels.includes(node.id) || isAdmin;
        const isSelected = selectedLevel === node.id;

        if (node.isMoon) {
          return (
            <g
              key={node.id}
              transform={`translate(${node.x}, ${node.y})`}
              onClick={() => {
                if (isUnlocked) onSelect(node.id);
              }}
              className={isUnlocked ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'}
            >
              {isUnlocked && (
                <circle
                  cx="0"
                  cy="0"
                  r={isSelected ? 18 : 14}
                  fill="#fbbf24"
                  opacity={isSelected ? 0.55 : 0.25}
                  className="animate-pulse"
                />
              )}
              {isSelected && (
                <circle cx="0" cy="0" r="14" fill="none" stroke="#ffffff" strokeWidth="1.5" strokeDasharray="3 2" />
              )}
              <circle
                cx="0"
                cy="0"
                r="10"
                fill={isUnlocked ? (isSelected ? "#f59e0b" : "#fbbf24") : "#1e293b"}
                stroke={isSelected ? "#ffffff" : (isUnlocked ? "#fef08a" : "#475569")}
                strokeWidth={isSelected ? 2 : 1.5}
                className={isUnlocked ? "animate-pulse" : ""}
              />
              <text x="0" y="4" fill={isUnlocked ? "#1e293b" : "#94a3b8"} fontSize="10" fontWeight="black" textAnchor="middle">{isUnlocked ? "🌙" : "🔒"}</text>
              <g transform={`translate(0, ${node.textY})`}>
                <rect x="-30" y="-12" width="60" height="16" rx="4" fill="#1e293b" fillOpacity="0.8" />
                <text x="0" y="0" fill={isUnlocked ? (isSelected ? "#fef08a" : "#fbbf24") : "#64748b"} fontSize="11" fontWeight="bold" textAnchor="middle">Ayur</text>
              </g>
            </g>
          );
        }
        if (node.isSanctuary) {
          // Final Goal: Ijduden
          return (
            <g
              key={node.id}
              transform={`translate(${node.x}, ${node.y})`}
              onClick={() => {
                if (isUnlocked) onSelect(node.id);
              }}
              className={isUnlocked ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'}
            >
              {/* Selected / Unlocked Aura */}
              {isUnlocked && (
                <circle
                  cx="0"
                  cy="0"
                  r={isSelected ? 22 : 16}
                  fill="#34d399"
                  opacity={isSelected ? 0.45 : 0.22}
                  className="animate-pulse"
                />
              )}

              {/* Selection Ring */}
              {isSelected && (
                <circle cx="0" cy="0" r="16" fill="none" stroke="#ffffff" strokeWidth="1.5" strokeDasharray="3 2" />
              )}

              {/* Main Sanctuary Button */}
              <circle
                cx="0"
                cy="0"
                r="11"
                fill={isUnlocked ? (isSelected ? "#10b981" : "#059669") : "#1e293b"}
                stroke={isSelected ? "#ffffff" : isUnlocked ? "#34d399" : "#475569"}
                strokeWidth={isSelected ? 2 : 1.5}
              />
              
              {/* Yaz ⵣ Symbol */}
              <text x="0" y="4" fill={isUnlocked ? "#ffffff" : "#94a3b8"} fontSize="11" fontWeight="black" textAnchor="middle">
                {isUnlocked ? "ⵣ" : "🔒"}
              </text>

              {/* High Contrast Name Badge */}
              <g transform={`translate(0, ${node.textY})`}>
                <rect
                  x={isLevel5Restored ? -45 : -24}
                  y="-7"
                  width={isLevel5Restored ? 90 : 48}
                  height="14"
                  rx="4"
                  fill={isSelected ? "#064e3b" : "#022c22"}
                  stroke={isSelected ? "#34d399" : isUnlocked ? "#059669" : "#475569"}
                  strokeWidth="1.2"
                />
                <text
                  x="0"
                  y="3"
                  fill={isSelected ? "#ffffff" : isUnlocked ? "#ecfdf5" : "#94a3b8"}
                  fontSize={isLevel5Restored ? "7" : "8"}
                  fontWeight="900"
                  textAnchor="middle"
                  letterSpacing="0.5"
                >
                  {isLevel5Restored ? "Lǧemɛa (Purifié)" : node.name}
                </text>
              </g>
            </g>
          );
        }

        // Standard Nodes (1 to 4: Taddart, Azemmur, Djurdjura, Iɣil)
        return (
          <g
            key={node.id}
            transform={`translate(${node.x}, ${node.y})`}
            onClick={() => {
              if (isUnlocked) onSelect(node.id);
            }}
            className={isUnlocked ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'}
          >
            {/* Active Selection Glow */}
            {isSelected && (
              <circle
                cx="0"
                cy="0"
                r="16"
                fill={node.color}
                opacity="0.4"
                className="animate-pulse"
              />
            )}

            {/* Selection Dash Ring */}
            {isSelected && (
              <circle cx="0" cy="0" r="13" fill="none" stroke="#ffffff" strokeWidth="1.5" strokeDasharray="3 2" />
            )}

            {/* Unlocked pulsing ambient glow */}
            {isUnlocked && !isSelected && (
              <circle cx="0" cy="0" r="12" fill={node.color} opacity="0.2" />
            )}

            {/* Node Circle */}
            <circle
              cx="0"
              cy="0"
              r="8.5"
              fill={isUnlocked ? node.color : "#1e293b"}
              stroke={isSelected ? "#ffffff" : isUnlocked ? node.ringColor : "#475569"}
              strokeWidth={isSelected ? 2 : 1}
            />

            {/* Number or Lock */}
            <text
              x="0"
              y="3.5"
              fill={isUnlocked ? (node.id === 2 ? "#0c4a6e" : node.id === 3 ? "#3b0764" : "#1c1917") : "#94a3b8"}
              fontSize={isUnlocked ? "8.5" : "7"}
              fontWeight="bold"
              textAnchor="middle"
            >
              {isUnlocked ? node.number : "🔒"}
            </text>

            {/* Region Name Badge / Label */}
            {node.textY < 0 ? (
              // Above Node (Iɣil)
              <g transform={`translate(0, ${node.textY})`}>
                <rect
                  x="-18"
                  y="-7"
                  width="36"
                  height="13"
                  rx="3.5"
                  fill={isSelected ? "#3b0764" : "#1e1b4b"}
                  stroke={isSelected ? "#c084fc" : isUnlocked ? "#7c3aed" : "#475569"}
                  strokeWidth="1"
                />
                <text
                  x="0"
                  y="2.5"
                  fill={isSelected ? "#ffffff" : isUnlocked ? "#f3e8ff" : "#94a3b8"}
                  fontSize="7.5"
                  fontWeight="bold"
                  textAnchor="middle"
                >
                  {node.name}
                </text>
              </g>
            ) : (
              // Below Node (Taddart, Azemmur, Djurdjura)
              <g transform={`translate(0, ${node.textY})`}>
                <rect
                  x="-24"
                  y="-6"
                  width="48"
                  height="13"
                  rx="3.5"
                  fill={isSelected ? "#0f172a" : "#0f172a/90"}
                  stroke={isSelected ? node.color : isUnlocked ? node.ringColor : "#475569"}
                  strokeWidth="1"
                />
                <text
                  x="0"
                  y="3.5"
                  fill={isSelected ? "#ffffff" : isUnlocked ? (node.id === 0 ? "#fef3c7" : node.id === 1 ? "#ecfccb" : "#e0f2fe") : "#94a3b8"}
                  fontSize="7.5"
                  fontWeight="bold"
                  textAnchor="middle"
                >
                  {node.name}
                </text>
              </g>
            )}
          </g>
        );
      })}
      </svg>
    </div>
  );
});

interface TexturedLevelArtworkProps {
  levelId: number;
  baseColor: string;
  isRestored?: boolean;
  isNight?: boolean;
  className?: string;
  viewportWidth?: number;
}

export const TexturedLevelArtwork: React.FC<TexturedLevelArtworkProps> = React.memo(({
  levelId,
  baseColor,
  isRestored = false,
  isNight = false,
  className = "",
  viewportWidth = 480
}) => {
  const activeWidth = Math.max(360, viewportWidth);

  // Calcul d'écartement proportionnel et symétrique :
  // Largeur de référence mobile = 480px (ratio = 1.0, positions d'origine).
  // Lorsque l'écran s'élargit (tablette, desktop, grand écran), les décors
  // à gauche et à droite s'éloignent du chemin de manière proportionnelle.
  const widthRatio = Math.max(1, activeWidth / 480);
  const spreadFactor = Math.min(3.2, 1 + (widthRatio - 1) * 0.88);

  const getAdjustedX = React.useCallback((originalX: number) => {
    // 200 est l'axe central du chemin.
    return Math.round(200 + (originalX - 200) * spreadFactor);
  }, [spreadFactor]);

  const groundPatternId = `groundPattern_${levelId}_${isRestored ? 'restored' : 'base'}_${isNight ? 'night' : 'day'}`;
  const effectiveBaseColor = (levelId === 4 && isRestored)
    ? (isNight ? '#064e3b' : (baseColor || '#16a34a'))
    : (isNight ? '#064e3b' : baseColor);

  return (
    <svg
      className={`absolute top-0 left-0 w-full h-full pointer-events-none z-0 overflow-visible ${className}`}
      viewBox="0 0 400 1600"
      preserveAspectRatio="none"
      style={{ imageRendering: 'pixelated', overflow: 'visible' }}
    >
    <defs>
      <g id="flower_cluster">
        <g transform="translate(-10, 0)">
          <circle cx="-2" cy="-2" r="3" fill="#f472b6" />
          <circle cx="2" cy="-2" r="3" fill="#f472b6" />
          <circle cx="-2" cy="2" r="3" fill="#f472b6" />
          <circle cx="2" cy="2" r="3" fill="#f472b6" />
          <circle cx="0" cy="0" r="2" fill="#fde047" />
        </g>
        <g transform="translate(10, 5) scale(0.8)">
          <circle cx="-2" cy="-2" r="3" fill="#ffffff" />
          <circle cx="2" cy="-2" r="3" fill="#ffffff" />
          <circle cx="-2" cy="2" r="3" fill="#ffffff" />
          <circle cx="2" cy="2" r="3" fill="#ffffff" />
          <circle cx="0" cy="0" r="2" fill="#fde047" />
        </g>
        <g transform="translate(0, -8) scale(0.9)">
          <circle cx="-2" cy="-2" r="3" fill="#60a5fa" />
          <circle cx="2" cy="-2" r="3" fill="#60a5fa" />
          <circle cx="-2" cy="2" r="3" fill="#60a5fa" />
          <circle cx="2" cy="2" r="3" fill="#60a5fa" />
          <circle cx="0" cy="0" r="2" fill="#fde047" />
        </g>
      </g>
      {levelId === 2 ? (
        <pattern
          id={groundPatternId}
          width="40"
          height="40"
          patternUnits="userSpaceOnUse"
        >
          {/* Manteau neigeux nocturne éclairé par la lune sur le Djurdjura */}
          <rect width="40" height="40" fill="#475569" />
          <rect x="0" y="0" width="20" height="20" fill="#4f5e75" />
          <rect x="20" y="20" width="20" height="20" fill="#4f5e75" />
          {/* Névés et ondulations de neige bleutée sous la nuit */}
          <rect x="2" y="14" width="16" height="5" rx="2" fill="#384556" opacity="0.4" />
          <rect x="22" y="32" width="14" height="4" rx="2" fill="#384556" opacity="0.4" />
          <rect x="4" y="2" width="12" height="4" rx="2" fill="#64748b" opacity="0.45" />
          <rect x="24" y="18" width="12" height="4" rx="2" fill="#64748b" opacity="0.45" />
          {/* Cristaux de givre et étincelles de neige au clair de lune */}
          <circle cx="8" cy="8" r="0.9" fill="#ffffff" opacity="0.85" />
          <circle cx="28" cy="6" r="1.1" fill="#f0f9ff" opacity="0.75" />
          <circle cx="15" cy="28" r="0.8" fill="#e0f2fe" opacity="0.8" />
          <circle cx="34" cy="26" r="1.2" fill="#ffffff" opacity="0.9" />
          <circle cx="20" cy="38" r="0.7" fill="#ffffff" opacity="0.7" />
        </pattern>
      ) : (
        <pattern
          id={groundPatternId}
          width="40"
          height="40"
          patternUnits="userSpaceOnUse"
        >
          <rect width="40" height="40" fill={effectiveBaseColor} />
          <rect x="0" y="0" width="20" height="20" fill="#000" opacity={isNight ? 0.2 : 0.05} />
          <rect x="20" y="20" width="20" height="20" fill="#000" opacity={isNight ? 0.2 : 0.05} />
          <rect x="5" y="5" width="4" height="4" fill="#000" opacity={isNight ? 0.25 : 0.1} />
          {isNight && (
            <>
              <circle cx="12" cy="12" r="1" fill="#34d399" opacity="0.22" />
              <circle cx="32" cy="32" r="1.2" fill="#6ee7b7" opacity="0.18" />
            </>
          )}
        </pattern>
      )}

      {/* Motifs de texture spécifiques pour le Sanctuaire Level 4 */}
      <pattern id="groundPattern_4_corrupted" width="40" height="40" patternUnits="userSpaceOnUse">
        <rect width="40" height="40" fill="#3f3f46" />
        <rect x="0" y="0" width="20" height="20" fill="#27272a" opacity="0.4" />
        <rect x="20" y="20" width="20" height="20" fill="#27272a" opacity="0.4" />
        <rect x="5" y="5" width="4" height="4" fill="#18181b" opacity="0.25" />
      </pattern>

      <pattern id="groundPattern_4_day" width="40" height="40" patternUnits="userSpaceOnUse">
        <rect width="40" height="40" fill="#16a34a" />
        <rect x="0" y="0" width="20" height="20" fill="#000" opacity="0.05" />
        <rect x="20" y="20" width="20" height="20" fill="#000" opacity="0.05" />
        <rect x="5" y="5" width="4" height="4" fill="#000" opacity="0.1" />
      </pattern>

      <pattern id="groundPattern_4_night" width="40" height="40" patternUnits="userSpaceOnUse">
        <rect width="40" height="40" fill="#27272a" />
        <rect x="0" y="0" width="20" height="20" fill="#18181b" opacity="0.5" />
        <rect x="20" y="20" width="20" height="20" fill="#18181b" opacity="0.5" />
        <rect x="5" y="5" width="4" height="4" fill="#3f3f46" opacity="0.3" />
        <circle cx="12" cy="12" r="1" fill="#94a3b8" opacity="0.25" />
        <circle cx="32" cy="32" r="1.2" fill="#cbd5e1" opacity="0.2" />
      </pattern>

      {/* Halos de lumière chaleureuse des lanternes et feux de camp du Djurdjura */}
      <radialGradient id="lanternGlowWarm" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#fef08a" stopOpacity="0.85" />
        <stop offset="25%" stopColor="#f59e0b" stopOpacity="0.5" />
        <stop offset="55%" stopColor="#ea580c" stopOpacity="0.2" />
        <stop offset="80%" stopColor="#d97706" stopOpacity="0.06" />
        <stop offset="100%" stopColor="#0f172a" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="lanternGroundLight" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#fef08a" stopOpacity="0.55" />
        <stop offset="35%" stopColor="#f59e0b" stopOpacity="0.3" />
        <stop offset="70%" stopColor="#d97706" stopOpacity="0.08" />
        <stop offset="100%" stopColor="#0f172a" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="campfireGroundLight" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#fef08a" stopOpacity="0.65" />
        <stop offset="30%" stopColor="#f97316" stopOpacity="0.35" />
        <stop offset="65%" stopColor="#c2410c" stopOpacity="0.15" />
        <stop offset="100%" stopColor="#0f172a" stopOpacity="0" />
      </radialGradient>

      {/* Masque de fondu progressif ultra-doux sur les bordures latérales et haute/basse */}
      <mask id={`boardFeatherMask_${levelId}`}>
        <rect width="400" height="1200" fill="url(#maskGradHorizontal)" />
      </mask>

      <linearGradient id="maskGradHorizontal" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
        <stop offset="3%" stopColor="#ffffff" stopOpacity="0.35" />
        <stop offset="7%" stopColor="#ffffff" stopOpacity="0.7" />
        <stop offset="12%" stopColor="#ffffff" stopOpacity="1" />
        <stop offset="88%" stopColor="#ffffff" stopOpacity="1" />
        <stop offset="93%" stopColor="#ffffff" stopOpacity="0.7" />
        <stop offset="97%" stopColor="#ffffff" stopOpacity="0.35" />
        <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
      </linearGradient>

      {/* Dégradé de transition d'ambiance périphérique (flancs gauche/droite et raccord bas) */}
      <linearGradient id={`edgeVignetteLeft_${levelId}`} x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor={baseColor} stopOpacity="0.8" />
        <stop offset="100%" stopColor={baseColor} stopOpacity="0" />
      </linearGradient>
      <linearGradient id={`edgeVignetteRight_${levelId}`} x1="100%" y1="0%" x2="0%" y2="0%">
        <stop offset="0%" stopColor={baseColor} stopOpacity="0.8" />
        <stop offset="100%" stopColor={baseColor} stopOpacity="0" />
      </linearGradient>
      <linearGradient id={`edgeVignetteBottom_${levelId}`} x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor={baseColor} stopOpacity="0" />
        <stop offset="60%" stopColor={baseColor} stopOpacity="0.4" />
        <stop offset="100%" stopColor={baseColor} stopOpacity="0.95" />
      </linearGradient>
      {levelId === 2 ? (
        <pattern
          id={`dirtTile_${levelId}`}
          width="20"
          height="20"
          patternUnits="userSpaceOnUse"
        >
          {/* Chemin blanc neige immaculée du Djurdjura */}
          <rect width="20" height="20" fill="#ffffff" />
          <rect x="0" y="0" width="10" height="10" fill="#f8fafc" />
          <rect x="10" y="10" width="10" height="10" fill="#f1f5f9" />
          <rect x="2" y="2" width="3" height="3" fill="#ffffff" />
          <rect x="12" y="12" width="3" height="3" fill="#e2e8f0" opacity="0.6" />
          <circle cx="16" cy="4" r="0.8" fill="#e0f2fe" />
          <circle cx="5" cy="15" r="0.8" fill="#ffffff" />
        </pattern>
      ) : levelId === 1 ? (
        <pattern
          id={`dirtTile_${levelId}`}
          width="20"
          height="20"
          patternUnits="userSpaceOnUse"
        >
          {/* Chemin ocre / terre d'oliviers */}
          <rect width="20" height="20" fill="#b47b48" />
          <rect x="0" y="0" width="10" height="10" fill="#c48c59" />
          <rect x="10" y="10" width="10" height="10" fill="#c48c59" />
          <rect x="2" y="2" width="2" height="2" fill="#9c6536" />
        </pattern>
      ) : (
        <pattern
          id={`dirtTile_${levelId}`}
          width="20"
          height="20"
          patternUnits="userSpaceOnUse"
        >
          <rect width="20" height="20" fill="#a07a50" />
          <rect x="0" y="0" width="10" height="10" fill="#ad875b" />
          <rect x="10" y="10" width="10" height="10" fill="#ad875b" />
          <rect x="2" y="2" width="2" height="2" fill="#8c6840" />
        </pattern>
      )}

      {/* Forest / Village assets */}
      <g id="tree_0">
        <ellipse cx="0" cy="5" rx="16" ry="6" fill="#000" opacity="0.3" />
        <path d="M-4,0 L4,0 L3,-15 L-3,-15 Z" fill="#5c4033" />
        <path d="M-2,0 L2,0 L1,-15 L-1,-15 Z" fill="#78350f" />
        <circle cx="0" cy="-25" r="18" fill="#14532d" />
        <circle cx="-8" cy="-20" r="14" fill="#166534" />
        <circle cx="8" cy="-18" r="12" fill="#166534" />
        <circle cx="0" cy="-30" r="14" fill="#22c55e" />
        <circle cx="-4" cy="-25" r="8" fill="#4ade80" />
      </g>
      {/* Level 0 Houses: Taddart (Architecture Kabyle Traditionnelle : Pierre de Taille & Brique) */}
      {/* Variant 0: Grande Bâtisse en Pierre de Montagne (Tazeqqa) */}
      <g id="house_0_0">
        <polygon points="-36,16 46,16 50,-10 28,-10" fill="#000" opacity="0.3" />
        {/* Corps principal en belle pierre beige-grise de montagne */}
        <rect x="-30" y="-11" width="60" height="26" fill="#d8cdbf" stroke="#716251" strokeWidth="1.2" rx="1" />
        {/* Base de fondation en pierre taillée plus sombre */}
        <rect x="-30" y="9" width="60" height="6" fill="#aba091" stroke="#716251" strokeWidth="0.8" />
        {/* Quelques joints de pierres nets et épurés */}
        <line x1="-30" y1="-3" x2="30" y2="-3" stroke="#9e9282" strokeWidth="0.8" />
        <line x1="-30" y1="3" x2="30" y2="3" stroke="#9e9282" strokeWidth="0.8" />
        <line x1="-12" y1="-11" x2="-12" y2="-3" stroke="#9e9282" strokeWidth="0.8" />
        <line x1="16" y1="-11" x2="16" y2="-3" stroke="#9e9282" strokeWidth="0.8" />
        <line x1="-20" y1="-3" x2="-20" y2="3" stroke="#9e9282" strokeWidth="0.8" />
        <line x1="8" y1="-3" x2="8" y2="3" stroke="#9e9282" strokeWidth="0.8" />
        {/* Chaînage d'angle stylisé */}
        <rect x="-30" y="-11" width="4" height="4.5" fill="#aba091" />
        <rect x="-30" y="-2" width="4" height="4.5" fill="#aba091" />
        <rect x="26" y="-7" width="4" height="4.5" fill="#aba091" />
        <rect x="26" y="3" width="4" height="4.5" fill="#aba091" />

        {/* Linteau en bois de cèdre au-dessus de la porte */}
        <rect x="-9" y="-1" width="18" height="2.5" fill="#451a03" rx="0.5" />
        {/* Porte cintrée en bois clouté traditionnel */}
        <path d="M-7,15 L-7,1 A7,7 0 0 1 7,1 L7,15 Z" fill="#290e02" />
        <path d="M-5.5,15 L-5.5,2.5 A5.5,5.5 0 0 1 5.5,2.5 L5.5,15 Z" fill="#5c2c10" />
        <line x1="0" y1="2" x2="0" y2="15" stroke="#290e02" strokeWidth="1" />
        <circle cx="3.5" cy="8.5" r="0.9" fill="#facc15" />

        {/* Fenêtre avec volets bleu indigo kabyle */}
        <rect x="-22" y="-2" width="9" height="9" fill="#0284c7" stroke="#0369a1" strokeWidth="0.8" rx="0.5" />
        <rect x="-20" y="0" width="5" height="5" fill="#fef08a" opacity="0.85" />
        <line x1="-17.5" y1="-2" x2="-17.5" y2="7" stroke="#0369a1" strokeWidth="0.8" />

        {/* Toit traditionnel en tuiles romaines rouges (Iqremiden) */}
        <polygon points="-35,-11 0,-30 35,-11" fill="#991b1b" stroke="#570a0a" strokeWidth="1" />
        <polygon points="-31,-11 0,-27 31,-11" fill="#dc2626" />
        <path d="M-34,-11 L0,-29 L34,-11" fill="none" stroke="#ea580c" strokeWidth="1.2" />

        {/* Jarre en terre cuite à l'entrée */}
        <ellipse cx="16" cy="14" rx="3" ry="1.2" fill="#000" opacity="0.3" />
        <path d="M13,13.5 C11.5,9.5 13.5,6 16,6 C18.5,6 20.5,9.5 19,13.5 Z" fill="#c2410c" stroke="#7c2d12" strokeWidth="0.6" />
        <ellipse cx="16" cy="6" rx="2" ry="0.8" fill="#ea580c" />
      </g>
      <g id="house_0"><use href="#house_0_0" /></g>

      {/* Variant 1: Maison Traditionnelle à 2 Étages avec Toit-Terrasse Plat (Sans Toit en Pente) */}
      <g id="house_0_1">
        <polygon points="-34,16 42,16 46,-12 24,-12" fill="#000" opacity="0.3" />
        {/* Rez-de-chaussée (RDC) */}
        <rect x="-26" y="-3" width="52" height="18" fill="#cfc4b4" stroke="#716251" strokeWidth="1.2" rx="0.5" />
        {/* Étage supérieur */}
        <rect x="-26" y="-22" width="52" height="20" fill="#ded4c5" stroke="#716251" strokeWidth="1.2" rx="0.5" />

        {/* Rebord / Corniche séparant les 2 étages */}
        <rect x="-28" y="-4" width="56" height="2.5" fill="#a49989" stroke="#716251" strokeWidth="0.8" rx="0.5" />

        {/* Acrotère / Parapet du toit-terrasse plat */}
        <rect x="-28" y="-24.5" width="56" height="3.5" fill="#8c7e6c" stroke="#594d3f" strokeWidth="1" rx="0.5" />
        {/* Petits piliers d'angle sur le toit terrasse */}
        <rect x="-28" y="-27" width="5" height="3" fill="#a49989" stroke="#716251" strokeWidth="0.8" rx="0.4" />
        <rect x="23" y="-27" width="5" height="3" fill="#a49989" stroke="#716251" strokeWidth="0.8" rx="0.4" />
        <rect x="-2" y="-26.5" width="4" height="2.5" fill="#a49989" stroke="#716251" strokeWidth="0.8" rx="0.4" />

        {/* Porte d'entrée cintrée en bois au RDC */}
        <path d="M-8,15 L-8,2 A8,8 0 0 1 8,2 L8,15 Z" fill="#290e02" />
        <path d="M-6.5,15 L-6.5,3.5 A6.5,6.5 0 0 1 6.5,3.5 L6.5,15 Z" fill="#5c2c10" />
        <line x1="0" y1="3" x2="0" y2="15" stroke="#290e02" strokeWidth="1" />
        <circle cx="3.5" cy="8.5" r="0.9" fill="#facc15" />

        {/* Fenêtre basse au RDC */}
        <rect x="-21" y="2" width="8" height="8" fill="#0284c7" stroke="#0369a1" strokeWidth="0.8" rx="0.5" />
        <rect x="-19.5" y="3.5" width="5" height="5" fill="#fef08a" opacity="0.85" />
        <line x1="-17" y1="2" x2="-17" y2="10" stroke="#0369a1" strokeWidth="0.8" />

        {/* Deux fenêtres à l'étage avec volets bleus */}
        <rect x="-20" y="-17" width="9" height="9" fill="#0284c7" stroke="#0369a1" strokeWidth="0.8" rx="0.5" />
        <rect x="-18" y="-15" width="5" height="5" fill="#fef08a" opacity="0.85" />
        <line x1="-15.5" y1="-17" x2="-15.5" y2="-8" stroke="#0369a1" strokeWidth="0.8" />

        <rect x="11" y="-17" width="9" height="9" fill="#0284c7" stroke="#0369a1" strokeWidth="0.8" rx="0.5" />
        <rect x="13" y="-15" width="5" height="5" fill="#fef08a" opacity="0.85" />
        <line x1="15.5" y1="-17" x2="15.5" y2="-8" stroke="#0369a1" strokeWidth="0.8" />

        {/* Motif décoratif berbère au centre de l'étage */}
        <g opacity="0.9">
          <polygon points="-4,-13 -1,-16 2,-13" fill="#dc2626" />
          <polygon points="2,-13 5,-16 8,-13" fill="#f59e0b" />
          <polygon points="-4,-13 -7,-16 -10,-13" fill="#10b981" />
        </g>

        {/* Jarre en terre cuite à l'entrée */}
        <ellipse cx="18" cy="14" rx="2.8" ry="1.2" fill="#000" opacity="0.25" />
        <path d="M15,13.5 C13.5,10 15,7 18,7 C21,7 22.5,10 21,13.5 Z" fill="#c2410c" stroke="#7c2d12" strokeWidth="0.6" />
        <ellipse cx="18" cy="7" rx="1.8" ry="0.8" fill="#ea580c" />
      </g>

      {/* Variant 2: Maisonnette de Village en Pierre Traditionnelle */}
      <g id="house_0_2">
        <polygon points="-34,16 42,16 46,-10 26,-10" fill="#000" opacity="0.3" />
        {/* Façade principale en pierre naturelle de montagne */}
        <rect x="-28" y="-11" width="56" height="26" fill="#cfc4b4" stroke="#716251" strokeWidth="1.2" rx="0.5" />

        {/* Double porte en bois */}
        <rect x="-6" y="2" width="12" height="13" fill="#290e02" stroke="#1c1917" strokeWidth="0.8" />
        <line x1="0" y1="2" x2="0" y2="15" stroke="#5c2c10" strokeWidth="1" />
        <circle cx="3" cy="8.5" r="0.8" fill="#facc15" />

        {/* Fenêtres symétriques à volets bleus */}
        <rect x="-21" y="-8" width="7" height="7" fill="#0284c7" stroke="#0369a1" strokeWidth="0.8" rx="0.5" />
        <rect x="-19.5" y="-6.5" width="4" height="4" fill="#fef08a" opacity="0.85" />
        <rect x="14" y="-8" width="7" height="7" fill="#0284c7" stroke="#0369a1" strokeWidth="0.8" rx="0.5" />
        <rect x="15.5" y="-6.5" width="4" height="4" fill="#fef08a" opacity="0.85" />

        {/* Toit en tuiles de terre cuite */}
        <polygon points="-33,-11 0,-28 33,-11" fill="#991b1b" stroke="#570a0a" strokeWidth="1" />
        <polygon points="-30,-11 0,-25 30,-11" fill="#dc2626" />

        {/* Symbole Berbère Yaz (ⵣ) au fronton */}
        <circle cx="0" cy="-6" r="3.2" fill="#fef08a" stroke="#ca8a04" strokeWidth="0.8" />
        <text x="0" y="-4.2" fill="#dc2626" fontSize="4.5" fontWeight="black" textAnchor="middle">ⵣ</text>
      </g>


      {/* Village Well (Tala / Anu n Taddart - Level 0) */}
      <g id="well_0">
        {/* Ground shadow */}
        <ellipse cx="0" cy="6" rx="15" ry="5.5" fill="#000" opacity="0.35" />
        {/* Stone Basin Body */}
        <rect x="-10" y="-3" width="20" height="9" rx="1.5" fill="#94a3b8" stroke="#64748b" strokeWidth="1" />
        {/* Stone texture bricks */}
        <line x1="-10" y1="1" x2="10" y2="1" stroke="#475569" strokeWidth="0.8" />
        <line x1="-3" y1="-3" x2="-3" y2="1" stroke="#475569" strokeWidth="0.8" />
        <line x1="4" y1="-3" x2="4" y2="1" stroke="#475569" strokeWidth="0.8" />
        <line x1="0" y1="1" x2="0" y2="6" stroke="#475569" strokeWidth="0.8" />
        {/* Top Rim & Deep Water Center */}
        <ellipse cx="0" cy="-3" rx="10" ry="3.5" fill="#64748b" />
        <ellipse cx="0" cy="-3" rx="8" ry="2.6" fill="#1e293b" />
        <ellipse cx="0" cy="-3" rx="6.5" ry="1.8" fill="#0284c7" />
        <ellipse cx="-1.5" cy="-3.5" rx="3" ry="0.8" fill="#38bdf8" opacity="0.8" />
        {/* Upright Wooden Posts */}
        <rect x="-8.5" y="-19" width="2.5" height="16" fill="#78350f" stroke="#451a03" strokeWidth="0.6" />
        <rect x="6" y="-19" width="2.5" height="16" fill="#78350f" stroke="#451a03" strokeWidth="0.6" />
        {/* Wooden Winch Beam & Wheel */}
        <rect x="-8.5" y="-18" width="17" height="2" fill="#92400e" />
        <circle cx="0" cy="-17" r="2.8" fill="#b45309" stroke="#78350f" strokeWidth="0.7" />
        {/* Hanging Rope & Wooden Water Bucket (Tasilt) */}
        <line x1="0" y1="-17" x2="0" y2="-9" stroke="#fed7aa" strokeWidth="1" />
        <path d="M-3,-9 L-2.5,-5 L2.5,-5 L3,-9 Z" fill="#78350f" stroke="#451a03" strokeWidth="0.6" />
        <line x1="-3" y1="-9" x2="3" y2="-9" stroke="#ca8a04" strokeWidth="0.8" />
        {/* Pitched Red Tile Roof */}
        <polygon points="-12,-18 0,-26 12,-18" fill="#991b1b" />
        <polygon points="-11,-18 0,-24.5 11,-18" fill="#dc2626" />
        {/* Small Clay Water Jug (Tabuqalt) placed beside the well */}
        <g transform="translate(11, 4) scale(0.65)">
          <path d="M-4,3 C-6,0 -4,-6 0,-6 C4,-6 6,0 4,3 Z" fill="#c2410c" />
          <ellipse cx="0" cy="-6" rx="2.5" ry="1" fill="#ea580c" />
          <path d="M-4,-2 C-7,-2 -7,-5 -4,-5" fill="none" stroke="#9a3412" strokeWidth="1.2" />
        </g>
      </g>

      {/* Olive grove assets (Level 1 - Azemmur) */}
      <g id="tree_1">
        <ellipse cx="0" cy="6" rx="18" ry="6" fill="#000" opacity="0.35" />
        {/* Gnarled, sculptural olive trunk */}
        <path d="M-6,5 C-9,-6 -2,-12 -5,-22 C-2,-14 3,-10 6,5 Z" fill="#57534e" />
        <path d="M-4,5 C-6,-5 0,-12 -2,-22 C1,-14 3,-8 4,5 Z" fill="#78716c" />
        <path d="M-1,3 C0,-4 3,-10 2,-18 C3,-12 4,-6 3,3 Z" fill="#a8a29e" opacity="0.6" />
        {/* Layered sage / olive green foliage */}
        <ellipse cx="0" cy="-24" rx="22" ry="16" fill="#365314" />
        <ellipse cx="-8" cy="-26" rx="15" ry="12" fill="#4d7c0f" />
        <ellipse cx="8" cy="-24" rx="14" ry="11" fill="#4d7c0f" />
        <ellipse cx="-3" cy="-28" rx="14" ry="10" fill="#65a30d" />
        <ellipse cx="5" cy="-29" rx="11" ry="8" fill="#84cc16" />
        <ellipse cx="0" cy="-33" rx="7" ry="5" fill="#bef264" opacity="0.8" />
        {/* Hanging Ripe Black and Green Olives */}
        <circle cx="-13" cy="-22" r="2.2" fill="#18181b" />
        <circle cx="-12" cy="-23" r="0.7" fill="#ffffff" opacity="0.8" />
        <circle cx="11" cy="-20" r="2.2" fill="#18181b" />
        <circle cx="12" cy="-21" r="0.7" fill="#ffffff" opacity="0.8" />
        <circle cx="-3" cy="-18" r="2" fill="#3f6212" />
        <circle cx="4" cy="-17" r="2.2" fill="#18181b" />
        <circle cx="-6" cy="-31" r="1.8" fill="#4d7c0f" />
        <circle cx="8" cy="-30" r="2" fill="#18181b" />
        <circle cx="0" cy="-35" r="1.8" fill="#3f6212" />
      </g>

      {/* Level 1 Houses: Azemmur (Architecture Oléicole & Bastides des Collines) */}
      {/* Variant 0: Bastide & Moulin à Huile (Tassart) */}
      <g id="house_1_0">
        <polygon points="-38,16 48,16 52,-10 32,-10" fill="#000" opacity="0.35" />
        {/* Warm sandstone cottage */}
        <rect x="-32" y="-10" width="64" height="26" fill="#e2d9c8" stroke="#a89f91" strokeWidth="1" />
        {/* Terracotta tiled roof with clay awning */}
        <polygon points="-38,-10 0,-28 38,-10" fill="#b45309" />
        <polygon points="-36,-10 0,-26 36,-10" fill="#d97706" />
        <line x1="-24" y1="-10" x2="-2" y2="-25" stroke="#92400e" strokeWidth="1" />
        <line x1="-12" y1="-10" x2="0" y2="-26" stroke="#92400e" strokeWidth="1" />
        <line x1="0" y1="-10" x2="12" y2="-20" stroke="#92400e" strokeWidth="1" />
        <line x1="12" y1="-10" x2="24" y2="-15" stroke="#92400e" strokeWidth="1" />
        {/* Door and wooden lintel */}
        <rect x="-7" y="2" width="14" height="14" fill="#5c3818" />
        <path d="M-7,2 A7,7 0 0 1 7,2" fill="#3b220c" />
        <rect x="-9" y="1" width="18" height="2.5" fill="#3b220c" />
        {/* Small window with blue shutters */}
        <rect x="15" y="-2" width="10" height="9" fill="#0284c7" stroke="#0369a1" strokeWidth="0.8" />
        <line x1="20" y1="-2" x2="20" y2="7" stroke="#38bdf8" strokeWidth="0.8" />
        {/* Oil jars lined up under the roof */}
        <g transform="translate(-22, 10) scale(0.65)">
          <ellipse cx="0" cy="5" rx="5" ry="2" fill="#000" opacity="0.3" />
          <path d="M-5,4 C-7,-1 -4,-8 0,-8 C4,-8 7,-1 5,4 Z" fill="#c2410c" />
          <ellipse cx="0" cy="-8" rx="3" ry="1.2" fill="#ea580c" />
        </g>
        <g transform="translate(-15, 12) scale(0.5)">
          <path d="M-5,4 C-7,-1 -4,-8 0,-8 C4,-8 7,-1 5,4 Z" fill="#9a3412" />
        </g>
      </g>
      <g id="house_1"><use href="#house_1_0" /></g>

      {/* Variant 1: Domaine Oléicole à Grande Terrasse & Hangar de Récolte */}
      <g id="house_1_1">
        <polygon points="-40,16 46,16 48,-8 26,-8" fill="#000" opacity="0.32" />
        {/* Left main stone farmhouse */}
        <rect x="-34" y="-8" width="40" height="24" fill="#dcd1bd" stroke="#8c8273" strokeWidth="1" />
        {/* Stone texture bands */}
        <line x1="-34" y1="0" x2="6" y2="0" stroke="#b5ab9a" strokeWidth="0.8" />
        <line x1="-34" y1="8" x2="6" y2="8" stroke="#b5ab9a" strokeWidth="0.8" />
        {/* Right open harvest shed */}
        <polygon points="4,16 38,16 38,0 4,-3" fill="#78350f" opacity="0.25" />
        <line x1="6" y1="-3" x2="38" y2="0" stroke="#78350f" strokeWidth="2.5" />
        <rect x="34" y="0" width="2.5" height="16" fill="#78350f" />
        <rect x="18" y="-1" width="2" height="17" fill="#78350f" />
        {/* Stacked harvest crates in shed */}
        <rect x="8" y="7" width="10" height="8" fill="#a16207" stroke="#78350f" strokeWidth="0.8" />
        <rect x="20" y="9" width="12" height="7" fill="#ca8a04" stroke="#854d0e" strokeWidth="0.8" />
        {/* Main arched portal */}
        <path d="M-22,16 L-22,5 A5,5 0 0 1 -12,5 L-12,16 Z" fill="#451a03" />
        <path d="M-20,16 L-20,7 A3,3 0 0 1 -14,7 L-14,16 Z" fill="#78350f" />
        {/* Upper arched hayloft window */}
        <rect x="-7" y="-3" width="7" height="7" rx="2" fill="#1e293b" stroke="#78350f" strokeWidth="0.8" />
        {/* Terracotta tiled roof with chimney */}
        <polygon points="-38,-8 -14,-26 10,-8" fill="#9a3412" />
        <polygon points="-34,-8 -14,-23 6,-8" fill="#c2410c" />
        <line x1="-24" y1="-8" x2="-14" y2="-23" stroke="#7c2d12" strokeWidth="0.8" />
        <line x1="-4" y1="-8" x2="-14" y2="-23" stroke="#7c2d12" strokeWidth="0.8" />
        <rect x="-30" y="-22" width="4" height="10" fill="#a89f91" stroke="#78716c" strokeWidth="0.8" />
      </g>

      {/* Variant 2: Tour-Cottage du Gardien d'Oliveraie */}
      <g id="house_1_2">
        <polygon points="-30,16 38,16 42,-12 20,-12" fill="#000" opacity="0.3" />
        {/* Compact two-story stone tower */}
        <rect x="-24" y="-12" width="48" height="28" fill="#ece5d8" stroke="#9a9182" strokeWidth="1" />
        {/* Decorative olive wreath emblem over door */}
        <circle cx="0" cy="-4" r="3.5" fill="none" stroke="#65a30d" strokeWidth="1.5" />
        <circle cx="0" cy="-4" r="1.2" fill="#18181b" />
        {/* Lower rustic door */}
        <rect x="-6" y="2" width="12" height="14" fill="#3b220c" stroke="#1c1917" strokeWidth="0.8" />
        <circle cx="3" cy="9" r="0.9" fill="#facc15" />
        {/* Upper dual windows with green shutters */}
        <rect x="-18" y="-9" width="7" height="7" fill="#3f6212" stroke="#1a2e05" strokeWidth="0.8" />
        <rect x="-16" y="-7" width="3" height="3" fill="#fef08a" opacity="0.85" />
        <rect x="11" y="-9" width="7" height="7" fill="#3f6212" stroke="#1a2e05" strokeWidth="0.8" />
        <rect x="13" y="-7" width="3" height="3" fill="#fef08a" opacity="0.85" />
        {/* Four-pitch pyramid terracotta roof */}
        <polygon points="-28,-12 0,-28 28,-12" fill="#b45309" />
        <polygon points="-25,-12 0,-25 25,-12" fill="#ea580c" />
        <polygon points="0,-25 25,-12 0,-12" fill="#c2410c" />
      </g>


      {/* Olive Grove Stone Well (Level 1 - Azemmur) */}
      <g id="well_1">
        {/* Ground shadow */}
        <ellipse cx="0" cy="6" rx="15" ry="5.5" fill="#000" opacity="0.35" />
        {/* Limestone Masonry Basin */}
        <rect x="-11" y="-3" width="22" height="9" rx="1.5" fill="#e2d9c8" stroke="#a89f91" strokeWidth="1" />
        <line x1="-11" y1="1" x2="11" y2="1" stroke="#8c8273" strokeWidth="0.8" />
        <line x1="-4" y1="-3" x2="-4" y2="1" stroke="#8c8273" strokeWidth="0.8" />
        <line x1="4" y1="-3" x2="4" y2="1" stroke="#8c8273" strokeWidth="0.8" />
        {/* Water & Basin Rim */}
        <ellipse cx="0" cy="-3" rx="10.5" ry="3.5" fill="#a89f91" />
        <ellipse cx="0" cy="-3" rx="8.5" ry="2.6" fill="#1c1917" />
        <ellipse cx="0" cy="-3" rx="7" ry="1.8" fill="#0284c7" />
        <ellipse cx="-2" cy="-3.5" rx="3" ry="0.8" fill="#38bdf8" opacity="0.8" />
        {/* Wooden Support Beams */}
        <rect x="-9" y="-20" width="2.5" height="17" fill="#78350f" stroke="#451a03" strokeWidth="0.6" />
        <rect x="6.5" y="-20" width="2.5" height="17" fill="#78350f" stroke="#451a03" strokeWidth="0.6" />
        {/* Winch Crank & Pulley with Bucket */}
        <rect x="-9" y="-19" width="18" height="2.2" fill="#92400e" />
        <circle cx="0" cy="-18" r="2.8" fill="#b45309" />
        <line x1="0" y1="-18" x2="0" y2="-9" stroke="#fed7aa" strokeWidth="1" />
        <path d="M-3,-9 L-2.5,-5 L2.5,-5 L3,-9 Z" fill="#78350f" stroke="#451a03" strokeWidth="0.6" />
        {/* Terracotta Canopy with Olive Branch */}
        <polygon points="-13,-19 0,-27 13,-19" fill="#b45309" />
        <polygon points="-12,-19 0,-25.5 12,-19" fill="#d97706" />
        <path d="M-6,-22 Q0,-25 7,-20" stroke="#4d7c0f" strokeWidth="1.5" fill="none" />
        <circle cx="2" cy="-22" r="1.2" fill="#18181b" />
        {/* Clay Water Pitcher beside the well */}
        <g transform="translate(12, 4) scale(0.65)">
          <path d="M-5,4 C-7,-1 -4,-8 0,-8 C4,-8 7,-1 5,4 Z" fill="#c2410c" />
          <ellipse cx="0" cy="-8" rx="2.5" ry="1" fill="#ea580c" />
          <path d="M-4,-2 C-7,-2 -7,-6 -4,-6" fill="none" stroke="#9a3412" strokeWidth="1.2" />
        </g>
      </g>

      {/* Traditional Clay Olive Oil Jar (Tajeḥlit n zzit) */}
      <g id="oil_jar_single">
        <ellipse cx="0" cy="6" rx="8" ry="3" fill="#000" opacity="0.4" />
        {/* Terracotta jar body */}
        <path d="M-8,5 C-12,0 -9,-12 0,-14 C9,-12 12,0 8,5 C5,8 -5,8 -8,5 Z" fill="#c2410c" />
        <path d="M-6,4 C-9,0 -7,-10 0,-12 C7,-10 9,0 6,4 C4,6 -4,6 -6,4 Z" fill="#ea580c" />
        {/* Light glint */}
        <path d="M-3,3 C-5,0 -4,-7 0,-9" stroke="#fed7aa" strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.7" />
        {/* Handles */}
        <path d="M-8,-4 C-12,-4 -12,-9 -8,-9" fill="none" stroke="#9a3412" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M8,-4 C12,-4 12,-9 8,-9" fill="none" stroke="#9a3412" strokeWidth="1.8" strokeLinecap="round" />
        {/* Berber geometric painted motifs */}
        <path d="M-5,-3 L-3,-6 L-1,-3 L1,-6 L3,-3 L5,-6" fill="none" stroke="#451a03" strokeWidth="1" />
        <circle cx="0" cy="0" r="1.2" fill="#451a03" />
        {/* Neck and cork/wooden lid */}
        <rect x="-4" y="-16" width="8" height="3" rx="1" fill="#9a3412" />
        <rect x="-3" y="-18" width="6" height="3" rx="1" fill="#78350f" />
      </g>

      {/* Group of Clay Oil Jars & Amphoras */}
      <g id="oil_jars_cluster">
        <ellipse cx="0" cy="7" rx="16" ry="5" fill="#000" opacity="0.35" />
        {/* Big Jar */}
        <g transform="translate(-5, 0) scale(1.1)">
          <path d="M-8,5 C-12,0 -9,-12 0,-14 C9,-12 12,0 8,5 C5,8 -5,8 -8,5 Z" fill="#c2410c" />
          <path d="M-6,4 C-9,0 -7,-10 0,-12 C7,-10 9,0 6,4 Z" fill="#ea580c" />
          <path d="M-5,-2 L-3,-5 L-1,-2 L1,-5 L3,-2 L5,-5" fill="none" stroke="#451a03" strokeWidth="1" />
          <rect x="-4" y="-16" width="8" height="3" fill="#9a3412" />
          <rect x="-3" y="-18" width="6" height="3" fill="#78350f" />
        </g>
        {/* Small Tilted Jar */}
        <g transform="translate(9, 3) scale(0.8) rotate(12)">
          <path d="M-8,5 C-12,0 -9,-12 0,-14 C9,-12 12,0 8,5 C5,8 -5,8 -8,5 Z" fill="#9a3412" />
          <path d="M-6,4 C-8,0 -6,-9 0,-11 C6,-9 8,0 6,4 Z" fill="#c2410c" />
          <rect x="-3" y="-16" width="6" height="3" fill="#78350f" />
        </g>
        {/* Olive Branch & Drops */}
        <path d="M-12,5 Q-7,2 2,7" stroke="#4d7c0f" strokeWidth="1.5" fill="none" />
        <ellipse cx="-9" cy="4" rx="2" ry="1" fill="#65a30d" transform="rotate(-20 -9 4)" />
        <ellipse cx="-4" cy="3.5" rx="2" ry="1" fill="#65a30d" transform="rotate(15 -4 3.5)" />
        <circle cx="-1" cy="5" r="1.5" fill="#18181b" />
        {/* Golden oil droplet reflection */}
        <ellipse cx="6" cy="6" rx="2" ry="1" fill="#facc15" opacity="0.8" />
      </g>

      {/* Traditional Stone Olive Millstone (Taṣṣart n uzemmur / Ṭṭaḥuna) */}
      <g id="stone_millstone">
        {/* Ground shadow */}
        <ellipse cx="0" cy="10" rx="26" ry="12" fill="#000" opacity="0.4" />
        {/* Stone Basin Base (La bassine circulaire en pierre) */}
        <ellipse cx="0" cy="6" rx="24" ry="10" fill="#64748b" stroke="#475569" strokeWidth="1.5" />
        <ellipse cx="0" cy="4" rx="22" ry="8.5" fill="#334155" />
        <ellipse cx="0" cy="3" rx="19" ry="7" fill="#1e293b" />
        {/* Crushed olives paste inside basin */}
        <ellipse cx="0" cy="3" rx="18" ry="6.5" fill="#2e1065" opacity="0.7" />
        <ellipse cx="-6" cy="3" rx="7" ry="2.5" fill="#365314" opacity="0.8" />
        <ellipse cx="5" cy="4" rx="8" ry="2.5" fill="#4d7c0f" opacity="0.7" />
        {/* Vertical Stone Grinding Wheel (La grande meule verticale) */}
        <g transform="translate(-4, -4)">
          <ellipse cx="0" cy="6" rx="8" ry="15" fill="#94a3b8" stroke="#475569" strokeWidth="1.5" />
          <ellipse cx="-1.5" cy="6" rx="6" ry="13" fill="#cbd5e1" />
          <ellipse cx="-1.5" cy="6" rx="2" ry="4" fill="#334155" />
          {/* Stone chiseled texture */}
          <line x1="-3" y1="-2" x2="-1" y2="1" stroke="#64748b" strokeWidth="1" />
          <line x1="-4" y1="8" x2="-1" y2="12" stroke="#64748b" strokeWidth="1" />
          <line x1="0" y1="3" x2="2" y2="7" stroke="#64748b" strokeWidth="1" />
        </g>
        {/* Wooden Central Pivot Beam (Poutre et axe en chêne) */}
        <rect x="-2" y="-12" width="4" height="16" fill="#78350f" stroke="#451a03" strokeWidth="1" />
        <circle cx="0" cy="-12" r="3" fill="#a16207" />
        <path d="M-18,-4 L18,2" stroke="#5c3818" strokeWidth="3" strokeLinecap="round" />
        <path d="M-18,-4 L-4,-4" stroke="#a16207" strokeWidth="3.5" strokeLinecap="round" />
      </g>

      {/* Traditional Olive Press with Woven Discs (Lemɛinsra / Presse traditionnelle) */}
      <g id="olive_press">
        <ellipse cx="0" cy="8" rx="20" ry="8" fill="#000" opacity="0.4" />
        {/* Heavy Stone Base */}
        <rect x="-18" y="0" width="36" height="8" rx="2" fill="#475569" stroke="#334155" strokeWidth="1" />
        {/* Wooden Upright Posts */}
        <rect x="-15" y="-24" width="5" height="25" fill="#78350f" stroke="#451a03" strokeWidth="1" />
        <rect x="10" y="-24" width="5" height="25" fill="#78350f" stroke="#451a03" strokeWidth="1" />
        <rect x="-17" y="-26" width="34" height="5" rx="1" fill="#92400e" stroke="#451a03" strokeWidth="1" />
        {/* Stack of Woven Alfa/Esparto Discs (Ticekkartin / Chouaris) */}
        <ellipse cx="0" cy="0" rx="11" ry="3.5" fill="#a16207" />
        <ellipse cx="0" cy="-3" rx="10.5" ry="3.2" fill="#ca8a04" />
        <ellipse cx="0" cy="-6" rx="10" ry="3" fill="#a16207" />
        <ellipse cx="0" cy="-9" rx="9.5" ry="2.8" fill="#ca8a04" />
        {/* Pressing Plate & Big Wooden Screw */}
        <rect x="-8" y="-12" width="16" height="3" fill="#451a03" />
        <rect x="-2.5" y="-23" width="5" height="12" fill="#78350f" />
        {/* Golden Olive Oil Stream & Bowl */}
        <line x1="0" y1="2" x2="0" y2="7" stroke="#facc15" strokeWidth="1.5" />
        <ellipse cx="0" cy="7" rx="3.5" ry="1.5" fill="#ca8a04" stroke="#eab308" strokeWidth="1" />
      </g>

      {/* Tall Wild Grass Tufts (Hautes herbes & graminées des oliveraies) */}
      <g id="tall_wild_grass">
        <ellipse cx="0" cy="3" rx="12" ry="4" fill="#000" opacity="0.25" />
        {/* Grass blades */}
        <path d="M-8,3 C-12,-8 -9,-18 -14,-26" stroke="#4d7c0f" strokeWidth="1.8" strokeLinecap="round" fill="none" />
        <path d="M-5,3 C-7,-10 -5,-22 -9,-30" stroke="#65a30d" strokeWidth="2" strokeLinecap="round" fill="none" />
        <path d="M-2,3 C-3,-12 0,-24 -3,-34" stroke="#84cc16" strokeWidth="1.8" strokeLinecap="round" fill="none" />
        <path d="M1,3 C2,-14 4,-26 1,-36" stroke="#ca8a04" strokeWidth="1.8" strokeLinecap="round" fill="none" />
        <path d="M4,3 C7,-10 6,-22 10,-32" stroke="#65a30d" strokeWidth="2" strokeLinecap="round" fill="none" />
        <path d="M7,3 C11,-8 10,-18 15,-27" stroke="#4d7c0f" strokeWidth="1.8" strokeLinecap="round" fill="none" />
        <path d="M0,3 C3,-8 8,-16 6,-24" stroke="#d97706" strokeWidth="1.5" strokeLinecap="round" fill="none" />
        {/* Golden seed heads / wheat-like plumes */}
        <circle cx="-14" cy="-26" r="1.5" fill="#fde047" />
        <circle cx="-9" cy="-30" r="1.8" fill="#facc15" />
        <circle cx="-3" cy="-34" r="1.8" fill="#fde047" />
        <circle cx="1" cy="-36" r="2" fill="#facc15" />
        <circle cx="10" cy="-32" r="1.8" fill="#fde047" />
        <circle cx="15" cy="-27" r="1.5" fill="#facc15" />
      </g>

      {/* Wild Hillside Poppies & Flowers (Coquelicots & fleurs sauvages) */}
      <g id="wild_poppies">
        <g transform="translate(-8, 0)">
          <path d="M0,2 C-1,-5 2,-10 0,-16" stroke="#15803d" strokeWidth="1.2" fill="none" />
          <circle cx="-3" cy="-17" r="3.5" fill="#dc2626" />
          <circle cx="3" cy="-17" r="3.5" fill="#ef4444" />
          <circle cx="0" cy="-20" r="3.5" fill="#b91c1c" />
          <circle cx="0" cy="-17" r="1.8" fill="#18181b" />
        </g>
        <g transform="translate(6, 3) scale(0.85)">
          <path d="M0,2 C1,-4 -2,-9 0,-14" stroke="#15803d" strokeWidth="1.2" fill="none" />
          <circle cx="-3" cy="-15" r="3" fill="#ef4444" />
          <circle cx="3" cy="-15" r="3" fill="#dc2626" />
          <circle cx="0" cy="-17" r="3" fill="#b91c1c" />
          <circle cx="0" cy="-15" r="1.5" fill="#18181b" />
        </g>
        <g transform="translate(0, -4) scale(0.7)">
          <circle cx="-2" cy="-2" r="2.5" fill="#facc15" />
          <circle cx="2" cy="-2" r="2.5" fill="#facc15" />
          <circle cx="0" cy="2" r="2.5" fill="#facc15" />
          <circle cx="0" cy="0" r="1.8" fill="#ea580c" />
        </g>
      </g>

      {/* Harvest Olive Tree with Wooden Ladder leaning directly against it (Tazemmurt s Sellum d Uqecwal) */}
      <g id="harvest_baskets_ladder">
        {/* Ground shadow */}
        <ellipse cx="0" cy="7" rx="26" ry="9" fill="#000" opacity="0.38" />
        
        {/* Harvest Ground Sheet / Bâche de récolte d'olives */}
        <path d="M-22,6 Q-10,3 4,7 Q18,4 26,8 Q12,12 -6,10 Q-18,11 -22,6 Z" fill="#ca8a04" opacity="0.65" />
        {/* Olives dropped on the ground sheet */}
        <circle cx="-14" cy="6" r="1.5" fill="#18181b" />
        <circle cx="-10" cy="8" r="1.3" fill="#4d7c0f" />
        <circle cx="-4" cy="7" r="1.5" fill="#18181b" />
        <circle cx="12" cy="7.5" r="1.4" fill="#18181b" />
        <circle cx="18" cy="7" r="1.3" fill="#3f6212" />

        {/* Gnarled Olive Tree with ladder resting in its branches */}
        <g id="tree_ladder_base">
          {/* Main Trunk */}
          <path d="M-6,6 C-10,-6 -4,-16 -7,-26 C-4,-18 2,-12 5,6 Z" fill="#57534e" />
          <path d="M-4,6 C-7,-5 -1,-14 -3,-26 C0,-17 3,-8 4,6 Z" fill="#78716c" />
          <path d="M-1,4 C0,-4 3,-10 2,-20 C3,-13 4,-6 3,4 Z" fill="#a8a29e" opacity="0.6" />
          {/* Main Branch fork where ladder rests */}
          <path d="M-4,-16 C-10,-22 -14,-28 -18,-32" stroke="#57534e" strokeWidth="3.5" strokeLinecap="round" fill="none" />
          <path d="M-3,-18 C4,-24 10,-28 14,-32" stroke="#57534e" strokeWidth="3" strokeLinecap="round" fill="none" />

          {/* Olive Foliage */}
          <ellipse cx="-2" cy="-28" rx="22" ry="16" fill="#365314" />
          <ellipse cx="-10" cy="-30" rx="16" ry="12" fill="#4d7c0f" />
          <ellipse cx="7" cy="-28" rx="15" ry="11" fill="#4d7c0f" />
          <ellipse cx="-4" cy="-32" rx="14" ry="10" fill="#65a30d" />
          <ellipse cx="4" cy="-33" rx="12" ry="8" fill="#84cc16" />
          <ellipse cx="-1" cy="-37" rx="7" ry="5" fill="#bef264" opacity="0.8" />

          {/* Ripe Olives in branches */}
          <circle cx="-14" cy="-26" r="2.2" fill="#18181b" />
          <circle cx="-13" cy="-27" r="0.7" fill="#ffffff" opacity="0.8" />
          <circle cx="10" cy="-24" r="2.2" fill="#18181b" />
          <circle cx="-4" cy="-22" r="2" fill="#3f6212" />
          <circle cx="5" cy="-21" r="2.2" fill="#18181b" />
          <circle cx="-7" cy="-35" r="1.8" fill="#4d7c0f" />
          <circle cx="7" cy="-34" r="2" fill="#18181b" />
        </g>

        {/* Wooden Ladder Resting Directly Against the Olive Tree (Sellum n wesɣar) */}
        <g transform="translate(-1, 0) rotate(-16)">
          {/* Ladder Shadow on trunk */}
          <line x1="-3" y1="-32" x2="-3" y2="6" stroke="#000000" strokeWidth="2.5" opacity="0.3" />
          {/* Ladder Rails */}
          <line x1="-4" y1="-32" x2="-4" y2="6" stroke="#78350f" strokeWidth="2.2" strokeLinecap="round" />
          <line x1="4" y1="-32" x2="4" y2="6" stroke="#78350f" strokeWidth="2.2" strokeLinecap="round" />
          {/* Ladder Rungs */}
          <line x1="-4" y1="-26" x2="4" y2="-26" stroke="#a16207" strokeWidth="1.6" />
          <line x1="-4" y1="-19" x2="4" y2="-19" stroke="#a16207" strokeWidth="1.6" />
          <line x1="-4" y1="-12" x2="4" y2="-12" stroke="#a16207" strokeWidth="1.6" />
          <line x1="-4" y1="-5" x2="4" y2="-5" stroke="#a16207" strokeWidth="1.6" />
          <line x1="-4" y1="2" x2="4" y2="2" stroke="#a16207" strokeWidth="1.6" />
        </g>

        {/* Woven Harvest Basket (Aqecwal) at tree foot overflowing with olives */}
        <g transform="translate(13, 2)">
          <ellipse cx="0" cy="5" rx="7" ry="2.5" fill="#000" opacity="0.3" />
          <path d="M-7,4 L-5,-5 L5,-5 L7,4 Z" fill="#a16207" stroke="#78350f" strokeWidth="1" />
          <line x1="-5" y1="-2" x2="5" y2="-2" stroke="#78350f" strokeWidth="0.8" />
          <line x1="-6" y1="1" x2="6" y2="1" stroke="#78350f" strokeWidth="0.8" />
          {/* Olives inside basket */}
          <ellipse cx="0" cy="-5" rx="5" ry="2.5" fill="#18181b" />
          <circle cx="-2.5" cy="-6" r="1.6" fill="#18181b" />
          <circle cx="0.5" cy="-7" r="1.6" fill="#4d7c0f" />
          <circle cx="3" cy="-6" r="1.6" fill="#18181b" />
          <circle cx="-1" cy="-4.5" r="1.4" fill="#65a30d" />
        </g>
      </g>

      {/* Traditional Drystone Terrace Wall (Taftist) */}
      <g id="drystone_terrace">
        <polygon points="-26,8 26,8 28,-4 -28,-4" fill="#000" opacity="0.3" />
        {/* Layered rustic stones */}
        <rect x="-25" y="-3" width="12" height="6" rx="1" fill="#64748b" stroke="#334155" strokeWidth="1" />
        <rect x="-12" y="-3" width="14" height="6" rx="1" fill="#78716c" stroke="#44403c" strokeWidth="1" />
        <rect x="3" y="-3" width="12" height="6" rx="1" fill="#64748b" stroke="#334155" strokeWidth="1" />
        <rect x="16" y="-3" width="10" height="6" rx="1" fill="#78716c" stroke="#44403c" strokeWidth="1" />
        <rect x="-22" y="3" width="15" height="5" rx="1" fill="#57534e" stroke="#292524" strokeWidth="1" />
        <rect x="-6" y="3" width="16" height="5" rx="1" fill="#475569" stroke="#1e293b" strokeWidth="1" />
        <rect x="11" y="3" width="14" height="5" rx="1" fill="#57534e" stroke="#292524" strokeWidth="1" />
        {/* Small tuft of moss */}
        <circle cx="-12" cy="-4" r="2.5" fill="#65a30d" />
        <circle cx="3" cy="-4" r="2" fill="#84cc16" />
      </g>

      {/* Traditional Olive Wood Timber / Logs Depot (Dépôt de grumes d'olivier d'Azemmur) */}
      <g id="olive_logs_depot">
        {/* Soft cast shadow */}
        <ellipse cx="0" cy="8" rx="24" ry="8" fill="#000" opacity="0.36" />
        {/* Scattered woodchips & sawdust on ground */}
        <ellipse cx="-16" cy="7" rx="3.5" ry="1.5" fill="#ca8a04" opacity="0.8" />
        <ellipse cx="14" cy="9" rx="4" ry="1.8" fill="#eab308" opacity="0.75" />
        <ellipse cx="6" cy="11" rx="2.5" ry="1.2" fill="#a16207" opacity="0.9" />
        <circle cx="-8" cy="10" r="1.2" fill="#facc15" />
        <circle cx="18" cy="6" r="1.3" fill="#ca8a04" />
        {/* Side retaining wooden stakes holding the pile */}
        <line x1="-16" y1="10" x2="-20" y2="-12" stroke="#451a03" strokeWidth="2.5" strokeLinecap="round" />
        <line x1="16" y1="10" x2="20" y2="-12" stroke="#451a03" strokeWidth="2.5" strokeLinecap="round" />
        {/* Bottom Tier: Log 1 (Left) */}
        <g transform="translate(-10, 3)">
          <rect x="-4" y="-5" width="24" height="10" rx="3.5" fill="#78350f" stroke="#451a03" strokeWidth="1" />
          <line x1="-2" y1="-2" x2="18" y2="-2" stroke="#92400e" strokeWidth="1" />
          <line x1="-2" y1="2" x2="18" y2="2" stroke="#5c2409" strokeWidth="1" />
          <ellipse cx="-4" cy="0" rx="3.5" ry="5" fill="#ca8a04" stroke="#451a03" strokeWidth="0.8" />
          <ellipse cx="-4" cy="0" rx="2.2" ry="3.2" fill="#eab308" />
          <circle cx="-4" cy="0" r="1" fill="#451a03" />
        </g>
        {/* Bottom Tier: Log 2 (Right) */}
        <g transform="translate(6, 3)">
          <rect x="-4" y="-5" width="22" height="10" rx="3.5" fill="#854d0e" stroke="#451a03" strokeWidth="1" />
          <line x1="-2" y1="-2" x2="16" y2="-2" stroke="#a16207" strokeWidth="1" />
          <line x1="-2" y1="2" x2="16" y2="2" stroke="#713f12" strokeWidth="1" />
          <ellipse cx="-4" cy="0" rx="3.5" ry="5" fill="#d97706" stroke="#451a03" strokeWidth="0.8" />
          <ellipse cx="-4" cy="0" rx="2.2" ry="3.2" fill="#f59e0b" />
          <circle cx="-4" cy="0" r="1" fill="#451a03" />
        </g>
        {/* Middle Tier: Log 3 */}
        <g transform="translate(-6, -4)">
          <rect x="-4" y="-5" width="24" height="10" rx="3.5" fill="#78350f" stroke="#451a03" strokeWidth="1" />
          <line x1="-2" y1="-2" x2="18" y2="-2" stroke="#92400e" strokeWidth="1" />
          <ellipse cx="-4" cy="0" rx="3.5" ry="5" fill="#b45309" stroke="#451a03" strokeWidth="0.8" />
          <ellipse cx="-4" cy="0" rx="2" ry="3" fill="#d97706" />
          <circle cx="-4" cy="0" r="1" fill="#451a03" />
        </g>
        {/* Middle Tier: Log 4 */}
        <g transform="translate(8, -4)">
          <rect x="-4" y="-4.5" width="20" height="9" rx="3" fill="#92400e" stroke="#451a03" strokeWidth="1" />
          <ellipse cx="-4" cy="0" rx="3" ry="4.5" fill="#ca8a04" stroke="#451a03" strokeWidth="0.8" />
          <ellipse cx="-4" cy="0" rx="1.8" ry="2.8" fill="#facc15" />
          <circle cx="-4" cy="0" r="0.8" fill="#451a03" />
        </g>
        {/* Top Tier: Log 5 (Crown) */}
        <g transform="translate(0, -11)">
          <rect x="-4" y="-4.5" width="22" height="9" rx="3" fill="#78350f" stroke="#451a03" strokeWidth="1" />
          <line x1="-2" y1="-1" x2="16" y2="-1" stroke="#a16207" strokeWidth="1" />
          <ellipse cx="-4" cy="0" rx="3" ry="4.5" fill="#eab308" stroke="#451a03" strokeWidth="0.8" />
          <ellipse cx="-4" cy="0" rx="1.8" ry="2.8" fill="#fef08a" />
          <circle cx="-4" cy="0" r="0.8" fill="#451a03" />
        </g>
        {/* Thick Hemp Binding Rope */}
        <path d="M-10,6 C-12,-4 -7,-14 -1,-15 C5,-14 10,-4 8,6" stroke="#ca8a04" strokeWidth="1.8" strokeDasharray="3 1.5" fill="none" opacity="0.9" />
        <path d="M4,6 C2,-4 7,-14 13,-15 C19,-14 24,-4 22,6" stroke="#ca8a04" strokeWidth="1.8" strokeDasharray="3 1.5" fill="none" opacity="0.9" />
      </g>

      {/* Compact Olive Wood Logs Pile (Petit tas de bûches d'olivier) */}
      <g id="olive_logs_pile_small">
        <ellipse cx="0" cy="6" rx="16" ry="5.5" fill="#000" opacity="0.32" />
        <ellipse cx="-8" cy="6" rx="3" ry="1.2" fill="#ca8a04" opacity="0.75" />
        <ellipse cx="10" cy="7" rx="3" ry="1.2" fill="#eab308" opacity="0.75" />
        {/* Log 1 */}
        <g transform="translate(-7, 2)">
          <rect x="-3" y="-4" width="18" height="8" rx="2.5" fill="#78350f" stroke="#451a03" strokeWidth="0.8" />
          <ellipse cx="-3" cy="0" rx="2.5" ry="4" fill="#ca8a04" stroke="#451a03" strokeWidth="0.6" />
          <circle cx="-3" cy="0" r="0.8" fill="#451a03" />
        </g>
        {/* Log 2 */}
        <g transform="translate(5, 2)">
          <rect x="-3" y="-4" width="16" height="8" rx="2.5" fill="#854d0e" stroke="#451a03" strokeWidth="0.8" />
          <ellipse cx="-3" cy="0" rx="2.5" ry="4" fill="#d97706" stroke="#451a03" strokeWidth="0.6" />
          <circle cx="-3" cy="0" r="0.8" fill="#451a03" />
        </g>
        {/* Log 3 on top */}
        <g transform="translate(-1, -4)">
          <rect x="-3" y="-3.5" width="17" height="7" rx="2" fill="#92400e" stroke="#451a03" strokeWidth="0.8" />
          <ellipse cx="-3" cy="0" rx="2.2" ry="3.5" fill="#f59e0b" stroke="#451a03" strokeWidth="0.6" />
          <circle cx="-3" cy="0" r="0.7" fill="#451a03" />
        </g>
        {/* Rope */}
        <path d="M-5,4 C-7,-2 -3,-8 1,-8 C5,-8 9,-2 7,4" stroke="#ca8a04" strokeWidth="1.4" strokeDasharray="2 1" fill="none" />
      </g>

      {/* Grand Tapis Kabyle Traditionnel (Tapis berbère / Agellid n tzerzit) */}
      <g id="kabyle_carpet_large">
        {/* Ground shadow beneath rug */}
        <polygon points="-24,14 26,14 28,-12 -22,-12" fill="#000" opacity="0.32" />
        
        {/* Top/Bottom Wool Tassel Fringes (Franges en laine écrue) */}
        <path d="M-22,-12 L-24,-15 M-18,-12 L-19,-15 M-14,-12 L-15,-15 M-10,-12 L-11,-15 M-6,-12 L-7,-15 M-2,-12 L-3,-15 M2,-12 L1,-15 M6,-12 L5,-15 M10,-12 L9,-15 M14,-12 L13,-15 M18,-12 L17,-15 M22,-12 L21,-15 M26,-12 L25,-15" stroke="#fef08a" strokeWidth="1.2" strokeLinecap="round" />
        <path d="M-24,12 L-25,16 M-20,12 L-21,16 M-16,12 L-17,16 M-12,12 L-13,16 M-8,12 L-9,16 M-4,12 L-5,16 M0,12 L-1,16 M4,12 L3,16 M8,12 L7,16 M12,12 L11,16 M16,12 L15,16 M20,12 L19,16 M24,12 L23,16" stroke="#fef08a" strokeWidth="1.2" strokeLinecap="round" />

        {/* Main Wool Carpet Base (Fond écru / crème traditionnel) */}
        <polygon points="-23,12 25,12 27,-12 -21,-12" fill="#fefce8" stroke="#78350f" strokeWidth="1" />
        
        {/* Outer Red Madder Woven Border */}
        <polygon points="-21,10 23,10 25,-10 -19,-10" fill="#dc2626" />
        <polygon points="-19,8.5 21,8.5 23,-8.5 -17,-8.5" fill="#fef9c3" />

        {/* Traditional Kabyle Zigzag / Sawtooth Edges */}
        <path d="M -17 -7 L -15 -9 L -13 -7 L -11 -9 L -9 -7 L -7 -9 L -5 -7 L -3 -9 L -1 -7 L 1 -9 L 3 -7 L 5 -9 L 7 -7 L 9 -9 L 11 -7 L 13 -9 L 15 -7 L 17 -9 L 19 -7 L 21 -9" stroke="#1e293b" strokeWidth="0.9" fill="none" />
        <path d="M -19 7 L -17 9 L -15 7 L -13 9 L -11 7 L -9 9 L -7 7 L -5 9 L -3 7 L -1 9 L 1 7 L 3 9 L 5 7 L 7 9 L 9 7 L 11 9 L 13 7 L 15 9 L 17 7 L 19 9 L 21 7" stroke="#1e293b" strokeWidth="0.9" fill="none" />

        {/* Side Diamond Medallions (Losanges kabyles - Tazerzayt) */}
        {/* Left Diamond */}
        <polygon points="-11,0 -6,-6 -1,0 -6,6" fill="#ea580c" stroke="#18181b" strokeWidth="0.8" />
        <polygon points="-8,0 -6,-3 -4,0 -6,3" fill="#facc15" />
        <circle cx="-6" cy="0" r="1.1" fill="#15803d" />

        {/* Center Grand Yaz Medallion */}
        <polygon points="1,0 6,-7 11,0 6,7" fill="#dc2626" stroke="#18181b" strokeWidth="0.8" />
        <polygon points="3,0 6,-4 9,0 6,4" fill="#fef08a" />
        <text x="6" y="3" fill="#1e293b" fontSize="5.5" fontWeight="black" textAnchor="middle">ⵣ</text>

        {/* Right Diamond */}
        <polygon points="13,0 18,-6 23,0 18,6" fill="#16a34a" stroke="#18181b" strokeWidth="0.8" />
        <polygon points="16,0 18,-3 20,0 18,3" fill="#facc15" />
        <circle cx="18" cy="0" r="1.1" fill="#dc2626" />

        {/* Corner protective chevron markers (Aɣyul / Tiṭṭ n tsekurt) */}
        <polygon points="-18,-7 -14,-7 -18,-3" fill="#eab308" />
        <polygon points="22,-7 18,-7 22,-3" fill="#eab308" />
        <polygon points="-20,7 -16,7 -20,3" fill="#eab308" />
        <polygon points="20,7 16,7 20,3" fill="#eab308" />
      </g>

      {/* Tapis Berbère Allongé / Runner Kabyle */}
      <g id="kabyle_carpet_runner">
        <polygon points="-12,20 16,20 18,-18 -10,-18" fill="#000" opacity="0.3" />
        {/* Tassels */}
        <path d="M-10,-18 L-11,-21 M-6,-18 L-7,-21 M-2,-18 L-3,-21 M2,-18 L1,-21 M6,-18 L5,-21 M10,-18 L9,-21 M14,-18 L13,-21" stroke="#fef08a" strokeWidth="1.2" />
        <path d="M-12,18 L-13,22 M-8,18 L-9,22 M-4,18 L-5,22 M0,18 L-1,22 M4,18 L3,22 M8,18 L7,22 M12,18 L11,22" stroke="#fef08a" strokeWidth="1.2" />
        
        {/* Base */}
        <polygon points="-11,18 15,18 17,-18 -9,-18" fill="#fefce8" stroke="#451a03" strokeWidth="0.9" />
        <polygon points="-9.5,16.5 13.5,16.5 15.5,-16.5 -7.5,-16.5" fill="#b91c1c" />
        <polygon points="-8,15 12,15 14,-15 -6,-15" fill="#fef3c7" />

        {/* Vertical sequence of Berber Diamond Motifs */}
        {/* Top Diamond */}
        <polygon points="4,-10 8,-5 4,0 0,-5" fill="#eab308" stroke="#1c1917" strokeWidth="0.7" />
        <polygon points="4,-8 6,-5 4,-2 2,-5" fill="#15803d" />
        
        {/* Center Diamond with Yaz */}
        <polygon points="3,-1 7,4 3,9 -1,4" fill="#dc2626" stroke="#1c1917" strokeWidth="0.7" />
        <polygon points="3,1 5,4 3,7 1,4" fill="#fef08a" />
        <text x="3" y="6" fill="#18181b" fontSize="4.5" fontWeight="bold" textAnchor="middle">ⵣ</text>

        {/* Bottom Diamond */}
        <polygon points="2,8 6,13 2,18 -2,13" fill="#ea580c" stroke="#1c1917" strokeWidth="0.7" />
        <polygon points="2,10 4,13 2,16 0,13" fill="#2563eb" />
      </g>

      {/* --- ÂNE KABYLE AVEC TAPIS TRADITIONNEL (Aɣyul aqbayli s tzerzit) --- */}
      <g id="kabyle_donkey_standing">
        {/* Ground shadow */}
        <ellipse cx="0" cy="14" rx="20" ry="6.5" fill="#000" opacity="0.35" />

        {/* Tail */}
        <path d="M-13,0 C-17,4 -18,10 -16,13" stroke="#475569" strokeWidth="2.2" strokeLinecap="round" fill="none" />
        <ellipse cx="-16" cy="13.5" rx="2.5" ry="3.5" fill="#1e293b" />

        {/* Back legs (shadowed/far) */}
        <line x1="-8" y1="4" x2="-8" y2="13.5" stroke="#475569" strokeWidth="3" strokeLinecap="round" />
        <rect x="-9.5" y="12" width="3" height="2" rx="0.5" fill="#1e293b" />
        <line x1="8" y1="4" x2="7" y2="13.5" stroke="#475569" strokeWidth="3" strokeLinecap="round" />
        <rect x="5.5" y="12" width="3" height="2" rx="0.5" fill="#1e293b" />

        {/* Main Body */}
        <ellipse cx="-1" cy="0" rx="14" ry="8.5" fill="#64748b" />
        <ellipse cx="-1" cy="2" rx="13" ry="6.5" fill="#94a3b8" opacity="0.4" />

        {/* Front near legs */}
        <line x1="-10" y1="2" x2="-10" y2="14" stroke="#64748b" strokeWidth="3.2" strokeLinecap="round" />
        <rect x="-11.8" y="12.5" width="3.5" height="2.2" rx="0.5" fill="#1e293b" />
        <line x1="5" y1="2" x2="5" y2="14" stroke="#64748b" strokeWidth="3.2" strokeLinecap="round" />
        <rect x="3.2" y="12.5" width="3.5" height="2.2" rx="0.5" fill="#1e293b" />

        {/* Wooden Pack Saddle base (Lberda / Barda) */}
        <polygon points="-7,-4 5,-4 7,-8 -9,-8" fill="#78350f" stroke="#451a03" strokeWidth="0.8" />
        <line x1="-1" y1="-8" x2="-1" y2="4" stroke="#451a03" strokeWidth="1.2" />

        {/* --- TRADITIONAL KABYLE WOVEN CARPET ON DONKEY'S BACK --- */}
        {/* Carpet hanging flap on donkey's flank */}
        <polygon points="-9,-6 6,-6 8,5 -10,5" fill="#dc2626" stroke="#451a03" strokeWidth="0.8" />
        {/* Golden inner border */}
        <polygon points="-8,-4 5,-4 6.5,3.5 -8.5,3.5" fill="#fef08a" />
        {/* Red core */}
        <polygon points="-7,-3 4,-3 5.5,2 -7.5,2" fill="#b91c1c" />
        {/* Berber Diamond (Tazerzayt) in center */}
        <polygon points="-1,-0.5 1.5,-3 4,-0.5 1.5,2" fill="#ea580c" stroke="#1e293b" strokeWidth="0.5" />
        <polygon points="-6,-0.5 -3.5,-3 -1,-0.5 -3.5,2" fill="#15803d" stroke="#1e293b" strokeWidth="0.5" />
        {/* Yaz ⵣ mark on carpet */}
        <text x="-1" y="1.2" fill="#fef08a" fontSize="3" fontWeight="900" textAnchor="middle">ⵣ</text>
        {/* Wool tassel fringes along bottom edge of rug */}
        <path d="M-10,5 L-10.5,8 M-8,5 L-8.5,8 M-6,5 L-6.5,8 M-4,5 L-4.5,8 M-2,5 L-2.5,8 M0,5 L-0.5,8 M2,5 L1.5,8 M4,5 L3.5,8 M6,5 L5.5,8 M8,5 L7.5,8" stroke="#fef08a" strokeWidth="0.9" strokeLinecap="round" />
        {/* Leather Harness strap around donkey chest */}
        <path d="M5,-2 C8,-1 11,2 10,6" stroke="#451a03" strokeWidth="1.2" fill="none" />
        <circle cx="10" cy="3" r="1" fill="#facc15" />

        {/* Neck & Mane */}
        <path d="M7,-3 L13,-12 L18,-10 L12,2 Z" fill="#64748b" />
        <path d="M8,-5 L13,-14 L14,-12 L9,-2 Z" fill="#334155" />

        {/* Donkey Head */}
        <ellipse cx="17" cy="-11" rx="5.5" ry="4" fill="#64748b" transform="rotate(-15, 17, -11)" />
        {/* White Snout / Muzzle */}
        <ellipse cx="20.5" cy="-8.5" rx="3.5" ry="2.8" fill="#f1f5f9" transform="rotate(-15, 20.5, -8.5)" />
        <circle cx="21.5" cy="-9" r="0.7" fill="#1e293b" />
        <path d="M20,-7.2 Q21,-6.5 22,-7.5" stroke="#475569" strokeWidth="0.6" fill="none" />

        {/* Eye */}
        <circle cx="16" cy="-12.5" r="1.5" fill="#f8fafc" />
        <circle cx="16.2" cy="-12.5" r="1" fill="#0f172a" />
        <circle cx="16.4" cy="-12.8" r="0.4" fill="#ffffff" />

        {/* Big Alert Donkey Ears */}
        {/* Far ear */}
        <g transform="translate(13.5, -13) rotate(-22)">
          <path d="M0,0 Q-2,-10 0,-14 Q2,-10 0,0 Z" fill="#475569" />
          <path d="M0,-2 Q-1,-8 0,-11 Q1,-8 0,-2 Z" fill="#fda4af" opacity="0.8" />
        </g>
        {/* Near ear */}
        <g transform="translate(15.5, -14) rotate(-8)">
          <path d="M0,0 Q-2.5,-11 0,-15 Q2.5,-11 0,0 Z" fill="#64748b" />
          <path d="M0,-2 Q-1.5,-9 0,-12 Q1.5,-9 0,-2 Z" fill="#f472b6" opacity="0.8" />
        </g>

        {/* Halter / Bridle (Lgida n uɣyul) */}
        <path d="M14.5,-13 L19.5,-9 L16.5,-8" stroke="#ca8a04" strokeWidth="0.8" fill="none" />
      </g>

      {/* Âne Kabyle Paisible (Variant 2 : Âne tourné avec tapis et couffin en osier) */}
      <g id="kabyle_donkey_resting">
        <ellipse cx="0" cy="13" rx="18" ry="6" fill="#000" opacity="0.32" />
        {/* Tail */}
        <path d="M12,0 C16,4 17,9 15,12" stroke="#475569" strokeWidth="2" strokeLinecap="round" fill="none" />
        <ellipse cx="15" cy="12.5" rx="2.2" ry="3" fill="#1e293b" />
        {/* Legs */}
        <line x1="7" y1="3" x2="7" y2="12.5" stroke="#475569" strokeWidth="2.8" strokeLinecap="round" />
        <line x1="-7" y1="3" x2="-6" y2="12.5" stroke="#475569" strokeWidth="2.8" strokeLinecap="round" />
        <line x1="9" y1="2" x2="9" y2="13" stroke="#64748b" strokeWidth="3" strokeLinecap="round" />
        <line x1="-5" y1="2" x2="-5" y2="13" stroke="#64748b" strokeWidth="3" strokeLinecap="round" />
        {/* Body */}
        <ellipse cx="1" cy="0" rx="13" ry="8" fill="#64748b" />
        <ellipse cx="1" cy="2" rx="11" ry="6" fill="#cbd5e1" opacity="0.35" />
        {/* Carpet / Couverture berbère bleue & jaune */}
        <polygon points="-5,-5 7,-5 8,4 -6,4" fill="#0284c7" stroke="#451a03" strokeWidth="0.8" />
        <polygon points="-4,-3.5 6,-3.5 7,2.5 -5,2.5" fill="#facc15" />
        <polygon points="-2.5,-0.5 0.5,-2.5 3.5,-0.5 0.5,1.5" fill="#dc2626" />
        <path d="M-6,4 L-6.5,7 M-4,4 L-4.5,7 M-2,4 L-2.5,7 M0,4 L-0.5,7 M2,4 L1.5,7 M4,4 L3.5,7 M6,4 L5.5,7 M8,4 L7.5,7" stroke="#fef08a" strokeWidth="0.8" strokeLinecap="round" />
        {/* Small Wicker Saddlebag (Aqecwal) */}
        <rect x="-4" y="-1" width="7" height="6" rx="1.5" fill="#a16207" stroke="#78350f" strokeWidth="0.7" />
        <line x1="-4" y1="1" x2="3" y2="1" stroke="#78350f" strokeWidth="0.6" />
        <line x1="-4" y1="3" x2="3" y2="3" stroke="#78350f" strokeWidth="0.6" />
        {/* Neck & Head grazing downwards */}
        <path d="M-6,-2 L-13,-5 L-16,3 L-9,3 Z" fill="#64748b" />
        <ellipse cx="-16" cy="2" rx="5" ry="3.8" fill="#64748b" transform="rotate(25, -16, 2)" />
        <ellipse cx="-19" cy="4.5" rx="3.2" ry="2.5" fill="#f1f5f9" transform="rotate(25, -19, 4.5)" />
        <circle cx="-20" cy="4" r="0.6" fill="#1e293b" />
        <circle cx="-15" cy="0.5" r="1.2" fill="#0f172a" />
        <circle cx="-14.8" cy="0.3" r="0.4" fill="#ffffff" />
        {/* Ears */}
        <g transform="translate(-13, -2) rotate(40)">
          <path d="M0,0 Q-2,-9 0,-13 Q2,-9 0,0 Z" fill="#64748b" />
          <path d="M0,-2 Q-1,-7 0,-10 Q1,-7 0,-2 Z" fill="#fda4af" opacity="0.8" />
        </g>
      </g>

      {/* --- CACTUS DE FIGUES DE BARBARIE (Akermus / Tahendit / Ahendiṭ) --- */}
      <g id="prickly_pear_cactus_large">
        {/* Ground shadow & sandy mound */}
        <ellipse cx="0" cy="10" rx="22" ry="6.5" fill="#000" opacity="0.32" />
        <ellipse cx="-2" cy="9" rx="18" ry="4" fill="#78350f" opacity="0.3" />
        {/* Base desert stones */}
        <ellipse cx="-14" cy="8" rx="4" ry="2" fill="#78716c" stroke="#44403c" strokeWidth="0.7" />
        <ellipse cx="13" cy="9" rx="4.5" ry="2.2" fill="#a8a29e" stroke="#57534e" strokeWidth="0.7" />
        <ellipse cx="5" cy="11" rx="3" ry="1.5" fill="#78716c" />

        {/* Trunk / Base Woody Pad */}
        <ellipse cx="0" cy="4" rx="8" ry="6" fill="#14532d" stroke="#052e16" strokeWidth="0.9" />
        <line x1="-4" y1="5" x2="4" y2="5" stroke="#166534" strokeWidth="0.8" />

        {/* --- LOWER TIER PADS --- */}
        {/* Left Lower Pad */}
        <g transform="translate(-10, -2) rotate(-24)">
          <ellipse cx="0" cy="0" rx="8.5" ry="12" fill="#15803d" stroke="#14532d" strokeWidth="0.9" />
          <ellipse cx="-1" cy="0" rx="6.5" ry="9.5" fill="#16a34a" opacity="0.5" />
          {/* Spines / Areoles */}
          <circle cx="-3" cy="-4" r="0.7" fill="#fef08a" /><line x1="-3" y1="-4" x2="-5" y2="-6" stroke="#ca8a04" strokeWidth="0.6" />
          <circle cx="3" cy="-2" r="0.7" fill="#fef08a" /><line x1="3" y1="-2" x2="5" y2="-4" stroke="#ca8a04" strokeWidth="0.6" />
          <circle cx="-2" cy="4" r="0.7" fill="#fef08a" />
          <circle cx="2" cy="5" r="0.7" fill="#fef08a" />
        </g>

        {/* Right Lower Pad */}
        <g transform="translate(10, -2) rotate(22)">
          <ellipse cx="0" cy="0" rx="8.5" ry="12" fill="#15803d" stroke="#14532d" strokeWidth="0.9" />
          <ellipse cx="1" cy="0" rx="6.5" ry="9.5" fill="#16a34a" opacity="0.5" />
          <circle cx="-3" cy="-3" r="0.7" fill="#fef08a" />
          <circle cx="3" cy="-4" r="0.7" fill="#fef08a" /><line x1="3" y1="-4" x2="5" y2="-6" stroke="#ca8a04" strokeWidth="0.6" />
          <circle cx="0" cy="3" r="0.7" fill="#fef08a" />
        </g>

        {/* Center Main Pad */}
        <g transform="translate(0, -9)">
          <ellipse cx="0" cy="0" rx="9" ry="13" fill="#16a34a" stroke="#14532d" strokeWidth="0.9" />
          <ellipse cx="0" cy="0" rx="7" ry="10.5" fill="#22c55e" opacity="0.45" />
          <circle cx="-4" cy="-5" r="0.7" fill="#fef08a" /><line x1="-4" y1="-5" x2="-6" y2="-7" stroke="#ca8a04" strokeWidth="0.6" />
          <circle cx="4" cy="-5" r="0.7" fill="#fef08a" /><line x1="4" y1="-5" x2="6" y2="-7" stroke="#ca8a04" strokeWidth="0.6" />
          <circle cx="-3" cy="2" r="0.7" fill="#fef08a" />
          <circle cx="3" cy="2" r="0.7" fill="#fef08a" />
          <circle cx="0" cy="-1" r="0.7" fill="#fef08a" />
        </g>

        {/* --- UPPER TIER PADS --- */}
        {/* Upper Left Pad */}
        <g transform="translate(-13, -19) rotate(-35)">
          <ellipse cx="0" cy="0" rx="7" ry="10" fill="#22c55e" stroke="#15803d" strokeWidth="0.9" />
          <circle cx="-2" cy="-3" r="0.6" fill="#fef08a" /><line x1="-2" y1="-3" x2="-4" y2="-5" stroke="#ca8a04" strokeWidth="0.5" />
          <circle cx="2" cy="-2" r="0.6" fill="#fef08a" />
          <circle cx="0" cy="3" r="0.6" fill="#fef08a" />
          {/* Prickly pear fruit (Akermus orange) on top */}
          <g transform="translate(-4, -9.5) rotate(-15)">
            <ellipse cx="0" cy="0" rx="2.5" ry="3.8" fill="#ea580c" stroke="#9a3412" strokeWidth="0.6" />
            <ellipse cx="0" cy="0" rx="1.5" ry="2.6" fill="#f97316" />
            {/* Fruit crown / flower scar */}
            <circle cx="0" cy="-3.5" r="1.1" fill="#7c2d12" />
            <circle cx="0" cy="-3.5" r="0.6" fill="#facc15" />
          </g>
          {/* Prickly pear fruit (Akermus ruby red) */}
          <g transform="translate(3, -9) rotate(20)">
            <ellipse cx="0" cy="0" rx="2.3" ry="3.5" fill="#dc2626" stroke="#991b1b" strokeWidth="0.6" />
            <ellipse cx="0" cy="0" rx="1.4" ry="2.4" fill="#ef4444" />
            <circle cx="0" cy="-3.2" r="1" fill="#7f1d1d" />
          </g>
        </g>

        {/* Upper Center Crown Pad */}
        <g transform="translate(1, -26) rotate(6)">
          <ellipse cx="0" cy="0" rx="7.5" ry="10.5" fill="#22c55e" stroke="#15803d" strokeWidth="0.9" />
          <ellipse cx="0" cy="0" rx="5.5" ry="8" fill="#4ade80" opacity="0.45" />
          <circle cx="-3" cy="-3" r="0.6" fill="#fef08a" /><line x1="-3" y1="-3" x2="-5" y2="-5" stroke="#ca8a04" strokeWidth="0.5" />
          <circle cx="3" cy="-3" r="0.6" fill="#fef08a" /><line x1="3" y1="-3" x2="5" y2="-5" stroke="#ca8a04" strokeWidth="0.5" />
          <circle cx="0" cy="2" r="0.6" fill="#fef08a" />
          
          {/* 3 Ripe Prickly Pear Fruits on crown */}
          {/* Fruit 1 (Golden Yellow - Ahendiṭ awraɣ) */}
          <g transform="translate(-4.5, -9.5) rotate(-20)">
            <ellipse cx="0" cy="0" rx="2.4" ry="3.6" fill="#eab308" stroke="#a16207" strokeWidth="0.6" />
            <ellipse cx="0" cy="0" rx="1.4" ry="2.5" fill="#fde047" />
            <circle cx="0" cy="-3.4" r="1" fill="#854d0e" />
          </g>
          {/* Fruit 2 (Vivid Orange - Ahendiṭ ačinawi) */}
          <g transform="translate(0, -10.5)">
            <ellipse cx="0" cy="0" rx="2.5" ry="3.8" fill="#ea580c" stroke="#9a3412" strokeWidth="0.6" />
            <ellipse cx="0" cy="0" rx="1.5" ry="2.6" fill="#fb923c" />
            <circle cx="0" cy="-3.5" r="1.1" fill="#7c2d12" />
          </g>
          {/* Fruit 3 (Deep Ruby - Ahendiṭ azeggwaɣ) */}
          <g transform="translate(4.5, -9.5) rotate(20)">
            <ellipse cx="0" cy="0" rx="2.4" ry="3.6" fill="#dc2626" stroke="#991b1b" strokeWidth="0.6" />
            <ellipse cx="0" cy="0" rx="1.4" ry="2.5" fill="#f87171" />
            <circle cx="0" cy="-3.4" r="1" fill="#7f1d1d" />
          </g>
        </g>

        {/* Upper Right Pad */}
        <g transform="translate(14, -18) rotate(32)">
          <ellipse cx="0" cy="0" rx="7" ry="10" fill="#22c55e" stroke="#15803d" strokeWidth="0.9" />
          <circle cx="-2" cy="-2" r="0.6" fill="#fef08a" />
          <circle cx="2" cy="-3" r="0.6" fill="#fef08a" /><line x1="2" y1="-3" x2="4" y2="-5" stroke="#ca8a04" strokeWidth="0.5" />
          <circle cx="0" cy="3" r="0.6" fill="#fef08a" />
          {/* Prickly pear fruits */}
          <g transform="translate(-2, -9.5) rotate(-10)">
            <ellipse cx="0" cy="0" rx="2.4" ry="3.6" fill="#eab308" stroke="#a16207" strokeWidth="0.6" />
            <ellipse cx="0" cy="0" rx="1.4" ry="2.4" fill="#fef08a" />
            <circle cx="0" cy="-3.3" r="1" fill="#854d0e" />
          </g>
          <g transform="translate(4, -8.5) rotate(25)">
            <ellipse cx="0" cy="0" rx="2.3" ry="3.5" fill="#ea580c" stroke="#9a3412" strokeWidth="0.6" />
            <ellipse cx="0" cy="0" rx="1.3" ry="2.3" fill="#f97316" />
            <circle cx="0" cy="-3.2" r="1" fill="#7c2d12" />
          </g>
        </g>
      </g>

      {/* Compact / Tuft Cactus Clump (Petit massif de figuiers de barbarie) */}
      <g id="prickly_pear_cactus_cluster">
        <ellipse cx="0" cy="7" rx="16" ry="5" fill="#000" opacity="0.3" />
        <ellipse cx="-8" cy="6.5" rx="3" ry="1.5" fill="#78716c" />
        {/* Pad 1 Left */}
        <g transform="translate(-6, 0) rotate(-18)">
          <ellipse cx="0" cy="0" rx="6.5" ry="9" fill="#15803d" stroke="#14532d" strokeWidth="0.8" />
          <circle cx="-1" cy="-2" r="0.5" fill="#fef08a" />
          <circle cx="2" cy="1" r="0.5" fill="#fef08a" />
          {/* Fruit */}
          <g transform="translate(-2, -8.5)">
            <ellipse cx="0" cy="0" rx="2" ry="3" fill="#ea580c" stroke="#7c2d12" strokeWidth="0.5" />
            <circle cx="0" cy="-2.7" r="0.8" fill="#451a03" />
          </g>
        </g>
        {/* Pad 2 Right */}
        <g transform="translate(6, 0) rotate(16)">
          <ellipse cx="0" cy="0" rx="6.5" ry="9" fill="#15803d" stroke="#14532d" strokeWidth="0.8" />
          <circle cx="1" cy="-2" r="0.5" fill="#fef08a" />
          {/* Fruit */}
          <g transform="translate(2, -8.5)">
            <ellipse cx="0" cy="0" rx="2" ry="3" fill="#dc2626" stroke="#7f1d1d" strokeWidth="0.5" />
            <circle cx="0" cy="-2.7" r="0.8" fill="#450a0a" />
          </g>
        </g>
        {/* Top center pad */}
        <g transform="translate(0, -7)">
          <ellipse cx="0" cy="0" rx="6" ry="8.5" fill="#22c55e" stroke="#15803d" strokeWidth="0.8" />
          <circle cx="-2" cy="-1" r="0.5" fill="#fef08a" />
          <circle cx="2" cy="-1" r="0.5" fill="#fef08a" />
          {/* Top fruits */}
          <g transform="translate(-2.5, -8)">
            <ellipse cx="0" cy="0" rx="1.9" ry="2.8" fill="#eab308" stroke="#78350f" strokeWidth="0.5" />
          </g>
          <g transform="translate(2.5, -8)">
            <ellipse cx="0" cy="0" rx="1.9" ry="2.8" fill="#f97316" stroke="#7c2d12" strokeWidth="0.5" />
          </g>
        </g>
      </g>

      {/* --- FEMMES DU GROUPE DJURDJURA EN COSTUME TRADITIONNEL KABYLE (Tilawin n Ǧerǧer) --- */}
      {/* Trio / Duo de chanteuses Kabyles du Djurdjura avec robes traditionnelles (Tiqendyar), Fouta, bijoux en argent et Bendir */}
      <g id="djurdjura_singers_group">
        {/* Ground shadow beneath group */}
        <ellipse cx="0" cy="14" rx="28" ry="7" fill="#000" opacity="0.38" />

        {/* --- SINGER 1 (Left): Traditional Yellow/Gold Kabyle Dress with Red Fouta --- */}
        <g transform="translate(-14, 0)">
          {/* Robe Kabyle (Taqendurt) Base - Bright Saffron Yellow */}
          <polygon points="-8,12 8,12 5,-4 -5,-4" fill="#eab308" stroke="#854d0e" strokeWidth="0.8" />
          
          {/* Fouta / Lfouta (Striped Berber Silk/Wool apron wrapped around waist) */}
          <polygon points="-7,1 7,1 8,11 -8,11" fill="#dc2626" />
          {/* Striped weave of the Fouta */}
          <line x1="-7.5" y1="3" x2="7.5" y2="3" stroke="#facc15" strokeWidth="1" />
          <line x1="-7.7" y1="5.5" x2="7.7" y2="5.5" stroke="#18181b" strokeWidth="0.8" />
          <line x1="-7.8" y1="8" x2="7.8" y2="8" stroke="#f97316" strokeWidth="1" />
          <line x1="-8" y1="10.5" x2="8" y2="10.5" stroke="#facc15" strokeWidth="0.8" />
          
          {/* Embroidered Braid Trim (Leḥwaci / Zigzag patterns on collar & skirt) */}
          <path d="M-6,12 L-4,10 L-2,12 L0,10 L2,12 L4,10 L6,12" stroke="#15803d" strokeWidth="0.8" fill="none" />
          <path d="M-4,-4 L0,2 L4,-4" stroke="#dc2626" strokeWidth="1.2" fill="none" />

          {/* Silver Berber Fibula / Brooch (Tabzimt n lfeṭṭa avec corail rouge) */}
          <polygon points="0,-1 2.2,1.5 0,4 -2.2,1.5" fill="#e2e8f0" stroke="#475569" strokeWidth="0.5" />
          <circle cx="0" cy="1.5" r="0.8" fill="#dc2626" />

          {/* Arms & Hands holding Bendir frame drum */}
          <path d="M-5,-2 C-9,2 -8,7 -4,7" stroke="#fdba74" strokeWidth="2.2" strokeLinecap="round" fill="none" />
          <path d="M5,-2 C8,2 7,6 3,7" stroke="#fdba74" strokeWidth="2.2" strokeLinecap="round" fill="none" />
          
          {/* Traditional Kabyle Bendir (Allun / Tambour sur cadre en peau avec motif Yaz) */}
          <circle cx="-1" cy="7" r="5.5" fill="#fef3c7" stroke="#78350f" strokeWidth="1" />
          <circle cx="-1" cy="7" r="4.2" fill="#fffbeb" stroke="#b45309" strokeWidth="0.4" />
          <text x="-1" y="8.8" fill="#b91c1c" fontSize="4" fontWeight="bold" textAnchor="middle">ⵣ</text>
          {/* Silver bangles on wrists */}
          <circle cx="-4" cy="7" r="1.1" fill="#e2e8f0" />
          <circle cx="3" cy="7" r="1.1" fill="#e2e8f0" />

          {/* Neck & Head */}
          <circle cx="0" cy="-7.5" r="3.6" fill="#fed7aa" />
          {/* Facial features (eyes, serene smile, cheek accent) */}
          <circle cx="-1.2" cy="-8" r="0.5" fill="#1e293b" />
          <circle cx="1.2" cy="-8" r="0.5" fill="#1e293b" />
          <path d="M-1,-6.5 Q0,-5.8 1,-6.5" stroke="#991b1b" strokeWidth="0.5" fill="none" />
          {/* Berber forehead tattoo / silver diadem accent (Ta'essabt) */}
          <circle cx="0" cy="-10.2" r="0.6" fill="#0284c7" />

          {/* Black hair braids & Traditional Headscarf (Mendil / Amendil à franges dorées) */}
          <path d="M-3.5,-6 C-5,-4 -4,3 -3,5" stroke="#0f172a" strokeWidth="1.8" strokeLinecap="round" fill="none" />
          <path d="M3.5,-6 C5,-4 4,3 3,5" stroke="#0f172a" strokeWidth="1.8" strokeLinecap="round" fill="none" />
          {/* Silk Mendil scarf draping on shoulders */}
          <path d="M-4,-7 Q0,-12 4,-7 Q5,-3 3,0 Q0,-4 -3,0 Q-5,-3 -4,-7 Z" fill="#dc2626" />
          <circle cx="0" cy="-11" r="1.2" fill="#facc15" />
          <path d="M-4,-7 L-5,-5 M-3,-8 L-4,-6 M3,-8 L4,-6 M4,-7 L5,-5" stroke="#fef08a" strokeWidth="0.8" />
        </g>

        {/* --- SINGER 2 (Center - Lead Soloist): Royal Blue & Vermillion Kabyle Dress --- */}
        <g transform="translate(0, -2)">
          {/* Main Robe - Saphir Blue */}
          <polygon points="-9,14 9,14 6,-4 -6,-4" fill="#2563eb" stroke="#1d4ed8" strokeWidth="0.8" />
          
          {/* Fouta (Bright Yellow & Red Striped) */}
          <polygon points="-8,2 8,2 9,13 -9,13" fill="#ea580c" />
          <line x1="-8.5" y1="4.5" x2="8.5" y2="4.5" stroke="#facc15" strokeWidth="1.2" />
          <line x1="-8.7" y1="7.5" x2="8.7" y2="7.5" stroke="#dc2626" strokeWidth="1.2" />
          <line x1="-8.9" y1="10.5" x2="8.9" y2="10.5" stroke="#18181b" strokeWidth="0.8" />
          
          {/* Colorful Chevron Braid Trim along Hem */}
          <path d="M-7,14 L-5,12 L-3,14 L-1,12 L1,14 L3,12 L5,14 L7,12" stroke="#facc15" strokeWidth="1" fill="none" />
          <path d="M-5,-4 L0,3 L5,-4" stroke="#facc15" strokeWidth="1.4" fill="none" />

          {/* Grand Silver Coral Brooch (Tabzimt n tzerzit) */}
          <polygon points="0,-1 3,2 0,5 -3,2" fill="#f1f5f9" stroke="#334155" strokeWidth="0.6" />
          <circle cx="0" cy="2" r="1" fill="#dc2626" />
          <circle cx="0" cy="2" r="0.4" fill="#38bdf8" />

          {/* Raised Singing Arms & Expressive Gesture */}
          <path d="M-6,-2 C-11,-1 -11,4 -7,5" stroke="#fed7aa" strokeWidth="2.4" strokeLinecap="round" fill="none" />
          <path d="M6,-2 C11,-1 11,4 7,5" stroke="#fed7aa" strokeWidth="2.4" strokeLinecap="round" fill="none" />
          {/* Traditional Silver Cuffs (Isebla) on wrists */}
          <rect x="-8.5" y="3.5" width="2.5" height="2" rx="0.5" fill="#f8fafc" stroke="#64748b" strokeWidth="0.4" />
          <rect x="6" y="3.5" width="2.5" height="2" rx="0.5" fill="#f8fafc" stroke="#64748b" strokeWidth="0.4" />

          {/* Head & Silver Berber Diadem (Ta'essabt / Frontalier d'argent et émaux) */}
          <circle cx="0" cy="-8" r="4" fill="#fed7aa" />
          <circle cx="-1.4" cy="-8.5" r="0.6" fill="#0f172a" />
          <circle cx="1.4" cy="-8.5" r="0.6" fill="#0f172a" />
          {/* Singing open mouth */}
          <ellipse cx="0" cy="-6.5" rx="1.2" ry="0.8" fill="#991b1b" />

          {/* Headscarf (Mendil à motifs rouges et or) */}
          <path d="M-4.5,-7 Q0,-13 4.5,-7 Q6,-2 4,1 Q0,-4 -4,1 Q-6,-2 -4.5,-7 Z" fill="#b91c1c" />
          {/* Silver Diadem chain across forehead with coral drops */}
          <path d="M-3.5,-9.5 Q0,-8.5 3.5,-9.5" stroke="#facc15" strokeWidth="1" fill="none" />
          <circle cx="-2" cy="-9" r="0.5" fill="#dc2626" />
          <circle cx="0" cy="-8.5" r="0.7" fill="#38bdf8" />
          <circle cx="2" cy="-9" r="0.5" fill="#dc2626" />
          {/* Long dark braids flowing onto chest */}
          <path d="M-4,-6 C-6,-2 -5,4 -4,7" stroke="#0f172a" strokeWidth="2" strokeLinecap="round" fill="none" />
          <path d="M4,-6 C6,-2 5,4 4,7" stroke="#0f172a" strokeWidth="2" strokeLinecap="round" fill="none" />
        </g>

        {/* --- SINGER 3 (Right): Emerald Green & Gold Kabyle Dress with Bendir --- */}
        <g transform="translate(14, 0)">
          {/* Emerald Green Robe */}
          <polygon points="-8,12 8,12 5,-4 -5,-4" fill="#15803d" stroke="#14532d" strokeWidth="0.8" />
          
          {/* Fouta (Red & Gold) */}
          <polygon points="-7,1 7,1 8,11 -8,11" fill="#dc2626" />
          <line x1="-7.5" y1="3.5" x2="7.5" y2="3.5" stroke="#facc15" strokeWidth="1" />
          <line x1="-7.7" y1="6.5" x2="7.7" y2="6.5" stroke="#ea580c" strokeWidth="0.9" />
          <line x1="-7.9" y1="9.5" x2="7.9" y2="9.5" stroke="#facc15" strokeWidth="1" />

          {/* Braid trim */}
          <path d="M-6,12 L-4,10 L-2,12 L0,10 L2,12 L4,10 L6,12" stroke="#ea580c" strokeWidth="0.8" fill="none" />
          <path d="M-4,-4 L0,2 L4,-4" stroke="#facc15" strokeWidth="1.2" fill="none" />

          {/* Silver Fibula */}
          <polygon points="0,-1 2.2,1.5 0,4 -2.2,1.5" fill="#f1f5f9" stroke="#475569" strokeWidth="0.5" />
          <circle cx="0" cy="1.5" r="0.7" fill="#15803d" />

          {/* Arms holding Bendir percussion */}
          <path d="M-5,-2 C-8,2 -7,6 -3,7" stroke="#fdba74" strokeWidth="2.2" strokeLinecap="round" fill="none" />
          <path d="M5,-2 C9,2 8,7 4,7" stroke="#fdba74" strokeWidth="2.2" strokeLinecap="round" fill="none" />
          {/* Bendir drum */}
          <circle cx="1" cy="7" r="5.5" fill="#fef3c7" stroke="#78350f" strokeWidth="1" />
          <circle cx="1" cy="7" r="4.2" fill="#fffbeb" stroke="#b45309" strokeWidth="0.4" />
          <circle cx="1" cy="7" r="1.5" fill="#ea580c" />

          {/* Head & Mendil */}
          <circle cx="0" cy="-7.5" r="3.6" fill="#fed7aa" />
          <circle cx="-1.2" cy="-8" r="0.5" fill="#1e293b" />
          <circle cx="1.2" cy="-8" r="0.5" fill="#1e293b" />
          <path d="M-1,-6.5 Q0,-5.8 1,-6.5" stroke="#991b1b" strokeWidth="0.5" fill="none" />
          
          <path d="M-3.5,-6 C-5,-4 -4,3 -3,5" stroke="#0f172a" strokeWidth="1.8" strokeLinecap="round" fill="none" />
          <path d="M3.5,-6 C5,-4 4,3 3,5" stroke="#0f172a" strokeWidth="1.8" strokeLinecap="round" fill="none" />
          <path d="M-4,-7 Q0,-12 4,-7 Q5,-3 3,0 Q0,-4 -3,0 Q-5,-3 -4,-7 Z" fill="#eab308" />
          <circle cx="0" cy="-11" r="1.2" fill="#dc2626" />
        </g>
      </g>

      {/* Chanteuse Kabyle Solo avec grand Bendir (Chant traditionnel du Djurdjura) */}
      <g id="djurdjura_woman_solo">
        <ellipse cx="0" cy="14" rx="16" ry="5.5" fill="#000" opacity="0.35" />
        
        {/* Robe Kabyle Rouge & Or */}
        <polygon points="-8,13 8,13 5,-4 -5,-4" fill="#dc2626" stroke="#991b1b" strokeWidth="0.8" />
        {/* Fouta Jaune d'or aux rayures noires et bleues */}
        <polygon points="-7,1 7,1 8,12 -8,12" fill="#facc15" />
        <line x1="-7.5" y1="3.5" x2="7.5" y2="3.5" stroke="#1e293b" strokeWidth="1" />
        <line x1="-7.7" y1="6.5" x2="7.7" y2="6.5" stroke="#2563eb" strokeWidth="1.1" />
        <line x1="-7.9" y1="9.5" x2="7.9" y2="9.5" stroke="#dc2626" strokeWidth="1" />
        
        {/* Broderies / Chevrons */}
        <path d="M-6,13 L-4,11 L-2,13 L0,11 L2,13 L4,11 L6,13" stroke="#22c55e" strokeWidth="0.9" fill="none" />
        <path d="M-4,-4 L0,2 L4,-4" stroke="#facc15" strokeWidth="1.3" fill="none" />

        {/* Tabzimt en argent */}
        <polygon points="0,-1 2.5,1.5 0,4 -2.5,1.5" fill="#f8fafc" stroke="#334155" strokeWidth="0.5" />
        <circle cx="0" cy="1.5" r="0.8" fill="#dc2626" />

        {/* Grand Bendir joué avec dynamisme */}
        <path d="M-5,-2 C-9,1 -8,6 -4,7" stroke="#fed7aa" strokeWidth="2.2" strokeLinecap="round" fill="none" />
        <path d="M5,-2 C9,0 10,5 6,8" stroke="#fed7aa" strokeWidth="2.2" strokeLinecap="round" fill="none" />
        <circle cx="0" cy="7" r="6" fill="#fef3c7" stroke="#78350f" strokeWidth="1.1" />
        <circle cx="0" cy="7" r="4.8" fill="#fffbeb" stroke="#b45309" strokeWidth="0.5" />
        <text x="0" y="8.8" fill="#dc2626" fontSize="4.5" fontWeight="black" textAnchor="middle">ⵣ</text>
        <circle cx="-4" cy="7" r="1.1" fill="#e2e8f0" />
        <circle cx="6" cy="7" r="1.1" fill="#e2e8f0" />

        {/* Visage & Coiffe Mendil */}
        <circle cx="0" cy="-7.5" r="3.8" fill="#fed7aa" />
        <circle cx="-1.3" cy="-8" r="0.5" fill="#0f172a" />
        <circle cx="1.3" cy="-8" r="0.5" fill="#0f172a" />
        <ellipse cx="0" cy="-6.5" rx="1.1" ry="0.7" fill="#991b1b" />
        <path d="M-3.8,-6 C-5.5,-3 -4.5,4 -3.5,6" stroke="#0f172a" strokeWidth="1.9" strokeLinecap="round" fill="none" />
        <path d="M3.8,-6 C5.5,-3 4.5,4 3.5,6" stroke="#0f172a" strokeWidth="1.9" strokeLinecap="round" fill="none" />
        <path d="M-4.2,-7 Q0,-12.5 4.2,-7 Q5.5,-3 3.5,0 Q0,-4 -3.5,0 Q-5.5,-3 -4.2,-7 Z" fill="#2563eb" />
        <circle cx="0" cy="-11" r="1.3" fill="#facc15" />
      </g>

      {/* --- ÉTANGS GLACÉS DE HAUTE MONTAGNE (Agelmim n wedrar / Lacs gelés du Djurdjura) --- */}
      {/* Grand Étang Glacé avec banquise bleutée, failles cristallines et rochers enneigés */}
      <g id="glacial_frozen_pond_large">
        {/* Soft ground depth shadow */}
        <ellipse cx="0" cy="3" rx="36" ry="17" fill="#000" opacity="0.4" />
        
        {/* Outer Snowy Bank Shoreline */}
        <ellipse cx="0" cy="1" rx="34" ry="15.5" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="1" />
        
        {/* Deep Frozen Glacial Water Base */}
        <ellipse cx="0" cy="0" rx="30" ry="13" fill="#0284c7" stroke="#0369a1" strokeWidth="1.2" />
        
        {/* Translucent Cyan & Turquoise Solid Ice Surface */}
        <ellipse cx="0" cy="0" rx="28" ry="11.5" fill="#38bdf8" opacity="0.85" />
        <ellipse cx="-4" cy="-2" rx="24" ry="9" fill="#7dd3fc" opacity="0.75" />
        <ellipse cx="3" cy="1" rx="18" ry="6.5" fill="#bae6fd" opacity="0.8" />
        
        {/* Pure White Central Glacial Reflection */}
        <ellipse cx="-2" cy="-1" rx="12" ry="3.5" fill="#ffffff" opacity="0.65" />
        <ellipse cx="8" cy="2" rx="8" ry="2.2" fill="#ffffff" opacity="0.5" />

        {/* Crystalline Ice Fissures / Cracks in the Ice (Fissures de gel) */}
        <path d="M-18,-4 L-11,-1 L-6,-5 L0,-2 L7,-6 L14,-3 L20,-5" stroke="#ffffff" strokeWidth="0.9" fill="none" opacity="0.9" />
        <path d="M-11,-1 L-14,4 L-8,6 L-2,3 L4,6 L9,3" stroke="#ffffff" strokeWidth="0.7" fill="none" opacity="0.75" />
        <path d="M0,-2 L2,4 L6,2" stroke="#e0f2fe" strokeWidth="0.6" fill="none" />
        <path d="M14,-3 L16,3 L21,2" stroke="#e0f2fe" strokeWidth="0.6" fill="none" />

        {/* Shoreline Slate & Quartz Rocks with Snow Frosting */}
        {/* Top-Left Rock */}
        <g transform="translate(-24, -9)">
          <polygon points="-7,5 6,3 4,-5 -5,-3" fill="#475569" stroke="#334155" strokeWidth="0.7" />
          <path d="M-5,-3 Q0,-6 4,-5 Q2,-2 -5,-3 Z" fill="#ffffff" />
        </g>
        {/* Top-Right Rock */}
        <g transform="translate(22, -8)">
          <polygon points="-6,4 7,4 5,-6 -4,-4" fill="#334155" stroke="#1e293b" strokeWidth="0.7" />
          <path d="M-4,-4 Q1,-7 5,-6 Q3,-3 -4,-4 Z" fill="#ffffff" />
        </g>
        {/* Bottom-Left Slate Slab */}
        <g transform="translate(-20, 10)">
          <polygon points="-8,3 8,2 6,-3 -6,-3" fill="#64748b" stroke="#334155" strokeWidth="0.6" />
          <path d="M-6,-3 Q0,-5 6,-3 Q4,-1 -6,-3 Z" fill="#ffffff" />
        </g>
        {/* Bottom-Right Quartz Stone */}
        <g transform="translate(18, 10)">
          <polygon points="-5,3 6,2 4,-4 -4,-3" fill="#475569" stroke="#1e293b" strokeWidth="0.6" />
          <path d="M-4,-3 Q0,-5 4,-4 Q2,-1 -4,-3 Z" fill="#ffffff" />
        </g>

        {/* Subtle Sparkle Highlights on Ice */}
        <circle cx="-12" cy="-3" r="1.1" fill="#ffffff" />
        <circle cx="10" cy="-2" r="1" fill="#ffffff" />
        <circle cx="-2" cy="4" r="0.8" fill="#ffffff" />
        <circle cx="16" cy="3" r="0.9" fill="#ffffff" />
      </g>

      {/* Étang Glacé Compact (Petit tarn miroitant de crête) */}
      <g id="glacial_frozen_pond_compact">
        <ellipse cx="0" cy="2" rx="22" ry="11" fill="#000" opacity="0.35" />
        <ellipse cx="0" cy="1" rx="20" ry="10" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="0.8" />
        <ellipse cx="0" cy="0" rx="17" ry="8" fill="#0284c7" />
        <ellipse cx="0" cy="0" rx="15" ry="6.8" fill="#38bdf8" opacity="0.85" />
        <ellipse cx="-2" cy="-1" rx="11" ry="4.5" fill="#7dd3fc" opacity="0.8" />
        <ellipse cx="0" cy="-0.5" rx="7" ry="2" fill="#ffffff" opacity="0.7" />
        {/* Cracks */}
        <path d="M-10,-2 L-5,0 L0,-3 L6,-1 L11,-3" stroke="#ffffff" strokeWidth="0.7" fill="none" opacity="0.85" />
        <path d="M-5,0 L-3,3 L2,1 L5,3" stroke="#e0f2fe" strokeWidth="0.5" fill="none" opacity="0.7" />
        {/* Side rocks */}
        <polygon points="-16,-4 -12,-8 -9,-4" fill="#475569" stroke="#334155" strokeWidth="0.6" />
        <polygon points="12,-5 16,-8 18,-4" fill="#334155" stroke="#1e293b" strokeWidth="0.6" />
        <circle cx="-5" cy="-2" r="0.8" fill="#ffffff" />
        <circle cx="6" cy="1" r="0.7" fill="#ffffff" />
      </g>

      {/* Mountain snowy assets */}
      <g id="tree_2">
        <ellipse cx="0" cy="5" rx="14" ry="5" fill="#000" opacity="0.3" />
        <rect x="-3" y="-5" width="6" height="10" fill="#3f3f46" />
        <path d="M-20,0 L0,-20 L20,0 Z" fill="#0284c7" />
        <path d="M-15,-10 L0,-30 L15,-10 Z" fill="#0ea5e9" />
        <path d="M-10,-20 L0,-40 L10,-20 Z" fill="#38bdf8" />
        <path d="M-20,0 L-10,-10 L0,-5 L10,-10 L20,0 L15,-5 L0,0 L-15,-5 Z" fill="#f8fafc" />
        <path d="M-15,-10 L-5,-20 L0,-15 L5,-20 L15,-10 L10,-15 L0,-10 L-10,-15 Z" fill="#f1f5f9" />
        <path d="M-10,-20 L0,-40 L10,-20 L0,-25 Z" fill="#ffffff" />
      </g>
      {/* Level 2 Houses: Djurdjura (Refuges Alpins & Chalets Enneigés) */}
      {/* Variant 0: Grand Refuge Alpin du Djurdjura */}
      <g id="house_2_0">
        <polygon points="-38,16 48,16 52,-10 32,-10" fill="#000" opacity="0.35" />
        {/* Heavy mountain slate walls */}
        <rect x="-32" y="-10" width="64" height="26" fill="#475569" stroke="#1e293b" strokeWidth="1" />
        <line x1="-32" y1="-2" x2="32" y2="-2" stroke="#334155" strokeWidth="0.9" />
        <line x1="-32" y1="6" x2="32" y2="6" stroke="#334155" strokeWidth="0.9" />
        {/* Stone chimney with curling warm smoke */}
        <rect x="18" y="-28" width="6" height="18" fill="#334155" stroke="#1e293b" strokeWidth="0.8" />
        <path d="M21,-28 Q24,-34 20,-38 Q17,-42 22,-46" stroke="#e2e8f0" strokeWidth="1.6" fill="none" strokeLinecap="round" opacity="0.75" />
        {/* Pure Snow blanket on roof (sans triangle noir saillant) */}
        <path d="M-38,-10 Q0,-32 38,-10 Q28,-6 16,-9 Q0,-4 -16,-9 Q-28,-6 -38,-10 Z" fill="#ffffff" />
        <path d="M-36,-10 Q0,-30 36,-10" stroke="#e2e8f0" strokeWidth="1.2" fill="none" />
        {/* Icicles under eave */}
        <path d="M-28,-8 L-27,-3 L-26,-8 M-15,-7 L-14,0 L-13,-7 M12,-7 L13,-1 L14,-7 M28,-8 L29,-4 L30,-8" stroke="#bae6fd" strokeWidth="1.2" fill="none" />
        {/* Warm lit windows with wood grilles */}
        <rect x="-22" y="0" width="10" height="9" rx="1" fill="#fef08a" stroke="#78350f" strokeWidth="0.9" />
        <line x1="-17" y1="0" x2="-17" y2="9" stroke="#78350f" strokeWidth="0.8" />
        <line x1="-22" y1="4.5" x2="-12" y2="4.5" stroke="#78350f" strokeWidth="0.8" />
        {/* Heavy reinforced wooden door */}
        <rect x="-4" y="2" width="12" height="14" fill="#3b220c" stroke="#1c1917" strokeWidth="0.8" />
        <line x1="2" y1="2" x2="2" y2="16" stroke="#1c1917" strokeWidth="0.8" />
        <circle cx="0" cy="9" r="0.9" fill="#facc15" />
        {/* Firewood log stack beside door */}
        <rect x="12" y="9" width="12" height="7" fill="#78350f" stroke="#451a03" strokeWidth="0.7" />
        <circle cx="15" cy="12" r="1.3" fill="#ca8a04" />
        <circle cx="19" cy="12" r="1.3" fill="#a16207" />
      </g>
      <g id="house_2"><use href="#house_2_0" /></g>

      {/* Variant 1: Azib d'Altitude (Abri pastoral de berger sous congère de neige) */}
      <g id="house_2_1">
        <polygon points="-36,16 44,16 46,-6 24,-6" fill="#000" opacity="0.32" />
        {/* Low sturdy mountain stone hut */}
        <rect x="-30" y="-6" width="58" height="22" rx="2" fill="#334155" stroke="#1e293b" strokeWidth="1" />
        {/* Curved heavy snow thatch roof (sans triangle noir) */}
        <path d="M-34,-6 Q-2,-26 34,-6 Q26,-2 12,-4 Q-2,-1 -16,-4 Q-28,-2 -34,-6 Z" fill="#ffffff" />
        <path d="M-32,-6 Q-2,-22 32,-6" stroke="#cbd5e1" strokeWidth="1.2" fill="none" />
        {/* Icicles */}
        <line x1="-22" y1="-4" x2="-22" y2="2" stroke="#e0f2fe" strokeWidth="1.3" />
        <line x1="-8" y1="-3" x2="-8" y2="4" stroke="#e0f2fe" strokeWidth="1.3" />
        <line x1="18" y1="-3" x2="18" y2="3" stroke="#e0f2fe" strokeWidth="1.3" />
        {/* Low reinforced arched door */}
        <path d="M-6,16 L-6,5 A6,6 0 0 1 6,5 L6,16 Z" fill="#1c1917" stroke="#0c0a09" strokeWidth="0.8" />
        <path d="M-4,16 L-4,6 A4,4 0 0 1 4,6 L4,16 Z" fill="#451a03" />
        {/* Warm lantern hung on the wall */}
        <circle cx="-16" cy="3" r="5" fill="#facc15" opacity="0.3" />
        <rect x="-18" y="1" width="4" height="6" rx="1" fill="#fef08a" stroke="#78350f" strokeWidth="0.7" />
        {/* Small frosted portal */}
        <circle cx="16" cy="3" r="3.5" fill="#38bdf8" stroke="#1e293b" strokeWidth="0.8" />
        <circle cx="16" cy="3" r="2" fill="#e0f2fe" opacity="0.8" />
      </g>

      {/* Variant 2: Chalet des Cimes à Double Toit & Lucarne Lumineuse */}
      <g id="house_2_2">
        <polygon points="-36,16 46,16 48,-12 28,-12" fill="#000" opacity="0.32" />
        {/* Main chalet block */}
        <rect x="-28" y="-12" width="56" height="28" fill="#3f3f46" stroke="#18181b" strokeWidth="1" />
        {/* Lower stone masonry texture */}
        <rect x="-28" y="4" width="56" height="12" fill="#27272a" />
        {/* Pure snow roof without black triangle */}
        <path d="M-34,-12 Q0,-32 34,-12 Q26,-9 12,-11 Q0,-8 -12,-11 Q-26,-9 -34,-12 Z" fill="#ffffff" />
        <path d="M-32,-12 Q0,-30 32,-12" stroke="#e2e8f0" strokeWidth="1.2" fill="none" />
        {/* Attic dormer window (Lucarne) */}
        <polygon points="-6,-18 0,-24 6,-18" fill="#18181b" />
        <path d="M-7,-18 Q0,-26 7,-18" stroke="#ffffff" strokeWidth="1.5" fill="none" />
        <rect x="-3" y="-17" width="6" height="5" fill="#fbbf24" stroke="#78350f" strokeWidth="0.7" />
        {/* Lower glowing windows and cedar door */}
        <rect x="-20" y="-4" width="8" height="7" fill="#fef08a" stroke="#78350f" strokeWidth="0.8" />
        <rect x="12" y="-4" width="8" height="7" fill="#fef08a" stroke="#78350f" strokeWidth="0.8" />
        <rect x="-5" y="3" width="10" height="13" fill="#451a03" stroke="#1c1917" strokeWidth="0.8" />
        <circle cx="2" cy="9" r="0.9" fill="#facc15" />
      </g>


      {/* Mountain Snowy Well (Level 2 - Djurdjura) */}
      <g id="well_2">
        <ellipse cx="0" cy="6" rx="15" ry="5.5" fill="#000" opacity="0.35" />
        {/* Frosty Slate Basin */}
        <rect x="-10" y="-3" width="20" height="9" rx="1.5" fill="#475569" stroke="#334155" strokeWidth="1" />
        <line x1="-10" y1="1" x2="10" y2="1" stroke="#334155" strokeWidth="0.8" />
        <ellipse cx="0" cy="-3" rx="10" ry="3.5" fill="#334155" />
        <ellipse cx="0" cy="-3" rx="8" ry="2.6" fill="#0f172a" />
        <ellipse cx="0" cy="-3" rx="6.5" ry="1.8" fill="#38bdf8" />
        {/* Wooden Posts */}
        <rect x="-8.5" y="-19" width="2.5" height="16" fill="#3f3f46" />
        <rect x="6" y="-19" width="2.5" height="16" fill="#3f3f46" />
        {/* Pulley & Bucket */}
        <rect x="-8.5" y="-18" width="17" height="2" fill="#52525b" />
        <circle cx="0" cy="-17" r="2.5" fill="#71717a" />
        <line x1="0" y1="-17" x2="0" y2="-9" stroke="#cbd5e1" strokeWidth="1" />
        <path d="M-3,-9 L-2.5,-5 L2.5,-5 L3,-9 Z" fill="#3f3f46" />
        {/* Snow Cap Roof & Icicles */}
        <polygon points="-12,-18 0,-26 12,-18" fill="#0284c7" />
        <path d="M-13,-18 Q0,-27 13,-18 Q10,-15 5,-17 Q0,-14 -5,-17 Q-10,-15 -13,-18 Z" fill="#ffffff" />
        <line x1="-8" y1="-16" x2="-8" y2="-13" stroke="#e0f2fe" strokeWidth="1.2" />
        <line x1="6" y1="-16" x2="6" y2="-12" stroke="#e0f2fe" strokeWidth="1.2" />
      </g>

      {/* Mountain Snowy Assets (Level 2 - Djurdjura) */}
      {/* Alpine Trail Lantern Post (Lanterne boréale de sentier montagnard éclairant le chemin) */}
      <g id="alpine_trail_lantern">
        {/* Halo de lumière chaleureuse projeté sur le sol et la neige */}
        <circle cx="12" cy="-16" r="45" fill="url(#lanternGlowWarm)" />
        <ellipse cx="6" cy="6" rx="20" ry="8" fill="url(#lanternGroundLight)" />

        {/* Monticule de neige à la base du poteau */}
        <ellipse cx="0" cy="5" rx="10" ry="4" fill="#f8fafc" opacity="0.95" />
        <ellipse cx="0" cy="4" rx="7" ry="2.5" fill="#ffffff" />

        {/* Poteau en bois de pin sombre et fer forgé */}
        <rect x="-2.5" y="-34" width="5" height="38" rx="1.2" fill="#1e293b" stroke="#0f172a" strokeWidth="0.8" />
        <line x1="-0.8" y1="-32" x2="-0.8" y2="2" stroke="#475569" strokeWidth="0.8" />
        
        {/* Potence en fer forgé */}
        <path d="M -1.5 -26 L 12 -26 L 12 -20" fill="none" stroke="#0f172a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="0" y1="-20" x2="8" y2="-26" stroke="#334155" strokeWidth="1.2" />

        {/* Corps de la lanterne suspendue */}
        <g transform="translate(12, -14)">
          {/* Anneau de suspension & chapeau */}
          <circle cx="0" cy="-9" r="2" fill="none" stroke="#0f172a" strokeWidth="1" />
          <polygon points="-6,-6 6,-6 4,-8 -4,-8" fill="#0f172a" />
          <polygon points="-5,-6 5,-6 4,-1 -4,-1" fill="#1e293b" />
          
          {/* Calotte de neige sur le chapeau */}
          <path d="M -5 -7 Q 0 -9.5 5 -7 L 4 -6 L -4 -6 Z" fill="#ffffff" />

          {/* Verre lumineux rayonnant */}
          <polygon points="-4.5,-1 4.5,-1 3.5,7 -3.5,7" fill="#fef08a" opacity="0.95" />
          <polygon points="-3,0 3,0 2,6 -2,6" fill="#fbbf24" opacity="0.9" />
          
          {/* Flamme dorée & étincelle */}
          <ellipse cx="0" cy="3" rx="1.8" ry="3" fill="#f59e0b" />
          <ellipse cx="0" cy="3.5" rx="0.8" ry="1.6" fill="#ffffff" />

          {/* Armature métallique */}
          <line x1="-4.5" y1="-1" x2="-3.5" y2="7" stroke="#0f172a" strokeWidth="1" />
          <line x1="4.5" y1="-1" x2="3.5" y2="7" stroke="#0f172a" strokeWidth="1" />
          <line x1="0" y1="-1" x2="0" y2="7" stroke="#0f172a" strokeWidth="0.8" opacity="0.7" />
          <polygon points="-4,7 4,7 2.5,9.5 -2.5,9.5" fill="#0f172a" />
        </g>
      </g>

      <g id="mountain_campfire">
        {/* Halo de lumière chaleureuse du feu au sol */}
        <circle cx="0" cy="0" r="50" fill="url(#lanternGlowWarm)" />
        <ellipse cx="0" cy="6" rx="28" ry="12" fill="url(#campfireGroundLight)" />

        <ellipse cx="0" cy="5" rx="14" ry="6" fill="#000" opacity="0.35" />
        {/* Ring of dark mountain rocks */}
        <circle cx="-9" cy="3" r="3.5" fill="#1e293b" stroke="#0f172a" strokeWidth="0.8" />
        <circle cx="-4" cy="5" r="3.2" fill="#334155" stroke="#0f172a" strokeWidth="0.8" />
        <circle cx="3" cy="5" r="3.5" fill="#1e293b" stroke="#0f172a" strokeWidth="0.8" />
        <circle cx="9" cy="3" r="3.2" fill="#334155" stroke="#0f172a" strokeWidth="0.8" />
        <circle cx="7" cy="-1" r="3" fill="#1e293b" stroke="#0f172a" strokeWidth="0.8" />
        <circle cx="-7" cy="-1" r="3" fill="#334155" stroke="#0f172a" strokeWidth="0.8" />
        {/* Burning Wood logs */}
        <line x1="-7" y1="2" x2="7" y2="2" stroke="#78350f" strokeWidth="2.5" strokeLinecap="round" />
        <line x1="-5" y1="5" x2="5" y2="-1" stroke="#451a03" strokeWidth="2.5" strokeLinecap="round" />
        {/* Glowing Ember base */}
        <ellipse cx="0" cy="2" rx="6" ry="3" fill="#ea580c" />
        {/* Dancing Flames */}
        <path d="M-4,3 Q-6,-5 -2,-10 Q0,-16 2,-12 Q5,-8 1,-1 Q-2,5 -4,3 Z" fill="#f97316" />
        <path d="M-2,3 Q-3,-4 0,-9 Q2,-13 2,-7 Q3,-2 0,3 Z" fill="#facc15" />
        <circle cx="0" cy="-2" r="2" fill="#fef08a" />
        {/* Rising spark particles */}
        <circle cx="-1" cy="-15" r="0.8" fill="#fde047" opacity="0.9" />
        <circle cx="3" cy="-18" r="0.7" fill="#fdba74" opacity="0.8" />
        <circle cx="-3" cy="-21" r="0.6" fill="#fef08a" opacity="0.7" />
      </g>

      {/* Mountain Trail Cairn (Takurmut n yeblaḍen / Balises de pierres empilées) */}
      <g id="mountain_cairn">
        <ellipse cx="0" cy="6" rx="12" ry="4.5" fill="#000" opacity="0.35" />
        {/* Base stone */}
        <ellipse cx="0" cy="3" rx="10" ry="4.5" fill="#475569" stroke="#1e293b" strokeWidth="0.8" />
        <path d="M-9,2 Q0,-1 9,2" stroke="#ffffff" strokeWidth="1.2" fill="none" opacity="0.8" />
        {/* Second stone */}
        <ellipse cx="1" cy="-2" rx="7.5" ry="3.5" fill="#64748b" stroke="#334155" strokeWidth="0.8" />
        <path d="M-6,-3 Q1,-5 7,-3" stroke="#ffffff" strokeWidth="1.2" fill="none" opacity="0.8" />
        {/* Third stone */}
        <ellipse cx="-0.5" cy="-7" rx="5.5" ry="2.6" fill="#94a3b8" stroke="#475569" strokeWidth="0.8" />
        <path d="M-4,-8 Q-0.5,-10 4,-8" stroke="#ffffff" strokeWidth="1" fill="none" opacity="0.8" />
        {/* Top peak stone */}
        <ellipse cx="0" cy="-11" rx="3.5" ry="2" fill="#cbd5e1" stroke="#64748b" strokeWidth="0.8" />
        {/* Snow dusting top */}
        <path d="M-3.5,-11 Q0,-14 3.5,-11 Q2,-9 -3.5,-11 Z" fill="#ffffff" />
      </g>

      {/* Stack of Mountain Cedar Firewood Logs (Isɣaren n wedrar) */}
      <g id="wood_logs_stack">
        <ellipse cx="0" cy="6" rx="14" ry="5" fill="#000" opacity="0.35" />
        {/* Bottom row of logs */}
        <g transform="translate(-6, 2)">
          <rect x="-3" y="-3" width="16" height="6" rx="2" fill="#78350f" stroke="#451a03" strokeWidth="0.8" />
          <ellipse cx="-3" cy="0" rx="2" ry="3" fill="#a16207" />
          <circle cx="-3" cy="0" r="0.8" fill="#451a03" />
        </g>
        <g transform="translate(4, 2)">
          <rect x="-3" y="-3" width="16" height="6" rx="2" fill="#92400e" stroke="#451a03" strokeWidth="0.8" />
          <ellipse cx="-3" cy="0" rx="2" ry="3" fill="#b45309" />
          <circle cx="-3" cy="0" r="0.8" fill="#451a03" />
        </g>
        {/* Top log */}
        <g transform="translate(-1, -3)">
          <rect x="-3" y="-3" width="16" height="6" rx="2" fill="#78350f" stroke="#451a03" strokeWidth="0.8" />
          <ellipse cx="-3" cy="0" rx="2" ry="3" fill="#ca8a04" />
          <circle cx="-3" cy="0" r="0.8" fill="#451a03" />
        </g>
        {/* Snow blanket on top of logs */}
        <path d="M-5,-6 Q7,-10 14,-5 Q10,-3 -4,-4 Z" fill="#ffffff" />
      </g>

      {/* Glacial Ice Crystals & Stalagmites (Agris n Ǧerǧer) */}
      <g id="glacial_crystals">
        <ellipse cx="0" cy="5" rx="12" ry="4" fill="#000" opacity="0.3" />
        {/* Left Crystal */}
        <polygon points="-8,4 -5,-14 -2,4" fill="#38bdf8" stroke="#0284c7" strokeWidth="0.8" />
        <polygon points="-5,-14 -2,4 -3,4" fill="#bae6fd" opacity="0.8" />
        {/* Tall Center Crystal */}
        <polygon points="-3,5 1,-24 5,5" fill="#0ea5e9" stroke="#0284c7" strokeWidth="0.8" />
        <polygon points="1,-24 5,5 2,5" fill="#e0f2fe" opacity="0.9" />
        {/* Right Crystal */}
        <polygon points="3,4 8,-12 11,4" fill="#38bdf8" stroke="#0369a1" strokeWidth="0.8" />
        <polygon points="8,-12 11,4 9,4" fill="#bae6fd" opacity="0.75" />
        {/* Shimmer sparkle */}
        <circle cx="1" cy="-24" r="1" fill="#ffffff" />
        <circle cx="-5" cy="-14" r="0.8" fill="#ffffff" />
      </g>

      {/* Proud Djurdjura Mountain Mouflon on Snowy Crag (Iɣid n wedrar) */}
      <g id="mountain_mouflon">
        <ellipse cx="0" cy="7" rx="16" ry="6" fill="#000" opacity="0.35" />
        {/* Snowy Crag Base */}
        <polygon points="-16,7 16,7 12,-2 -14,-2" fill="#475569" stroke="#334155" strokeWidth="1" />
        <path d="M-14,-2 Q0,-5 12,-2 Q8,1 -14,-2 Z" fill="#ffffff" />
        {/* Mouflon Body */}
        <ellipse cx="-1" cy="-7" rx="9" ry="5.5" fill="#78350f" />
        <ellipse cx="1" cy="-6" rx="6" ry="4" fill="#92400e" />
        {/* Mouflon Legs */}
        <line x1="-7" y1="-4" x2="-8" y2="4" stroke="#451a03" strokeWidth="1.8" />
        <line x1="-4" y1="-4" x2="-4" y2="4" stroke="#451a03" strokeWidth="1.8" />
        <line x1="4" y1="-4" x2="4" y2="4" stroke="#451a03" strokeWidth="1.8" />
        <line x1="7" y1="-4" x2="8" y2="4" stroke="#451a03" strokeWidth="1.8" />
        {/* Neck & Head */}
        <path d="M4,-9 L9,-16 L13,-14 L9,-6 Z" fill="#78350f" />
        <circle cx="11" cy="-15" r="3.2" fill="#92400e" />
        <circle cx="12" cy="-16" r="0.9" fill="#18181b" />
        {/* Grand Curved Horns */}
        <path d="M10,-17 C8,-24 3,-23 5,-17 C6,-15 8,-14 9,-16" fill="none" stroke="#ca8a04" strokeWidth="2.2" strokeLinecap="round" />
        <path d="M12,-18 C14,-25 19,-22 17,-16 C16,-14 14,-14 13,-17" fill="none" stroke="#a16207" strokeWidth="1.8" strokeLinecap="round" />
        {/* White tail & chest fluff */}
        <circle cx="-10" cy="-8" r="2" fill="#f8fafc" />
        <ellipse cx="5" cy="-8" rx="2" ry="3" fill="#f8fafc" opacity="0.8" />
      </g>

      {/* Snowy Atlas Pine Cluster (Taida n wedrar) */}
      <g id="snowy_atlas_pine">
        <ellipse cx="0" cy="7" rx="18" ry="6" fill="#000" opacity="0.35" />
        {/* Trunk */}
        <rect x="-3" y="-6" width="6" height="13" fill="#451a03" />
        {/* Layer 1 (Bottom) */}
        <polygon points="-24,-2 0,-24 24,-2" fill="#0f766e" />
        <path d="M-24,-2 Q-12,-8 0,-6 Q12,-8 24,-2 Q14,3 0,1 Q-14,3 -24,-2 Z" fill="#ffffff" />
        {/* Layer 2 (Middle) */}
        <polygon points="-18,-14 0,-34 18,-14" fill="#115e59" />
        <path d="M-18,-14 Q-9,-20 0,-18 Q9,-20 18,-14 Q10,-9 0,-11 Q-10,-9 -18,-14 Z" fill="#ffffff" />
        {/* Layer 3 (Top) */}
        <polygon points="-12,-26 0,-44 12,-26" fill="#134e4a" />
        <polygon points="-12,-26 0,-44 12,-26" fill="#ffffff" transform="scale(0.8) translate(0, -9)" />
      </g>

      {/* Enchanted Forest Assets (Level 3 - Iɣil) */}
      <g id="mystic_mushrooms">
        <ellipse cx="0" cy="4" rx="14" ry="5" fill="#000" opacity="0.3" />
        {/* Big Mushroom */}
        <g transform="translate(-4, 0)">
          <path d="M-2,3 L-1.5,-8 L1.5,-8 L2,3 Z" fill="#fdf4ff" />
          <path d="M-8,-8 C-8,-18 8,-18 8,-8 Z" fill="#c026d3" />
          <path d="M-6,-8 C-6,-16 6,-16 6,-8 Z" fill="#e879f9" />
          <circle cx="-3" cy="-12" r="1.3" fill="#ffffff" />
          <circle cx="3" cy="-11" r="1.1" fill="#ffffff" />
          <circle cx="0" cy="-15" r="1" fill="#ffffff" />
        </g>
        {/* Small Glowing Cyan Mushroom */}
        <g transform="translate(6, 2) scale(0.75)">
          <path d="M-2,3 L-1.5,-8 L1.5,-8 L2,3 Z" fill="#fdf4ff" />
          <path d="M-8,-8 C-8,-18 8,-18 8,-8 Z" fill="#06b6d4" />
          <path d="M-6,-8 C-6,-16 6,-16 6,-8 Z" fill="#67e8f9" />
          <circle cx="0" cy="-13" r="1.2" fill="#ffffff" />
        </g>
        {/* Spore sparkles */}
        <circle cx="-7" cy="-18" r="0.9" fill="#f0abfc" opacity="0.8" />
        <circle cx="8" cy="-19" r="0.8" fill="#a5f3fc" opacity="0.8" />
        <circle cx="0" cy="-22" r="0.7" fill="#fdf4ff" opacity="0.9" />
      </g>

      {/* Ancient Runic Carved Menhir (Aẓru ameqqran n tmusni) */}
      <g id="ancient_runic_stone">
        <ellipse cx="0" cy="7" rx="14" ry="5" fill="#000" opacity="0.35" />
        {/* Heavy Stone Monolith */}
        <polygon points="-8,6 -10,-26 -2,-32 8,-28 9,6" fill="#3b0764" stroke="#2e1065" strokeWidth="1" />
        <polygon points="-6,5 -8,-24 -1,-29 6,-26 7,5" fill="#581c87" />
        {/* Moss creeping at base */}
        <ellipse cx="-4" cy="5" rx="5" ry="2" fill="#059669" />
        <ellipse cx="4" cy="6" rx="4" ry="2" fill="#10b981" />
        {/* Glowing Tifinagh Letters */}
        <text x="0" y="-18" fill="#f0abfc" fontSize="8" fontWeight="bold" textAnchor="middle" filter="drop-shadow(0 0 2px #d946ef)">ⵣ</text>
        <text x="0" y="-8" fill="#67e8f9" fontSize="7" fontWeight="bold" textAnchor="middle" filter="drop-shadow(0 0 2px #06b6d4)">ⴼ</text>
        <text x="0" y="0" fill="#f0abfc" fontSize="6" fontWeight="bold" textAnchor="middle">ⵟ</text>
      </g>

      {/* Mystical Crystal Spires */}
      <g id="mystic_crystal_spires">
        <ellipse cx="0" cy="5" rx="12" ry="4" fill="#000" opacity="0.3" />
        <polygon points="-6,4 -3,-18 0,4" fill="#9333ea" stroke="#7e22ce" strokeWidth="0.8" />
        <polygon points="-3,-18 0,4 -1,4" fill="#d8b4fe" opacity="0.85" />
        <polygon points="-2,5 2,-26 6,5" fill="#c026d3" stroke="#a21caf" strokeWidth="0.8" />
        <polygon points="2,-26 6,5 3,5" fill="#f5d0fe" opacity="0.9" />
        <polygon points="4,4 8,-14 11,4" fill="#06b6d4" stroke="#0891b2" strokeWidth="0.8" />
        <circle cx="2" cy="-26" r="1.2" fill="#ffffff" />
      </g>
      <g id="tree_3">
        <ellipse cx="0" cy="5" rx="14" ry="5" fill="#000" opacity="0.3" />
        <path d="M-3,5 C-5,-5 2,-10 0,-20 C2,-10 -1,-5 3,5 Z" fill="#2e1065" />
        <path d="M-1,5 C-2,-5 1,-10 0,-20 C1,-10 0,-5 1,5 Z" fill="#6b21a8" />
        <circle cx="0" cy="-25" r="22" fill="#86198f" opacity="0.9" />
        <circle cx="-8" cy="-20" r="14" fill="#c026d3" />
        <circle cx="10" cy="-28" r="12" fill="#d946ef" />
        <circle cx="0" cy="-30" r="8" fill="#f0abfc" />
        <line x1="-10" y1="-10" x2="-10" y2="0" stroke="#f0abfc" strokeWidth="1" />
        <line x1="12" y1="-15" x2="12" y2="-5" stroke="#f0abfc" strokeWidth="1" />
        <circle cx="-10" cy="0" r="1.5" fill="#fdf4ff" />
        <circle cx="12" cy="-5" r="1.5" fill="#fdf4ff" />
      </g>
      {/* Level 3 Houses: Iɣil (Demeures Féeriques & Sanctuaires Sylvestres) */}
      {/* Variant 0: Dôme Sacré aux Runes */}
      <g id="house_3_0">
        <polygon points="-36,16 46,16 50,-10 30,-10" fill="#000" opacity="0.32" />
        <rect x="-30" y="-10" width="60" height="25" fill="#581c87" stroke="#3b0764" strokeWidth="1" />
        {/* Curved enchanted dome roof */}
        <path d="M-35,-10 C-35,-38 35,-38 35,-10 Z" fill="#86198f" />
        <path d="M-25,-10 C-25,-32 25,-32 25,-10 Z" fill="#d946ef" />
        {/* Crystal spire peak */}
        <polygon points="-3,-34 0,-44 3,-34" fill="#f0abfc" />
        <circle cx="0" cy="-44" r="1.5" fill="#ffffff" />
        {/* Glowing circular stained glass portals */}
        <circle cx="-15" cy="2" r="5" fill="#06b6d4" stroke="#67e8f9" strokeWidth="0.8" />
        <circle cx="-15" cy="2" r="3" fill="#ecfeff" opacity="0.85" />
        <circle cx="15" cy="2" r="5" fill="#06b6d4" stroke="#67e8f9" strokeWidth="0.8" />
        <circle cx="15" cy="2" r="3" fill="#ecfeff" opacity="0.85" />
        {/* Runic door */}
        <path d="M-8,15 L-8,0 A8,8 0 0 1 8,0 L8,15 Z" fill="#2e1065" />
        <text x="0" y="9" fill="#f0abfc" fontSize="6" fontWeight="bold" textAnchor="middle">ⵣ</text>
      </g>
      <g id="house_3"><use href="#house_3_0" /></g>

      {/* Variant 1: Pagode Mystique aux Cloches Féeriques */}
      <g id="house_3_1">
        <polygon points="-38,16 46,16 48,-10 28,-10" fill="#000" opacity="0.32" />
        {/* Main sanctum body */}
        <rect x="-26" y="-8" width="52" height="23" fill="#4c1d95" stroke="#2e1065" strokeWidth="1" />
        {/* Pillars */}
        <rect x="-24" y="-8" width="4" height="23" fill="#2e1065" />
        <rect x="20" y="-8" width="4" height="23" fill="#2e1065" />
        {/* Two-tier curved pagode roof */}
        <path d="M-36,-8 Q-16,-18 0,-14 Q16,-18 36,-8 Q26,-4 0,-7 Q-26,-4 -36,-8 Z" fill="#701a75" />
        <path d="M-26,-14 Q-12,-28 0,-24 Q12,-28 26,-14 Q18,-10 0,-13 Q-18,-10 -26,-14 Z" fill="#a21caf" />
        {/* Glowing cyan lantern orbs suspended at eaves */}
        <circle cx="-32" cy="-4" r="2.2" fill="#22d3ee" />
        <circle cx="32" cy="-4" r="2.2" fill="#22d3ee" />
        <circle cx="-23" cy="-10" r="1.8" fill="#a5f3fc" />
        <circle cx="23" cy="-10" r="1.8" fill="#a5f3fc" />
        {/* Arch portal */}
        <path d="M-7,15 L-7,3 A7,7 0 0 1 7,3 L7,15 Z" fill="#1e1b4b" />
        <circle cx="0" cy="8" r="2.5" fill="#f472b6" opacity="0.9" />
      </g>

      {/* Variant 2: Arboretum Sylvestre Féerique (Maison Arbre-Champignon) */}
      <g id="house_3_2">
        <polygon points="-34,16 44,16 46,-10 26,-10" fill="#000" opacity="0.3" />
        {/* Curved organic wooden trunk base */}
        <path d="M-24,15 C-28,2 -20,-10 -16,-12 L16,-12 C20,-10 28,2 24,15 Z" fill="#3b0764" stroke="#2e1065" strokeWidth="1" />
        {/* Giant glowing mushroom cap roof */}
        <path d="M-34,-10 C-34,-36 34,-36 34,-10 C24,-6 0,-8 -34,-10 Z" fill="#9333ea" />
        <path d="M-28,-11 C-28,-32 28,-32 28,-11" stroke="#e879f9" strokeWidth="2" fill="none" />
        {/* Luminous spots on mushroom cap */}
        <circle cx="-14" cy="-22" r="3" fill="#fdf4ff" opacity="0.9" />
        <circle cx="12" cy="-24" r="2.5" fill="#fdf4ff" opacity="0.9" />
        <circle cx="0" cy="-28" r="2.8" fill="#67e8f9" opacity="0.9" />
        {/* Star-shaped / diamond window */}
        <polygon points="0,-4 4,0 0,4 -4,0" fill="#67e8f9" stroke="#cffafe" strokeWidth="0.8" />
        {/* Organic arched door with glowing latch */}
        <path d="M-7,15 L-7,4 A7,7 0 0 1 7,4 L7,15 Z" fill="#18181b" />
        <circle cx="3" cy="9" r="1.1" fill="#f0abfc" />
      </g>

      {/* Mystical Glowing Fountain / Well (Level 3 - Iɣil) */}
      <g id="well_3">
        <ellipse cx="0" cy="6" rx="15" ry="5.5" fill="#000" opacity="0.35" />
        <rect x="-10" y="-3" width="20" height="9" rx="1.5" fill="#581c87" stroke="#3b0764" strokeWidth="1" />
        <ellipse cx="0" cy="-3" rx="10" ry="3.5" fill="#3b0764" />
        <ellipse cx="0" cy="-3" rx="7.5" ry="2" fill="#d946ef" />
        <ellipse cx="0" cy="-3" rx="5" ry="1.2" fill="#fdf4ff" opacity="0.9" />
        <rect x="-8.5" y="-19" width="2.5" height="16" fill="#4c1d95" />
        <rect x="6" y="-19" width="2.5" height="16" fill="#4c1d95" />
        <polygon points="-12,-18 0,-26 12,-18" fill="#701a75" />
        <polygon points="-10,-18 0,-24.5 10,-18" fill="#a21caf" />
        <circle cx="0" cy="-15" r="2.5" fill="#f0abfc" />
      </g>

      {/* Ancestral temple ruins */}
      <g id="tree_4">
        <ellipse cx="0" cy="5" rx="12" ry="4" fill="#000" opacity="0.3" />
        <path d="M-3,5 L-2,-5 L-12,-15 L-10,-17 L-1,-7 L0,-20 L3,-20 L2,-7 L10,-12 L12,-10 L3,-2 L3,5 Z" fill="#27272a" />
        <path d="M-1,5 L0,-5 L-8,-13 L-7,-14 L0,-6 L1,-18 L2,-18 L1,-6 L7,-10 L8,-9 L2,-2 L2,5 Z" fill="#3f3f46" />
      </g>

      {/* Level 4 Houses: Ijduden (Temples & Sanctuaires des Ancêtres) */}
      {/* Variant 0: Temple des Piliers Sacrés (Grand Sanctuaire) */}
      <g id="house_4_0">
        <polygon points="-38,16 48,16 52,-10 32,-10" fill="#000" opacity="0.35" />
        {/* Stepped stone plinth base course */}
        <rect x="-34" y="9" width="68" height="6" fill="#27272a" stroke="#18181b" strokeWidth="0.8" />
        <rect x="-30" y="-8" width="60" height="18" fill="#3f3f46" stroke="#27272a" strokeWidth="1" />
        {/* Classical fluted columns */}
        <rect x="-26" y="-8" width="5" height="18" fill="#71717a" stroke="#3f3f46" strokeWidth="0.7" />
        <rect x="-11" y="-8" width="5" height="18" fill="#71717a" stroke="#3f3f46" strokeWidth="0.7" />
        <rect x="6" y="-8" width="5" height="18" fill="#71717a" stroke="#3f3f46" strokeWidth="0.7" />
        <rect x="21" y="-8" width="5" height="18" fill="#71717a" stroke="#3f3f46" strokeWidth="0.7" />
        {/* Marble lintel and pediment with glowing Yaz ⵣ emblem */}
        <rect x="-32" y="-12" width="64" height="4.5" fill="#27272a" stroke="#18181b" strokeWidth="0.8" />
        <polygon points="-36,-12 0,-30 36,-12" fill="#18181b" stroke="#27272a" strokeWidth="1" />
        <polygon points="-30,-13 0,-27 30,-13" fill="#27272a" />
        {/* Glowing Golden Yaz Symbol in pediment */}
        <text x="0" y="-16" fill="#fbbf24" fontSize="9" fontWeight="bold" textAnchor="middle" filter="drop-shadow(0 0 2px #f59e0b)">ⵣ</text>
        {/* Central sanctuary portal */}
        <rect x="-5" y="-1" width="10" height="11" fill="#09090b" />
        <circle cx="0" cy="4" r="2.2" fill="#34d399" opacity="0.9" />
      </g>
      <g id="house_4"><use href="#house_4_0" /></g>

      {/* Variant 1: Rotonde & Panthéon Ancestral */}
      <g id="house_4_1">
        <polygon points="-36,16 46,16 48,-10 28,-10" fill="#000" opacity="0.32" />
        {/* Base stone course */}
        <rect x="-30" y="8" width="60" height="7" fill="#18181b" />
        {/* Colonnade rotunda */}
        <rect x="-26" y="-8" width="52" height="17" fill="#27272a" stroke="#18181b" strokeWidth="1" />
        <path d="M-20,-8 L-20,8 M-10,-8 L-10,8 M0,-8 L0,8 M10,-8 L10,8 M20,-8 L20,8" stroke="#71717a" strokeWidth="2.5" />
        {/* Golden Cupola Dome */}
        <path d="M-30,-8 C-30,-34 30,-34 30,-8 Z" fill="#18181b" stroke="#27272a" strokeWidth="1" />
        <path d="M-22,-8 C-22,-28 22,-28 22,-8 Z" fill="#ca8a04" />
        <path d="M-14,-8 C-14,-22 14,-22 14,-8 Z" fill="#facc15" />
        {/* Cupola finial */}
        <polygon points="-2,-32 0,-40 2,-32" fill="#fbbf24" />
        <circle cx="0" cy="-40" r="1.5" fill="#ffffff" />
        {/* Sacred altar portal */}
        <path d="M-6,8 L-6,-1 A6,6 0 0 1 6,-1 L6,8 Z" fill="#09090b" />
        <ellipse cx="0" cy="3" rx="2" ry="3" fill="#10b981" opacity="0.9" />
      </g>

      {/* Variant 2: Pavillon des Stèles d'Or (Sanctuaire Monumental) */}
      <g id="house_4_2">
        <polygon points="-36,16 46,16 48,-12 28,-12" fill="#000" opacity="0.32" />
        {/* Stepped ziggurat sanctuary */}
        <rect x="-30" y="6" width="60" height="9" fill="#18181b" stroke="#27272a" strokeWidth="0.8" />
        <rect x="-24" y="-4" width="48" height="11" fill="#27272a" stroke="#3f3f46" strokeWidth="0.8" />
        <rect x="-18" y="-12" width="36" height="9" fill="#3f3f46" stroke="#52525b" strokeWidth="0.8" />
        {/* Bronze-gilt trim on steps */}
        <line x1="-30" y1="6" x2="30" y2="6" stroke="#eab308" strokeWidth="1.5" />
        <line x1="-24" y1="-4" x2="24" y2="-4" stroke="#eab308" strokeWidth="1.5" />
        <line x1="-18" y1="-12" x2="18" y2="-12" stroke="#eab308" strokeWidth="1.5" />
        {/* Golden Sunburst Yaz Peak */}
        <polygon points="-12,-12 0,-24 12,-12" fill="#854d0e" stroke="#ca8a04" strokeWidth="1" />
        <polygon points="-8,-12 0,-21 8,-12" fill="#eab308" />
        <text x="0" y="-13.5" fill="#18181b" fontSize="5" fontWeight="bold" textAnchor="middle">ⵣ</text>
        {/* Side brazier alcoves */}
        <circle cx="-12" cy="1" r="2.2" fill="#34d399" />
        <circle cx="12" cy="1" r="2.2" fill="#34d399" />
        {/* Center monumental portal */}
        <rect x="-4" y="6" width="8" height="9" fill="#09090b" />
      </g>


      {/* Ancestral Sacred Font / Well (Level 4 - Ijduden) */}
      <g id="well_4">
        <ellipse cx="0" cy="6" rx="15" ry="5.5" fill="#000" opacity="0.35" />
        <rect x="-10" y="-3" width="20" height="9" rx="1.5" fill="#3f3f46" stroke="#27272a" strokeWidth="1" />
        <ellipse cx="0" cy="-3" rx="10" ry="3.5" fill="#27272a" />
        <ellipse cx="0" cy="-3" rx="7.5" ry="2" fill="#059669" />
        <ellipse cx="0" cy="-3" rx="5" ry="1.2" fill="#6ee7b7" opacity="0.85" />
        <rect x="-8.5" y="-19" width="2.5" height="16" fill="#27272a" />
        <rect x="6" y="-19" width="2.5" height="16" fill="#27272a" />
        <polygon points="-12,-18 0,-26 12,-18" fill="#18181b" />
        <polygon points="-10,-18 0,-24.5 10,-18" fill="#34d399" />
        <text x="0" y="-12" fill="#a7f3d0" fontSize="8" fontWeight="bold" textAnchor="middle">ⵣ</text>
      </g>

      {/* --- LEVEL 4 SCENERY PROPS (Sanctuary of Ijduden) --- */}
      {/* Sacred Sanctuary Brazier (Corrupted: Emerald Poison Flame / Restored: Radiant Solar Flame) */}
      <g id="sanctuary_brazier">
        <ellipse cx="0" cy="8" rx="14" ry="5" fill="#000000" opacity="0.35" />
        {/* Carved stone pedestal */}
        <polygon points="-10,8 10,8 8,-6 -8,-6" fill="#27272a" stroke="#18181b" strokeWidth="1" />
        <rect x="-12" y="-10" width="24" height="4.5" rx="1" fill="#3f3f46" stroke="#27272a" strokeWidth="1" />
        {/* Bowl */}
        <path d="M -14 -10 Q 0 -2 14 -10 L 12 -13 L -12 -13 Z" fill="#18181b" stroke="#3f3f46" strokeWidth="1.2" />
        {/* Glowing Rune / Gem */}
        <circle cx="0" cy="-6" r="2.5" fill="#34d399" opacity="0.9" />
        {/* Magic Fire & Flames */}
        <path d="M -8 -13 Q -10 -25 -2 -30 Q 0 -18 4 -32 Q 10 -24 8 -13 Z" fill="#10b981" opacity="0.95" />
        <path d="M -4 -13 Q -6 -22 0 -26 Q 2 -18 4 -26 Q 6 -20 4 -13 Z" fill="#6ee7b7" />
        <circle cx="0" cy="-22" r="3" fill="#ecfdf5" />
      </g>

      {/* Sacred Yaz Ancestral Monolith */}
      <g id="sanctuary_monolith">
        <ellipse cx="0" cy="7" rx="16" ry="6" fill="#000000" opacity="0.35" />
        {/* Stele Base */}
        <rect x="-13" y="1" width="26" height="7" rx="1.5" fill="#27272a" stroke="#18181b" strokeWidth="1" />
        <rect x="-11" y="-5" width="22" height="6" rx="1" fill="#3f3f46" />
        {/* Tapered Monolith Body */}
        <polygon points="-9,-5 9,-5 6,-42 -6,-42" fill="#18181b" stroke="#27272a" strokeWidth="1.2" />
        <polygon points="-6,-42 6,-42 0,-50" fill="#27272a" stroke="#3f3f46" strokeWidth="1" />
        {/* Glowing Yaz Glyph */}
        <text x="0" y="-18" fill="#34d399" fontSize="13" fontWeight="900" textAnchor="middle" fontFamily="sans-serif">
          ⵣ
        </text>
        <circle cx="0" cy="-44" r="2.5" fill="#6ee7b7" opacity="0.9" />
      </g>

      {/* Mystic Sanctuary Energy Crystals */}
      <g id="sanctuary_crystals">
        <ellipse cx="0" cy="5" rx="14" ry="5" fill="#000000" opacity="0.3" />
        <polygon points="0,5 -5,-25 0,-34 5,-25" fill="#059669" stroke="#34d399" strokeWidth="1" />
        <polygon points="0,5 0,-34 5,-25 3,5" fill="#10b981" />
        <polygon points="-4,5 -10,-18 -7,-24 -3,-18" fill="#047857" />
        <polygon points="3,5 9,-16 6,-22 2,-16" fill="#34d399" />
        <circle cx="0" cy="-28" r="4" fill="#a7f3d0" opacity="0.6" />
      </g>
    </defs>

    {/* Fond de base du plateau avec texture étendue sur toute la largeur panoramique */}
    {levelId === 4 ? (
      <>
        {/* 1. Sol Corrompu (avant libération) */}
        <g
          className="pointer-events-none"
          style={{
            opacity: (isRestored || isNight) ? 0 : 1,
            transition: 'opacity 2500ms ease-in-out'
          }}
        >
          <rect x="-5000" y="0" width="10000" height="1600" fill="url(#groundPattern_4_corrupted)" />
        </g>

        {/* 2. Sol Jour Purifié (plein jour printanier et fleurs alpines) */}
        <g
          className="pointer-events-none"
          style={{
            opacity: (isRestored && !isNight) ? 1 : 0,
            transition: 'opacity 2500ms ease-in-out'
          }}
        >
          <rect x="-5000" y="0" width="10000" height="1600" fill="url(#groundPattern_4_day)" />
          <rect x="-5000" y="0" width="10000" height="1600" fill="#15803d" opacity="0.85" />
          {/* Blooming Wild Alpine Poppies and Herbs upon liberation */}
          <use href="#flower_cluster" x={getAdjustedX(55)} y="1050" transform="scale(1.2)" />
          <use href="#flower_cluster" x={getAdjustedX(345)} y="850" transform="scale(1.2) scale(-1, 1)" />
          <use href="#flower_cluster" x={getAdjustedX(60)} y="650" transform="scale(1.1)" />
          <use href="#flower_cluster" x={getAdjustedX(340)} y="450" transform="scale(1.15) scale(-1, 1)" />
          <use href="#flower_cluster" x={getAdjustedX(55)} y="250" transform="scale(1.2)" />
        </g>

        {/* 3. Sol Nuit Épilogue (sous la lune Ayyur et la brume) */}
        <g
          className="pointer-events-none"
          style={{
            opacity: isNight ? 1 : 0,
            transition: 'opacity 2500ms ease-in-out'
          }}
        >
          <rect x="-5000" y="0" width="10000" height="1600" fill="url(#groundPattern_4_night)" />
          <rect x="-5000" y="0" width="10000" height="1600" fill="#27272a" opacity="0.85" />
          {/* Blooming Wild Alpine Poppies and Herbs in moonlit tones */}
          <use href="#flower_cluster" x={getAdjustedX(55)} y="1050" transform="scale(1.2)" />
          <use href="#flower_cluster" x={getAdjustedX(345)} y="850" transform="scale(1.2) scale(-1, 1)" />
          <use href="#flower_cluster" x={getAdjustedX(60)} y="650" transform="scale(1.1)" />
          <use href="#flower_cluster" x={getAdjustedX(340)} y="450" transform="scale(1.15) scale(-1, 1)" />
          <use href="#flower_cluster" x={getAdjustedX(55)} y="250" transform="scale(1.2)" />
          <rect x="-5000" y="0" width="10000" height="1600" fill="#0f172a" opacity="0.38" />
        </g>
      </>
    ) : (
      <>
        <rect x="-5000" y="0" width="10000" height="1600" fill={`url(#${groundPatternId})`} />
        {isNight && (
          <rect x="-5000" y="0" width="10000" height="1600" fill="#022c22" opacity="0.38" className="pointer-events-none" />
        )}
      </>
    )}

    {/* Prolongement naturel du chemin de terre battue au-delà du départ pour éliminer toute coupure */}
    {levelId === 2 && (
      <path
        d={`M 200 1600 L ${PATH_NODES.map((n) => `${n.x},${n.y}`).join(' L ')}`}
        fill="none"
        stroke="#cbd5e1"
        strokeWidth="36"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.5"
      />
    )}
    <path
      d={`M 200 1600 L ${PATH_NODES.map((n) => `${n.x},${n.y}`).join(' L ')}`}
      fill="none"
      stroke={`url(#dirtTile_${levelId})`}
      strokeWidth="32"
      strokeLinecap="square"
      strokeLinejoin="miter"
    />

    {/* ========================================================================= */}
    {/* PORTIQUE D'ENTRÉE DU NIVEAU - OMBRE AU SOL (y = 1210)                     */}
    {/* L'arche et le linteau sont rendus en premier plan au-dessus du joueur     */}
    {/* pour qu'il passe bien SOUS le portique.                                   */}
    {/* ========================================================================= */}
    <g id="level_entrance_portico_ground" transform="translate(200, 1210)">
      {/* Ombre portée au sol sous le portique */}
      <ellipse cx="0" cy="18" rx="68" ry="10" fill="#000000" opacity="0.35" />
    </g>

    {/* ========================================================================= */}
    {/* DÉCORS DE BASE DU PAYSAGE (ARBRES, MAISONS, PUITS EN ARRIÈRE-PLAN)        */}
    {/* Rendu en premier pour ne jamais masquer les éléments thématiques         */}
    {/* ========================================================================= */}
    {(LEVEL_DECOR_PLACEMENTS[levelId] || DECOR_PLACEMENTS).map((decor, idx) => {
      const scale = decor.scale || 1;
      const flip = decor.flipX ? -1 : 1;
      const variant = decor.variant !== undefined ? decor.variant : (idx % 3);
      const houseHref = `#house_${levelId}_${variant}`;
      const elemHref = decor.type === 'house' ? houseHref : `#${decor.type}_${levelId}`;
      const adjX = getAdjustedX(decor.x);
      return (
        <g key={idx} transform={`translate(${adjX}, ${decor.y}) scale(${scale * flip}, ${scale})`}>
          <use href={elemHref} x="0" y="0" />
        </g>
      );
    })}

    {/* ========================================================================= */}
    {/* DÉCORS THÉMATIQUES PAR NIVEAU (DEVANT LES ARBRES ET MAISONS - VISIBLES)   */}
    {/* ========================================================================= */}

    {/* Level 0 Scenery: Taddart (Village Ancestral avec Ânes aux tapis kabyles & Cactus Akermus) */}
    {levelId === 0 && (
      <g id="taddart_scenery">
        {/* Ânes Kabyles avec tapis traditionnels sur le dos dans les clairières */}
        <use href="#kabyle_donkey_standing" transform={`translate(${getAdjustedX(65)}, 1220) scale(1.15)`} />
        <use href="#kabyle_donkey_resting" transform={`translate(${getAdjustedX(335)}, 850) scale(-1.1, 1.1)`} />
        <use href="#kabyle_donkey_standing" transform={`translate(${getAdjustedX(65)}, 380) scale(1.15)`} />

        {/* Cactus de figues de barbarie (Akermus / Tahendit aux fruits colorés) à taille d'arbuste basse réaliste */}
        <use href="#prickly_pear_cactus_large" transform={`translate(${getAdjustedX(335)}, 1210) scale(0.55)`} />
        <use href="#prickly_pear_cactus_cluster" transform={`translate(${getAdjustedX(340)}, 880) scale(-0.52, 0.52)`} />
        <use href="#prickly_pear_cactus_large" transform={`translate(${getAdjustedX(65)}, 760) scale(0.52)`} />
        <use href="#prickly_pear_cactus_cluster" transform={`translate(${getAdjustedX(340)}, 600) scale(0.54)`} />
        <use href="#prickly_pear_cactus_large" transform={`translate(${getAdjustedX(65)}, 80) scale(0.52)`} />

        {/* Bosquets fleuris du village dans les espaces ouverts */}
        <use href="#flower_cluster" transform={`translate(${getAdjustedX(60)}, 1120) scale(1)`} />
        <use href="#flower_cluster" transform={`translate(${getAdjustedX(340)}, 800) scale(-1.1, 1.1)`} />
        <use href="#flower_cluster" transform={`translate(${getAdjustedX(65)}, 630) scale(1)`} />
        <use href="#flower_cluster" transform={`translate(${getAdjustedX(340)}, 480) scale(1.05)`} />
        <use href="#flower_cluster" transform={`translate(${getAdjustedX(65)}, 270) scale(-1.1, 1.1)`} />
        <use href="#flower_cluster" transform={`translate(${getAdjustedX(335)}, 150) scale(1)`} />
      </g>
    )}

    {/* Level 1 Scenery: Les Oliveraies d'Azemmur (Parfaitement visibles et dégagés) */}
    {levelId === 1 && (
      <g id="azemmur_scenery">
        {/* Traditional Kabyle Berber Rugs (Tapis Kabyles étalés au soleil) */}
        <use href="#kabyle_carpet_large" transform={`translate(${getAdjustedX(330)}, 1220) scale(1.15)`} />
        <use href="#kabyle_carpet_runner" transform={`translate(${getAdjustedX(65)}, 630) scale(1.15)`} />
        <use href="#kabyle_carpet_large" transform={`translate(${getAdjustedX(330)}, 600) scale(-1.08, 1.08)`} />

        {/* Traditional Olive Wood Logs Depots (Dépôts de grumes et bois d'olivier) */}
        <use href="#olive_logs_depot" transform={`translate(${getAdjustedX(65)}, 1230) scale(1.15)`} />
        <use href="#olive_logs_depot" transform={`translate(${getAdjustedX(335)}, 870) scale(-1.12, 1.12)`} />
        <use href="#olive_logs_pile_small" transform={`translate(${getAdjustedX(65)}, 380) scale(1.2)`} />

        {/* Harvest Baskets & Wooden Ladders on Olive Trees */}
        <use href="#harvest_baskets_ladder" transform={`translate(${getAdjustedX(340)}, 800) scale(-1.1, 1.1)`} />
        <use href="#harvest_baskets_ladder" transform={`translate(${getAdjustedX(65)}, 680) scale(1.15)`} />
        <use href="#harvest_baskets_ladder" transform={`translate(${getAdjustedX(335)}, 260) scale(-1.1, 1.1)`} />

        {/* Traditional Clay Olive Oil Jars */}
        <use href="#oil_jars_cluster" transform={`translate(${getAdjustedX(60)}, 1140) scale(1.15)`} />
        <use href="#oil_jar_single" transform={`translate(${getAdjustedX(335)}, 840) scale(1.1)`} />
        <use href="#oil_jars_cluster" transform={`translate(${getAdjustedX(65)}, 430) scale(1.15)`} />
        <use href="#oil_jar_single" transform={`translate(${getAdjustedX(335)}, 130) scale(1.2)`} />

        {/* Subtle Wild Grass & Poppies Accents */}
        <use href="#wild_poppies" transform={`translate(${getAdjustedX(340)}, 1130) scale(1)`} />
        <use href="#tall_wild_grass" transform={`translate(${getAdjustedX(60)}, 790) scale(1.05)`} />
        <use href="#wild_poppies" transform={`translate(${getAdjustedX(340)}, 480) scale(1.1)`} />
        <use href="#tall_wild_grass" transform={`translate(${getAdjustedX(65)}, 240) scale(1.1)`} />
      </g>
    )}

    {/* Level 2 Scenery: Djurdjura - Cimes Enneigées sous la Nuit (Lanternes boréales et ambiance nocturne) */}
    {levelId === 2 && (
      <g id="djurdjura_scenery">
        {/* Lanternes de sentier alpin illuminant le chemin dans la nuit */}
        <use href="#alpine_trail_lantern" transform="translate(240, 1435) scale(-1.1, 1.1)" />
        <use href="#alpine_trail_lantern" transform="translate(90, 1335) scale(1.1)" />
        <use href="#alpine_trail_lantern" transform="translate(320, 1205) scale(-1.1, 1.1)" />
        <use href="#alpine_trail_lantern" transform="translate(80, 1075) scale(1.1)" />
        <use href="#alpine_trail_lantern" transform="translate(325, 945) scale(-1.1, 1.1)" />
        <use href="#alpine_trail_lantern" transform="translate(155, 815) scale(1.1)" />
        <use href="#alpine_trail_lantern" transform="translate(75, 685) scale(1.1)" />
        <use href="#alpine_trail_lantern" transform="translate(330, 555) scale(-1.1, 1.1)" />
        <use href="#alpine_trail_lantern" transform="translate(85, 425) scale(1.1)" />
        <use href="#alpine_trail_lantern" transform="translate(315, 295) scale(-1.1, 1.1)" />
        <use href="#alpine_trail_lantern" transform="translate(155, 140) scale(1.05)" />
        <use href="#alpine_trail_lantern" transform="translate(245, 140) scale(-1.05, 1.05)" />

        {/* Femmes du Groupe Djurdjura en costumes traditionnels kabyles (Tiqendyar, Fouta, Tabzimt & Bendir) */}
        <use href="#djurdjura_singers_group" transform={`translate(${getAdjustedX(65)}, 1220) scale(1.15)`} />
        <use href="#djurdjura_woman_solo" transform={`translate(${getAdjustedX(330)}, 860) scale(-1.12, 1.12)`} />
        <use href="#djurdjura_singers_group" transform={`translate(${getAdjustedX(65)}, 620) scale(1.1)`} />
        <use href="#djurdjura_woman_solo" transform={`translate(${getAdjustedX(335)}, 250) scale(1.15)`} />

        {/* Frozen Glacial Ponds (Agelmim n Ǧerǧer / Étangs et lacs gelés de haute montagne) */}
        <use href="#glacial_frozen_pond_large" transform={`translate(${getAdjustedX(330)}, 1220) scale(1.15)`} />
        <use href="#glacial_frozen_pond_large" transform={`translate(${getAdjustedX(60)}, 760) scale(1.15)`} />
        <use href="#glacial_frozen_pond_compact" transform={`translate(${getAdjustedX(335)}, 600) scale(1.1)`} />
        <use href="#glacial_frozen_pond_compact" transform={`translate(${getAdjustedX(330)}, 120) scale(1.15)`} />

        {/* Mountain Campfires (Foyers réconfortants dans des alcôves sécurisées) */}
        <use href="#mountain_campfire" transform={`translate(${getAdjustedX(65)}, 1120) scale(1.15)`} />
        <use href="#mountain_campfire" transform={`translate(${getAdjustedX(335)}, 500) scale(1.15)`} />

        {/* Trail Cairns (Cairns de balisage espacés sur les flancs) */}
        <use href="#mountain_cairn" transform={`translate(${getAdjustedX(65)}, 870) scale(1.15)`} />
        <use href="#mountain_cairn" transform={`translate(${getAdjustedX(335)}, 800) scale(-1.2, 1.2)`} />
        <use href="#mountain_cairn" transform={`translate(${getAdjustedX(65)}, 380) scale(1.15)`} />

        {/* Stacks of Firewood Logs (Entièrement dégagés des toitures) */}
        <use href="#wood_logs_stack" transform={`translate(${getAdjustedX(340)}, 895) scale(1.15)`} />
        <use href="#wood_logs_stack" transform={`translate(${getAdjustedX(65)}, 270) scale(-1.15, 1.15)`} />

        {/* Glacial Ice Crystals */}
        <use href="#glacial_crystals" transform={`translate(${getAdjustedX(340)}, 1200) scale(1.2)`} />
        <use href="#glacial_crystals" transform={`translate(${getAdjustedX(60)}, 670) scale(-1.15, 1.15)`} />
        <use href="#glacial_crystals" transform={`translate(${getAdjustedX(335)}, 480) scale(1.25)`} />

        {/* Djurdjura Mountain Mouflons (Perchés sur les hautes corniches rocheuses sauvages, éloignés des maisons) */}
        <use href="#mountain_mouflon" transform={`translate(${getAdjustedX(340)}, 75) scale(-1.15, 1.15)`} />
        <use href="#mountain_mouflon" transform={`translate(${getAdjustedX(65)}, 75) scale(1.15)`} />
      </g>
    )}

    {/* Level 3 Scenery: Iɣil - Crêtes et Forêt Mystique (Dégagés et visibles) */}
    {levelId === 3 && (
      <g id="igil_scenery">
        {/* Ancient Runic Menhirs */}
        <use href="#ancient_runic_stone" transform={`translate(${getAdjustedX(65)}, 1220) scale(1.2)`} />
        <use href="#ancient_runic_stone" transform={`translate(${getAdjustedX(335)}, 870) scale(-1.2, 1.2)`} />
        <use href="#ancient_runic_stone" transform={`translate(${getAdjustedX(65)}, 610) scale(1.2)`} />
        <use href="#ancient_runic_stone" transform={`translate(${getAdjustedX(335)}, 260) scale(1.25)`} />

        {/* Bioluminescent Mushroom Clusters */}
        <use href="#mystic_mushrooms" transform={`translate(${getAdjustedX(330)}, 1220) scale(1.15)`} />
        <use href="#mystic_mushrooms" transform={`translate(${getAdjustedX(60)}, 1120) scale(-1.2, 1.2)`} />
        <use href="#mystic_mushrooms" transform={`translate(${getAdjustedX(340)}, 890) scale(1.15)`} />
        <use href="#mystic_mushrooms" transform={`translate(${getAdjustedX(65)}, 780) scale(-1.15, 1.15)`} />
        <use href="#mystic_mushrooms" transform={`translate(${getAdjustedX(335)}, 480) scale(1.2)`} />
        <use href="#mystic_mushrooms" transform={`translate(${getAdjustedX(65)}, 380) scale(-1.15, 1.15)`} />
        <use href="#mystic_mushrooms" transform={`translate(${getAdjustedX(330)}, 120) scale(1.15)`} />

        {/* Glowing Crystal Spires */}
        <use href="#mystic_crystal_spires" transform={`translate(${getAdjustedX(340)}, 1120) scale(-1.2, 1.2)`} />
        <use href="#mystic_crystal_spires" transform={`translate(${getAdjustedX(60)}, 1000) scale(1.2)`} />
        <use href="#mystic_crystal_spires" transform={`translate(${getAdjustedX(335)}, 600) scale(-1.25, 1.25)`} />
        <use href="#mystic_crystal_spires" transform={`translate(${getAdjustedX(65)}, 430) scale(1.2)`} />
        <use href="#mystic_crystal_spires" transform={`translate(${getAdjustedX(335)}, 480) scale(-1.2, 1.2)`} />
      </g>
    )}

    {/* Level 4 Scenery: Ijduden - Sanctuaire Ancestral */}
    {levelId === 4 && (
      <g id="ijduden_scenery">
        {/* Sacred Sanctuary Braziers */}
        <use href="#sanctuary_brazier" transform={`translate(${getAdjustedX(65)}, 1220) scale(1.15)`} />
        <use href="#sanctuary_brazier" transform={`translate(${getAdjustedX(335)}, 870) scale(-1.15, 1.15)`} />
        <use href="#sanctuary_brazier" transform={`translate(${getAdjustedX(65)}, 610) scale(1.15)`} />
        <use href="#sanctuary_brazier" transform={`translate(${getAdjustedX(335)}, 260) scale(1.2)`} />

        {/* Sacred Yaz Ancestral Monoliths */}
        <use href="#sanctuary_monolith" transform={`translate(${getAdjustedX(330)}, 1220) scale(1.1)`} />
        <use href="#sanctuary_monolith" transform={`translate(${getAdjustedX(65)}, 780) scale(1.15)`} />
        <use href="#sanctuary_monolith" transform={`translate(${getAdjustedX(335)}, 480) scale(-1.15, 1.15)`} />
        <use href="#sanctuary_monolith" transform={`translate(${getAdjustedX(65)}, 380) scale(1.15)`} />

        {/* Mystic Sanctuary Energy Crystals */}
        <use href="#sanctuary_crystals" transform={`translate(${getAdjustedX(340)}, 1120) scale(1.2)`} />
        <use href="#sanctuary_crystals" transform={`translate(${getAdjustedX(60)}, 1000) scale(-1.2, 1.2)`} />
        <use href="#sanctuary_crystals" transform={`translate(${getAdjustedX(335)}, 600) scale(1.2)`} />
        <use href="#sanctuary_crystals" transform={`translate(${getAdjustedX(65)}, 430) scale(-1.15, 1.15)`} />
        <use href="#sanctuary_crystals" transform={`translate(${getAdjustedX(330)}, 120) scale(1.25)`} />
      </g>
    )}

    {/* Voile Nocturne Ambiant sur toute la carte (Plateau, décors, chemins, maisons) lors du passage en Nuit */}
    {levelId === 4 && (
      <rect
        x="-5000"
        y="0"
        width="10000"
        height="1600"
        fill="#021424"
        className="pointer-events-none"
        style={{
          opacity: isNight ? 0.35 : 0,
          transition: 'opacity 2500ms ease-in-out'
        }}
      />
    )}
  </svg>
  );
});

/**
 * Portique d'entrée des niveaux (rendu au-dessus du joueur à zIndex élevé
 * pour que le personnage passe SOUS le portique et non sur le portique).
 */
export interface LevelEntrancePorticoOverlayProps {
  levelId: number;
  isRestored?: boolean;
  isNight?: boolean;
}

export const LevelEntrancePorticoOverlay: React.FC<LevelEntrancePorticoOverlayProps> = React.memo(({
  levelId,
  isRestored = false,
  isNight = false,
}) => {
  return (
    <g id="level_entrance_portico_overlay" transform="translate(200, 1210)">
      {/* LEVEL 0 : PORTIQUE TRADITIONNEL DE TADDART (Village Ancestral) */}
      {levelId === 0 && (
        <g id="portico_taddart">
          {/* Pilier gauche en pierre taillée */}
          <g transform="translate(-46, 0)">
            <rect x="-14" y="-4" width="28" height="24" rx="2" fill="#57534e" stroke="#292524" strokeWidth="1.5" />
            <line x1="-12" y1="8" x2="12" y2="8" stroke="#292524" strokeWidth="1.2" />
            <rect x="-11" y="-55" width="22" height="52" rx="2" fill="#78716c" stroke="#44403c" strokeWidth="1.5" />
            <line x1="-9" y1="-40" x2="9" y2="-40" stroke="#44403c" strokeWidth="1" />
            <line x1="-9" y1="-24" x2="9" y2="-24" stroke="#44403c" strokeWidth="1" />
            <line x1="-9" y1="-8" x2="9" y2="-8" stroke="#44403c" strokeWidth="1" />
            <rect x="-15" y="-62" width="30" height="9" rx="2" fill="#a8a29e" stroke="#57534e" strokeWidth="1.5" />
            {/* Lierre grimpant */}
            <path d="M -11 12 Q -7 0 -11 -20 Q -8 -35 -11 -48" stroke="#15803d" strokeWidth="2" fill="none" />
            <ellipse cx="-8" cy="-6" rx="3.5" ry="2" fill="#22c55e" />
            <ellipse cx="-8" cy="-26" rx="4" ry="2.5" fill="#16a34a" />
            {/* Lanterne traditionnelle en fer forgé suspendue */}
            <line x1="9" y1="-32" x2="17" y2="-32" stroke="#292524" strokeWidth="1.5" />
            <line x1="17" y1="-32" x2="17" y2="-24" stroke="#292524" strokeWidth="1.2" />
            <polygon points="13,-24 21,-24 19,-12 15,-12" fill="#1c1917" />
            <rect x="14.5" y="-22" width="5" height="8" rx="1" fill="#fbbf24" opacity="0.9" />
            <circle cx="17" cy="-18" r="6" fill="#f59e0b" opacity="0.45" />
          </g>

          {/* Pilier droit en pierre taillée */}
          <g transform="translate(46, 0)">
            <rect x="-14" y="-4" width="28" height="24" rx="2" fill="#57534e" stroke="#292524" strokeWidth="1.5" />
            <line x1="-12" y1="8" x2="12" y2="8" stroke="#292524" strokeWidth="1.2" />
            <rect x="-11" y="-55" width="22" height="52" rx="2" fill="#78716c" stroke="#44403c" strokeWidth="1.5" />
            <line x1="-9" y1="-40" x2="9" y2="-40" stroke="#44403c" strokeWidth="1" />
            <line x1="-9" y1="-24" x2="9" y2="-24" stroke="#44403c" strokeWidth="1" />
            <line x1="-9" y1="-8" x2="9" y2="-8" stroke="#44403c" strokeWidth="1" />
            <rect x="-15" y="-62" width="30" height="9" rx="2" fill="#a8a29e" stroke="#57534e" strokeWidth="1.5" />
            {/* Lierre grimpant */}
            <path d="M 11 12 Q 7 0 11 -20 Q 8 -35 11 -48" stroke="#15803d" strokeWidth="2" fill="none" />
            <ellipse cx="8" cy="-6" rx="3.5" ry="2" fill="#22c55e" />
            <ellipse cx="8" cy="-26" rx="4" ry="2.5" fill="#16a34a" />
            {/* Lanterne traditionnelle */}
            <line x1="-9" y1="-32" x2="-17" y2="-32" stroke="#292524" strokeWidth="1.5" />
            <line x1="-17" y1="-32" x2="-17" y2="-24" stroke="#292524" strokeWidth="1.2" />
            <polygon points="-21,-24 -13,-24 -15,-12 -19,-12" fill="#1c1917" />
            <rect x="-19.5" y="-22" width="5" height="8" rx="1" fill="#fbbf24" opacity="0.9" />
            <circle cx="-17" cy="-18" r="6" fill="#f59e0b" opacity="0.45" />
          </g>

          {/* Linteau en bois de cèdre sculpté */}
          <rect x="-62" y="-72" width="124" height="14" rx="2.5" fill="#78350f" stroke="#451a03" strokeWidth="2" />
          <rect x="-58" y="-70" width="116" height="2.5" fill="#92400e" opacity="0.8" />
          {/* Motifs géométriques berbères */}
          <path d="M -45 -65 L -40 -69 L -35 -65 L -30 -69 L -25 -65 M 25 -65 L 30 -69 L 35 -65 L 40 -69 L 45 -65" stroke="#f59e0b" strokeWidth="1.2" fill="none" />

          {/* Toiture en tuiles canal kabyles (Iqremiden) */}
          <polygon points="0,-92 -66,-72 66,-72" fill="#b45309" stroke="#78350f" strokeWidth="1.8" />
          <polygon points="0,-90 -62,-73 62,-73" fill="#c2410c" />
          <line x1="-50" y1="-73" x2="-2" y2="-89" stroke="#7c2d12" strokeWidth="1.2" />
          <line x1="-30" y1="-73" x2="-1" y2="-89" stroke="#7c2d12" strokeWidth="1.2" />
          <line x1="-10" y1="-73" x2="0" y2="-90" stroke="#7c2d12" strokeWidth="1.2" />
          <line x1="10" y1="-73" x2="0" y2="-90" stroke="#7c2d12" strokeWidth="1.2" />
          <line x1="30" y1="-73" x2="1" y2="-89" stroke="#7c2d12" strokeWidth="1.2" />
          <line x1="50" y1="-73" x2="2" y2="-89" stroke="#7c2d12" strokeWidth="1.2" />

          {/* Médaillon central Yaz ⵣ */}
          <circle cx="0" cy="-65" r="11" fill="#451a03" stroke="#f59e0b" strokeWidth="1.8" />
          <text x="0" y="-61" fill="#fef08a" fontSize="11" fontWeight="bold" textAnchor="middle">ⵣ</text>

          {/* Panneau du village */}
          <rect x="-26" y="-53" width="52" height="12" rx="3" fill="#1c1917" stroke="#b45309" strokeWidth="1" />
          <text x="0" y="-44.5" fill="#fde047" fontSize="7.5" fontWeight="900" textAnchor="middle" letterSpacing="0.8">TADDART</text>
        </g>
      )}

      {/* LEVEL 1 : PORTIQUE D'AZEMMUR (Vallée des Oliviers) */}
      {levelId === 1 && (
        <g id="portico_azemmur">
          {/* Jarres d'huile d'olive en terre cuite au pied des piliers */}
          <ellipse cx="-64" cy="12" rx="6" ry="9" fill="#c2410c" stroke="#7c2d12" strokeWidth="1" />
          <ellipse cx="-64" cy="4" rx="3" ry="1.5" fill="#9a3412" />
          <ellipse cx="64" cy="12" rx="6" ry="9" fill="#c2410c" stroke="#7c2d12" strokeWidth="1" />
          <ellipse cx="64" cy="4" rx="3" ry="1.5" fill="#9a3412" />

          {/* Pilier gauche en grès ocre */}
          <g transform="translate(-46, 0)">
            <rect x="-14" y="-4" width="28" height="24" rx="2" fill="#9a3412" stroke="#431407" strokeWidth="1.5" />
            <rect x="-11" y="-55" width="22" height="52" rx="2" fill="#c2410c" stroke="#7c2d12" strokeWidth="1.5" />
            <line x1="-9" y1="-38" x2="9" y2="-38" stroke="#7c2d12" strokeWidth="1" />
            <line x1="-9" y1="-20" x2="9" y2="-20" stroke="#7c2d12" strokeWidth="1" />
            <polygon points="0,-33 5,-29 0,-25 -5,-29" fill="#f59e0b" opacity="0.8" />
            <rect x="-15" y="-62" width="30" height="9" rx="2" fill="#ea580c" stroke="#7c2d12" strokeWidth="1.5" />
            {/* Rameau d'olivier grimpant */}
            <path d="M -11 8 Q -6 -10 -11 -30 Q -6 -45 -11 -50" stroke="#65a30d" strokeWidth="2" fill="none" />
            <ellipse cx="-7" cy="-8" rx="4" ry="2" fill="#84cc16" transform="rotate(-20, -7, -8)" />
            <ellipse cx="-7" cy="-28" rx="4" ry="2" fill="#84cc16" transform="rotate(20, -7, -28)" />
            <circle cx="-5" cy="-18" r="2.2" fill="#18181b" />
            {/* Lanterne ambrée */}
            <line x1="9" y1="-32" x2="17" y2="-32" stroke="#451a03" strokeWidth="1.5" />
            <line x1="17" y1="-32" x2="17" y2="-24" stroke="#451a03" strokeWidth="1.2" />
            <polygon points="13,-24 21,-24 19,-12 15,-12" fill="#78350f" />
            <rect x="14.5" y="-22" width="5" height="8" rx="1" fill="#f59e0b" opacity="0.9" />
            <circle cx="17" cy="-18" r="6" fill="#fbbf24" opacity="0.5" />
          </g>

          {/* Pilier droit en grès ocre */}
          <g transform="translate(46, 0)">
            <rect x="-14" y="-4" width="28" height="24" rx="2" fill="#9a3412" stroke="#431407" strokeWidth="1.5" />
            <rect x="-11" y="-55" width="22" height="52" rx="2" fill="#c2410c" stroke="#7c2d12" strokeWidth="1.5" />
            <line x1="-9" y1="-38" x2="9" y2="-38" stroke="#7c2d12" strokeWidth="1" />
            <line x1="-9" y1="-20" x2="9" y2="-20" stroke="#7c2d12" strokeWidth="1" />
            <polygon points="0,-33 5,-29 0,-25 -5,-29" fill="#f59e0b" opacity="0.8" />
            <rect x="-15" y="-62" width="30" height="9" rx="2" fill="#ea580c" stroke="#7c2d12" strokeWidth="1.5" />
            {/* Rameau d'olivier */}
            <path d="M 11 8 Q 6 -10 11 -30 Q 6 -45 11 -50" stroke="#65a30d" strokeWidth="2" fill="none" />
            <ellipse cx="7" cy="-8" rx="4" ry="2" fill="#84cc16" transform="rotate(20, 7, -8)" />
            <ellipse cx="7" cy="-28" rx="4" ry="2" fill="#84cc16" transform="rotate(-20, 7, -28)" />
            <circle cx="5" cy="-18" r="2.2" fill="#18181b" />
            {/* Lanterne ambrée */}
            <line x1="-9" y1="-32" x2="-17" y2="-32" stroke="#451a03" strokeWidth="1.5" />
            <line x1="-17" y1="-32" x2="-17" y2="-24" stroke="#451a03" strokeWidth="1.2" />
            <polygon points="-21,-24 -13,-24 -15,-12 -19,-12" fill="#78350f" />
            <rect x="-19.5" y="-22" width="5" height="8" rx="1" fill="#f59e0b" opacity="0.9" />
            <circle cx="-17" cy="-18" r="6" fill="#fbbf24" opacity="0.5" />
          </g>

          {/* Linteau en bois d'olivier poli */}
          <rect x="-62" y="-72" width="124" height="14" rx="2.5" fill="#854d0e" stroke="#451a03" strokeWidth="2" />
          <rect x="-58" y="-70" width="116" height="2.5" fill="#a16207" opacity="0.8" />
          <path d="M -48 -66 Q -30 -62 -15 -66 Q 0 -62 15 -66 Q 30 -62 48 -66" stroke="#4d7c0f" strokeWidth="2.2" fill="none" />

          {/* Toiture provençale / kabyle en tuiles ambrées */}
          <polygon points="0,-92 -66,-72 66,-72" fill="#c2410c" stroke="#7c2d12" strokeWidth="1.8" />
          <polygon points="0,-90 -62,-73 62,-73" fill="#d97706" />

          {/* Médaillon central Yaz */}
          <circle cx="0" cy="-65" r="11" fill="#451a03" stroke="#f59e0b" strokeWidth="1.8" />
          <text x="0" y="-61" fill="#fef08a" fontSize="11" fontWeight="bold" textAnchor="middle">ⵣ</text>

          {/* Panneau Azemmur */}
          <rect x="-28" y="-53" width="56" height="12" rx="3" fill="#451a03" stroke="#f59e0b" strokeWidth="1" />
          <text x="0" y="-44.5" fill="#fef08a" fontSize="7.5" fontWeight="900" textAnchor="middle" letterSpacing="0.8">AZEMMUR</text>
        </g>
      )}

      {/* LEVEL 2 : PORTIQUE DU DJURDJURA (Cimes Enneigées) */}
      {levelId === 2 && (
        <g id="portico_djurdjura">
          {/* Piles de neige au pied des piliers */}
          <ellipse cx="-46" cy="14" rx="18" ry="6" fill="#f1f5f9" opacity="0.9" />
          <ellipse cx="46" cy="14" rx="18" ry="6" fill="#f1f5f9" opacity="0.9" />

          {/* Pilier gauche en ardoise & mélèze */}
          <g transform="translate(-46, 0)">
            <rect x="-14" y="-4" width="28" height="24" rx="2" fill="#1e293b" stroke="#0f172a" strokeWidth="1.5" />
            <rect x="-11" y="-55" width="22" height="52" rx="2" fill="#334155" stroke="#1e293b" strokeWidth="1.5" />
            <line x1="-9" y1="-38" x2="9" y2="-38" stroke="#1e293b" strokeWidth="1" />
            <line x1="-9" y1="-20" x2="9" y2="-20" stroke="#1e293b" strokeWidth="1" />
            <rect x="-15" y="-62" width="30" height="9" rx="2" fill="#475569" stroke="#1e293b" strokeWidth="1.5" />
            <polygon points="-11,-55 0,-55 -4,-45 -11,-42" fill="#f8fafc" opacity="0.8" />
            {/* Lanterne tempête alpine */}
            <line x1="9" y1="-32" x2="17" y2="-32" stroke="#0f172a" strokeWidth="1.5" />
            <line x1="17" y1="-32" x2="17" y2="-24" stroke="#0f172a" strokeWidth="1.2" />
            <polygon points="13,-24 21,-24 19,-12 15,-12" fill="#0f172a" />
            <rect x="14.5" y="-22" width="5" height="8" rx="1" fill="#38bdf8" opacity="0.95" />
            <circle cx="17" cy="-18" r="6" fill="#0284c7" opacity="0.5" />
          </g>

          {/* Pilier droit */}
          <g transform="translate(46, 0)">
            <rect x="-14" y="-4" width="28" height="24" rx="2" fill="#1e293b" stroke="#0f172a" strokeWidth="1.5" />
            <rect x="-11" y="-55" width="22" height="52" rx="2" fill="#334155" stroke="#1e293b" strokeWidth="1.5" />
            <line x1="-9" y1="-38" x2="9" y2="-38" stroke="#1e293b" strokeWidth="1" />
            <line x1="-9" y1="-20" x2="9" y2="-20" stroke="#1e293b" strokeWidth="1" />
            <rect x="-15" y="-62" width="30" height="9" rx="2" fill="#475569" stroke="#1e293b" strokeWidth="1.5" />
            <polygon points="11,-55 0,-55 4,-45 11,-42" fill="#f8fafc" opacity="0.8" />
            {/* Lanterne tempête */}
            <line x1="-9" y1="-32" x2="-17" y2="-32" stroke="#0f172a" strokeWidth="1.5" />
            <line x1="-17" y1="-32" x2="-17" y2="-24" stroke="#0f172a" strokeWidth="1.2" />
            <polygon points="-21,-24 -13,-24 -15,-12 -19,-12" fill="#0f172a" />
            <rect x="-19.5" y="-22" width="5" height="8" rx="1" fill="#38bdf8" opacity="0.95" />
            <circle cx="-17" cy="-18" r="6" fill="#0284c7" opacity="0.5" />
          </g>

          {/* Poutre massive en bois sombre */}
          <rect x="-62" y="-72" width="124" height="14" rx="2.5" fill="#1e293b" stroke="#0f172a" strokeWidth="2" />
          <rect x="-58" y="-70" width="116" height="2.5" fill="#334155" opacity="0.8" />

          {/* Toit en bardeau alpin couvert de neige */}
          <polygon points="0,-94 -66,-72 66,-72" fill="#0f172a" stroke="#020617" strokeWidth="1.8" />
          {/* Manteau de neige */}
          <polygon points="0,-98 -68,-75 -60,-70 -35,-78 -20,-72 0,-85 20,-72 35,-78 60,-70 68,-75" fill="#f8fafc" />
          {/* Stalactites de glace (Icicles) */}
          <polygon points="-40,-72 -38,-64 -36,-72" fill="#e0f2fe" />
          <polygon points="-20,-72 -18,-62 -16,-72" fill="#e0f2fe" />
          <polygon points="18,-72 20,-63 22,-72" fill="#e0f2fe" />
          <polygon points="38,-72 40,-65 42,-72" fill="#e0f2fe" />

          {/* Médaillon central flocon & Yaz */}
          <circle cx="0" cy="-65" r="11" fill="#0f172a" stroke="#38bdf8" strokeWidth="1.8" />
          <text x="0" y="-61" fill="#e0f2fe" fontSize="11" fontWeight="bold" textAnchor="middle">ⵣ</text>

          {/* Panneau Djurdjura */}
          <rect x="-32" y="-53" width="64" height="12" rx="3" fill="#0f172a" stroke="#38bdf8" strokeWidth="1" />
          <text x="0" y="-44.5" fill="#38bdf8" fontSize="7.5" fontWeight="900" textAnchor="middle" letterSpacing="0.8">DJURDJURA</text>
        </g>
      )}

      {/* LEVEL 3 : PORTIQUE D'IƔIL (Forêt & Crêtes Mystiques) */}
      {levelId === 3 && (
        <g id="portico_igil">
          {/* Orbes magiques pulsantes au sol */}
          <circle cx="-62" cy="10" r="4" fill="#c084fc" opacity="0.8" />
          <circle cx="62" cy="10" r="4" fill="#22d3ee" opacity="0.8" />

          {/* Piliers mégalithiques en basalte mystique */}
          <g transform="translate(-46, 0)">
            <rect x="-14" y="-4" width="28" height="24" rx="2" fill="#1e1b4b" stroke="#3b0764" strokeWidth="1.5" />
            <rect x="-11" y="-55" width="22" height="52" rx="2" fill="#2e1065" stroke="#581c87" strokeWidth="1.5" />
            {/* Runes magiques cyan */}
            <path d="M 0 -45 L -5 -35 L 5 -35 Z M 0 -30 L 0 -15 M -4 -22 L 4 -22" stroke="#06b6d4" strokeWidth="1.5" fill="none" opacity="0.9" />
            <rect x="-15" y="-62" width="30" height="9" rx="2" fill="#3b0764" stroke="#a855f7" strokeWidth="1.5" />
            {/* Cristaux magiques */}
            <polygon points="14,-32 20,-28 17,-14 13,-18" fill="#d946ef" opacity="0.9" />
            <circle cx="16" cy="-22" r="7" fill="#c084fc" opacity="0.4" />
          </g>

          <g transform="translate(46, 0)">
            <rect x="-14" y="-4" width="28" height="24" rx="2" fill="#1e1b4b" stroke="#3b0764" strokeWidth="1.5" />
            <rect x="-11" y="-55" width="22" height="52" rx="2" fill="#2e1065" stroke="#581c87" strokeWidth="1.5" />
            {/* Runes magiques magenta */}
            <path d="M 0 -45 L 0 -15 M -5 -35 L 5 -35 M -5 -25 L 5 -25" stroke="#d946ef" strokeWidth="1.5" fill="none" opacity="0.9" />
            <rect x="-15" y="-62" width="30" height="9" rx="2" fill="#3b0764" stroke="#a855f7" strokeWidth="1.5" />
            <polygon points="-14,-32 -20,-28 -17,-14 -13,-18" fill="#06b6d4" opacity="0.9" />
            <circle cx="-16" cy="-22" r="7" fill="#22d3ee" opacity="0.4" />
          </g>

          {/* Arche runique supérieure */}
          <path d="M -62 -60 Q 0 -88 62 -60 L 56 -74 Q 0 -102 -56 -74 Z" fill="#2e1065" stroke="#a855f7" strokeWidth="1.8" />

          {/* Cristal central flottant en lévitation */}
          <polygon points="0,-106 8,-94 0,-82 -8,-94" fill="#e879f9" stroke="#f0abfc" strokeWidth="1.2" />
          <circle cx="0" cy="-94" r="10" fill="#a855f7" opacity="0.55" />

          {/* Médaillon central Yaz mystique */}
          <circle cx="0" cy="-68" r="11" fill="#1e1b4b" stroke="#c084fc" strokeWidth="1.8" />
          <text x="0" y="-64" fill="#f0abfc" fontSize="11" fontWeight="bold" textAnchor="middle">ⵣ</text>

          {/* Panneau Iɣil remonté */}
          <rect x="-24" y="-53" width="48" height="12" rx="3" fill="#1e1b4b" stroke="#c084fc" strokeWidth="1" />
          <text x="0" y="-44.5" fill="#f0abfc" fontSize="7.5" fontWeight="900" textAnchor="middle" letterSpacing="1">IƔIL</text>
        </g>
      )}

      {/* LEVEL 4 : PORTIQUE D'IJDUDEN / SANCTUAIRE */}
      {levelId === 4 && (
        <g id="portico_ijduden">
          {/* Portique corrompu par la brume */}
          <g
            id="portico_ijduden_corrupted"
            style={{
              opacity: isRestored ? 0 : 1,
              transition: 'opacity 2500ms ease-in-out',
              pointerEvents: 'none'
            }}
          >
            <ellipse cx="-46" cy="14" rx="16" ry="5" fill="#064e3b" opacity="0.7" />
            <ellipse cx="46" cy="14" rx="16" ry="5" fill="#064e3b" opacity="0.7" />

            {/* Piliers d'obsidienne fracturée */}
            <g transform="translate(-46, 0)">
              <rect x="-14" y="-4" width="28" height="24" rx="2" fill="#0f172a" stroke="#34d399" strokeWidth="1" />
              <rect x="-11" y="-55" width="22" height="52" rx="2" fill="#18181b" stroke="#34d399" strokeWidth="1.2" />
              <polyline points="-8,-45 -2,-32 -7,-18" stroke="#34d399" strokeWidth="1.5" fill="none" opacity="0.9" />
              <rect x="-15" y="-62" width="30" height="9" rx="2" fill="#09090b" stroke="#34d399" strokeWidth="1.2" />
              <polygon points="12,-28 20,-28 18,-16 14,-16" fill="#09090b" />
              <circle cx="16" cy="-22" r="5" fill="#34d399" opacity="0.8" />
            </g>

            <g transform="translate(46, 0)">
              <rect x="-14" y="-4" width="28" height="24" rx="2" fill="#0f172a" stroke="#34d399" strokeWidth="1" />
              <rect x="-11" y="-55" width="22" height="52" rx="2" fill="#18181b" stroke="#34d399" strokeWidth="1.2" />
              <polyline points="8,-45 2,-32 7,-18" stroke="#34d399" strokeWidth="1.5" fill="none" opacity="0.9" />
              <rect x="-15" y="-62" width="30" height="9" rx="2" fill="#09090b" stroke="#34d399" strokeWidth="1.2" />
              <polygon points="-20,-28 -12,-28 -14,-16 -18,-16" fill="#09090b" />
              <circle cx="-16" cy="-22" r="5" fill="#34d399" opacity="0.8" />
            </g>

            {/* Linteau corrompu */}
            <rect x="-62" y="-72" width="124" height="14" rx="2.5" fill="#09090b" stroke="#34d399" strokeWidth="1.5" />
            <polygon points="0,-92 -66,-72 66,-72" fill="#18181b" stroke="#047857" strokeWidth="1.8" />

            {/* Médaillon Yaz scellé */}
            <circle cx="0" cy="-65" r="11" fill="#09090b" stroke="#34d399" strokeWidth="1.8" />
            <text x="0" y="-61" fill="#34d399" fontSize="11" fontWeight="bold" textAnchor="middle">ⵣ</text>

            {/* Panneau */}
            <rect x="-30" y="-53" width="60" height="12" rx="3" fill="#09090b" stroke="#34d399" strokeWidth="1" />
            <text x="0" y="-44.5" fill="#6ee7b7" fontSize="7.5" fontWeight="900" textAnchor="middle" letterSpacing="0.8">IJDUDEN</text>
          </g>

          {/* Portique sanctuaire purifié et rayonnant - JOUR */}
          <g
            id="portico_ijduden_restored_day"
            style={{
              opacity: (isRestored && !isNight) ? 1 : 0,
              transition: 'opacity 2500ms ease-in-out',
              pointerEvents: 'none'
            }}
          >
            <ellipse cx="-46" cy="14" rx="16" ry="5" fill="#15803d" opacity="0.8" />
            <ellipse cx="46" cy="14" rx="16" ry="5" fill="#15803d" opacity="0.8" />

            {/* Piliers de marbre blanc et or */}
            <g transform="translate(-46, 0)">
              <rect x="-14" y="-4" width="28" height="24" rx="2" fill="#15803d" stroke="#facc15" strokeWidth="1.5" />
              <rect x="-11" y="-55" width="22" height="52" rx="2" fill="#ffffff" stroke="#facc15" strokeWidth="1.5" />
              <line x1="-9" y1="-38" x2="9" y2="-38" stroke="#facc15" strokeWidth="1.2" />
              <line x1="-9" y1="-20" x2="9" y2="-20" stroke="#facc15" strokeWidth="1.2" />
              <rect x="-15" y="-62" width="30" height="9" rx="2" fill="#fef08a" stroke="#ca8a04" strokeWidth="1.5" />
              {/* Lierre doré et jasmin */}
              <path d="M -11 8 Q -6 -10 -11 -30 Q -6 -45 -11 -50" stroke="#22c55e" strokeWidth="2" fill="none" />
              <circle cx="-7" cy="-8" r="2.5" fill="#ffffff" />
              <circle cx="-7" cy="-28" r="2.5" fill="#ffffff" />
              <polygon points="12,-28 20,-28 18,-16 14,-16" fill="#eab308" />
              <circle cx="16" cy="-22" r="6" fill="#facc15" opacity="0.9" />
            </g>

            <g transform="translate(46, 0)">
              <rect x="-14" y="-4" width="28" height="24" rx="2" fill="#15803d" stroke="#facc15" strokeWidth="1.5" />
              <rect x="-11" y="-55" width="22" height="52" rx="2" fill="#ffffff" stroke="#facc15" strokeWidth="1.5" />
              <line x1="-9" y1="-38" x2="9" y2="-38" stroke="#facc15" strokeWidth="1.2" />
              <line x1="-9" y1="-20" x2="9" y2="-20" stroke="#facc15" strokeWidth="1.2" />
              <rect x="-15" y="-62" width="30" height="9" rx="2" fill="#fef08a" stroke="#ca8a04" strokeWidth="1.5" />
              <path d="M 11 8 Q 6 -10 11 -30 Q 6 -45 11 -50" stroke="#22c55e" strokeWidth="2" fill="none" />
              <circle cx="7" cy="-8" r="2.5" fill="#ffffff" />
              <circle cx="7" cy="-28" r="2.5" fill="#ffffff" />
              <polygon points="-20,-28 -12,-28 -14,-16 -18,-16" fill="#eab308" />
              <circle cx="-16" cy="-22" r="6" fill="#facc15" opacity="0.9" />
            </g>

            {/* Linteau doré éclatant */}
            <rect x="-62" y="-72" width="124" height="14" rx="2.5" fill="#eab308" stroke="#ca8a04" strokeWidth="2" />
            <polygon points="0,-94 -66,-72 66,-72" fill="#16a34a" stroke="#ca8a04" strokeWidth="2" />

            {/* Grand Yaz ⵣ d'or triomphant */}
            <circle cx="0" cy="-65" r="12" fill="#ca8a04" stroke="#fef08a" strokeWidth="2" />
            <text x="0" y="-60.5" fill="#ffffff" fontSize="12" fontWeight="black" textAnchor="middle">ⵣ</text>

            {/* Panneau Sanctuaire */}
            <rect x="-36" y="-53" width="72" height="12" rx="3" fill="#15803d" stroke="#fef08a" strokeWidth="1" />
            <text x="0" y="-44.5" fill="#fef08a" fontSize="7" fontWeight="900" textAnchor="middle" letterSpacing="0.6">LǦEMƐA N TIZI</text>
          </g>

          {/* Portique sanctuaire sous la lune de nuit - NUIT ÉPILOGUE */}
          <g
            id="portico_ijduden_restored_night"
            style={{
              opacity: (isRestored && isNight) ? 1 : 0,
              transition: 'opacity 2500ms ease-in-out',
              pointerEvents: 'none'
            }}
          >
            <ellipse cx="-46" cy="14" rx="16" ry="5" fill="#064e3b" opacity="0.8" />
            <ellipse cx="46" cy="14" rx="16" ry="5" fill="#064e3b" opacity="0.8" />

            {/* Piliers de marbre émeraude nocturne et or */}
            <g transform="translate(-46, 0)">
              <rect x="-14" y="-4" width="28" height="24" rx="2" fill="#064e3b" stroke="#eab308" strokeWidth="1.5" />
              <rect x="-11" y="-55" width="22" height="52" rx="2" fill="#022c22" stroke="#eab308" strokeWidth="1.5" />
              <line x1="-9" y1="-38" x2="9" y2="-38" stroke="#ca8a04" strokeWidth="1.2" />
              <line x1="-9" y1="-20" x2="9" y2="-20" stroke="#ca8a04" strokeWidth="1.2" />
              <rect x="-15" y="-62" width="30" height="9" rx="2" fill="#ca8a04" stroke="#a16207" strokeWidth="1.5" />
              {/* Lierre nocturne et jasmin luminescent */}
              <path d="M -11 8 Q -6 -10 -11 -30 Q -6 -45 -11 -50" stroke="#059669" strokeWidth="2" fill="none" />
              <circle cx="-7" cy="-8" r="2.5" fill="#6ee7b7" />
              <circle cx="-7" cy="-28" r="2.5" fill="#6ee7b7" />
              <polygon points="12,-28 20,-28 18,-16 14,-16" fill="#ca8a04" />
              <circle cx="16" cy="-22" r="6" fill="#facc15" opacity="0.85" />
            </g>

            <g transform="translate(46, 0)">
              <rect x="-14" y="-4" width="28" height="24" rx="2" fill="#064e3b" stroke="#eab308" strokeWidth="1.5" />
              <rect x="-11" y="-55" width="22" height="52" rx="2" fill="#022c22" stroke="#eab308" strokeWidth="1.5" />
              <line x1="-9" y1="-38" x2="9" y2="-38" stroke="#ca8a04" strokeWidth="1.2" />
              <line x1="-9" y1="-20" x2="9" y2="-20" stroke="#ca8a04" strokeWidth="1.2" />
              <rect x="-15" y="-62" width="30" height="9" rx="2" fill="#ca8a04" stroke="#a16207" strokeWidth="1.5" />
              <path d="M 11 8 Q 6 -10 11 -30 Q 6 -45 11 -50" stroke="#059669" strokeWidth="2" fill="none" />
              <circle cx="7" cy="-8" r="2.5" fill="#6ee7b7" />
              <circle cx="7" cy="-28" r="2.5" fill="#6ee7b7" />
              <polygon points="-20,-28 -12,-28 -14,-16 -18,-16" fill="#ca8a04" />
              <circle cx="-16" cy="-22" r="6" fill="#facc15" opacity="0.85" />
            </g>

            {/* Linteau doré éclatant sous la lune */}
            <rect x="-62" y="-72" width="124" height="14" rx="2.5" fill="#ca8a04" stroke="#a16207" strokeWidth="2" />
            <polygon points="0,-94 -66,-72 66,-72" fill="#047857" stroke="#ca8a04" strokeWidth="2" />

            {/* Grand Yaz ⵣ d'or triomphant luminescent */}
            <circle cx="0" cy="-65" r="12" fill="#854d0e" stroke="#fef08a" strokeWidth="2" />
            <text x="0" y="-60.5" fill="#fef08a" fontSize="12" fontWeight="black" textAnchor="middle">ⵣ</text>

            {/* Panneau Sanctuaire */}
            <rect x="-36" y="-53" width="72" height="12" rx="3" fill="#022c22" stroke="#fef08a" strokeWidth="1" />
            <text x="0" y="-44.5" fill="#fef08a" fontSize="7" fontWeight="900" textAnchor="middle" letterSpacing="0.6">LǦEMƐA N TIZI</text>
          </g>
        </g>
      )}
    </g>
  );
});
